import { ChangeEvent, CSSProperties, DragEvent as ReactDragEvent, FormEvent, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';

import { formatDisplayPhoneNumber } from '../../services/phone.utils';
import { buildApiUrl, isErrorResponse, readJson } from '../../api/api.utils';
import { ApiMutationSuccess, AuthorizedJsonRequest, ErrorResponse } from '../../api/common.types';
import { loginOperator, logoutOperator, requestSessionRefresh } from '../../api/login/api';
import { AuthSuccessResponse, OperatorProfile } from '../../api/login/api.types';
import {
  applyChatCourseProgram,
  applyChatPhone,
  buildChatFileContentUrl,
  buildIncomingMessageEventSourceUrl,
  createChatReminder,
  deleteChatMessage,
  dismissChatReminder,
  fetchChatFileBlob,
  fetchChatFilePreview,
  getChatCoursePrograms,
  getChatCourses,
  getAiAutoresponderSettings,
  getChatCourseAttachmentFiles,
  getCoursePrograms,
  grantChatDemoAccess,
  getOperatorChatHistory,
  getOperatorChatMessages,
  getOperatorChats,
  getWidgetChatMessages,
  getWidgetChats,
  getWidgetCourseAttachmentFiles,
  linkWidgetChat,
  markChatMessageAsRead,
  markWidgetMessageAsRead,
  fetchChatCourseAttachmentFileBlob,
  fetchWidgetCourseAttachmentFileBlob,
  sendChatMessage,
  sendWidgetMessage,
  updateChatAiAutoresponderSettings,
  updateChatReminder,
  updateAiAutoresponderSettings
} from '../../api/main/api';
import {
  AiAutoresponderSettings,
  ChatCourseAttachmentFile,
  ChatCourseCatalogCourse,
  ChatCourseProgramApplyResult,
  ChatCourseProgramOption,
  ChatPhoneApplicationResult,
  ChatDirection,
  ChatMessageType,
  ChatPlatform,
  FileDocumentPreview,
  IncomingMessageStreamEvent,
  LinkedWidgetChatLinkSummary,
  OperatorChatHistoryEntry,
  OperatorChatMessage,
  OperatorChatMessageFile,
  OperatorChatSummary,
  WidgetChatLinkedSummary,
  WidgetChatSummary
} from '../../api/main/api.types';
import '../../styles/main.scss';
import { ChatsSection } from '../../components/smart/chats/ui';
import { AiReplyAssistant } from '../../components/smart/ai-reply/ui';
import { LmsSection } from '../../components/smart/lms/ui';
import { countPendingMissedUisGroups, formatCallCardPhoneNumber, formatUisCallCount, buildUisCallGroups, UisCallDetailsPanel, UisCallFilter, UisCallGroup, UisFilterOption, UisSection, useUisOverviewData } from '../../components/smart/uis/ui';
import { WdsSection } from '../../components/smart/wds/ui';
import ModalConfirm from '../../components/primitive/modal.confirm';
import ModalPreview from '../../components/primitive/modal.preview';
import Notification, { NotificationItem, NotificationTone } from '../../components/ui/notification';
import { ArrowLeftIcon, AttachmentIcon, BellIcon, CloseIcon, DownloadIcon, IconArrowDown, IconCalendar, IconDelete, IconDoneAll, IconExitApp, IconFilter, IconQuestion, IconSmsTracking, MenuIcon, PauseIcon, PhoneCallIcon, PlayIcon, SearchIcon, SparkIcon, TelegramSendIcon } from '../../components/ui/icons';
import { AppTheme, useTheme } from '../../hooks/useTheme';
import { LoginContainer } from '../login';
import widgetChatAvatarUrl from '../../styles/images/355.png';

interface LoginFormState {
  login: string;
  password: string;
}

interface ReminderFormState {
  remindAt: string;
  comment: string;
}

interface ToastConfig {
  tone: NotificationTone;
  title: string;
  message: string;
  durationMs?: number;
}

type AuthViewState = 'booting' | 'guest' | 'authenticated';
type SidebarSectionId = 'chats' | 'lms' | 'wds' | 'uis';
type DemoAccessDialogMode = 'confirm' | 'requirements';

interface ChatThreadSummary extends OperatorChatSummary {
  key: string;
  chats: OperatorChatSummary[];
  chatIds: number[];
  hasTelegramChat: boolean;
  hasMaxChat: boolean;
}

type QuickReplyChat = Pick<OperatorChatSummary, 'title' | 'courseName' | 'courseTitle' | 'courseProgramId' | 'programName' | 'volumeHours' | 'startEducationDate'>;

interface QuickReplyDefinition {
  id: string;
  title: string;
  buildMessage: (chat: QuickReplyChat, context: QuickReplyBuildContext) => string | null;
}

interface QuickReplyBuildContext {
  coursePrograms: ChatCourseProgramOption[];
  courseProgramsError: string;
  courseProgramsLoading: boolean;
}

interface ReleaseAssetSnapshot {
  mainScriptPath: string | null;
  mainStylePath: string | null;
}

interface ReleaseManifestPayload {
  files?: Record<string, unknown>;
}

interface ReleaseMetadataPayload {
  buildId?: string;
  builtAt?: string;
}

interface ReleaseMetadataSnapshot {
  buildId: string | null;
  builtAt: string | null;
}

type PhoneCountryCode = 'RU' | 'BY' | 'KZ';

interface PhoneCountryConfig {
  code: PhoneCountryCode;
  label: string;
  iconSrc: string;
  dialCode: string;
  localDigits: number;
  placeholder: string;
}

const PHONE_COUNTRY_MAP: Record<PhoneCountryCode, PhoneCountryConfig> = {
  RU: {
    code: 'RU',
    label: 'Россия',
    iconSrc: '/images/ru.svg',
    dialCode: '7',
    localDigits: 10,
    placeholder: '999 123 45 67'
  },
  BY: {
    code: 'BY',
    label: 'Беларусь',
    iconSrc: '/images/by.svg',
    dialCode: '375',
    localDigits: 9,
    placeholder: '29 123 45 67'
  },
  KZ: {
    code: 'KZ',
    label: 'Казахстан',
    iconSrc: '/images/kz.svg',
    dialCode: '7',
    localDigits: 10,
    placeholder: '700 123 45 67'
  }
};

const PHONE_COUNTRY_LIST = Object.values(PHONE_COUNTRY_MAP);
const DEFAULT_CHAT_PHONE_COUNTRY: PhoneCountryCode = 'RU';

interface WidgetPageInfo {
  currentPageUrl: string | null;
  currentPagePath: string | null;
  currentPageTitle: string | null;
}

interface DisplayedChatMessage extends OperatorChatMessage {
  sourceMessages: OperatorChatMessage[];
  groupedMessageIds: number[];
  renderTextBelowAttachments: boolean;
}

interface AudioPlaybackState {
  currentTimeSeconds: number;
  durationSeconds: number | null;
  isPlaying: boolean;
}

type AvatarLoadAttempt = 0 | 1 | 2;

type IncomingMessageAlertVariant = 'default' | 'whistle';

interface FilePreviewModalState {
  file: OperatorChatMessageFile;
  objectUrl: string | null;
  sourceBlob: Blob | null;
  documentPreview: FileDocumentPreview | null;
  isLoading: boolean;
  errorMessage: string | null;
}

interface AvatarPreviewState {
  title: string;
  imageUrl: string;
}

function DocxPreviewRenderer({ blob }: { blob: Blob }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    let isDisposed = false;
    setRenderError(null);
    container.innerHTML = '';

    const renderDocument = async () => {
      try {
        await renderAsync(blob, container, container, {
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          renderComments: false,
          renderAltChunks: true,
          useBase64URL: true
        });

        if (isDisposed) {
          container.innerHTML = '';
        }
      } catch (error) {
        console.error(error);

        if (!isDisposed) {
          container.innerHTML = '';
          setRenderError('Не удалось отрисовать документ Word в модальном окне.');
        }
      }
    };

    void renderDocument();

    return () => {
      isDisposed = true;
      container.innerHTML = '';
    };
  }, [blob]);

  if (renderError) {
    return (
      <div className="telegram-file-preview-dialog__placeholder telegram-file-preview-dialog__placeholder--error">
        <strong>Не удалось открыть документ Word</strong>
        <p>{renderError}</p>
      </div>
    );
  }

  return <div ref={containerRef} className="telegram-file-preview-dialog__docx-root" />;
}

const serviceIconUrl = `${process.env.PUBLIC_URL}/service-icon.svg`;
const telegramWallpaperUrl = `${process.env.PUBLIC_URL}/telegram-wallpaper.svg`;
const ACTIVE_SIDEBAR_SECTION_STORAGE_KEY = 'yaskill_msg.activeSidebarSection';
const SIDEBAR_SECTION_QUERY_PARAM = 'section';
const WDS_CHAT_ID_QUERY_PARAM = 'wdsChatId';
const UIS_GROUP_ID_QUERY_PARAM = 'uisGroupId';
const ACCESS_TOKEN_REFRESH_LEAD_MS = 60_000;
const ACCESS_TOKEN_RECHECK_THRESHOLD_MS = 90_000;
const CHAT_LIST_POLL_INTERVAL_MS = 2_500;
const CHAT_MESSAGES_POLL_INTERVAL_MS = 2_000;
const AI_AUTORESPONDER_SETTINGS_POLL_INTERVAL_MS = 10_000;
const incomingMessageAlertAudioUrl = `${process.env.PUBLIC_URL}/notify_01.mp3`;
const systemNotificationIconUrl = `${process.env.PUBLIC_URL}/service-icon.svg`;
const RELEASE_CHECK_POLL_INTERVAL_MS = 60_000;
const RELEASE_UPDATE_REMINDER_DELAY_MS = 5 * 60_000;
const SYSTEM_NOTIFICATION_MIN_INTERVAL_MS = 900;
const COMPOSER_MAX_FILES = 10;
const COMPOSER_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const SIDEBAR_SECTIONS: Array<{ id: SidebarSectionId; label: string }> = [
  { id: 'chats', label: 'ЧАТЫ' },
  { id: 'lms', label: 'LMS' },
  { id: 'wds', label: 'WDS' },
  { id: 'uis', label: 'UIS' }
];
const SIDEBAR_SECTION_PLACEHOLDER_TEXT: Record<Exclude<SidebarSectionId, 'chats'>, string> = {
  lms: 'Список LMS пока пуст.',
  wds: 'Список WDS пока пуст.',
  uis: 'Здесь будет собираться вся информация из сервиса UIS.'
};
const initialLoginFormState: LoginFormState = {
  login: '',
  password: ''
};

const initialReminderFormState: ReminderFormState = {
  remindAt: '',
  comment: ''
};

const QUICK_REPLY_IGNORED_TITLE_TOKENS = new Set([
  'telegram',
  'max',
  'wds',
  'widget',
  'chat',
  'чат',
  'клиент',
  'посетитель',
  'сайта',
  'сайт'
]);

const QUICK_REPLY_DEFINITIONS: QuickReplyDefinition[] = [
  {
    id: 'greeting-brief-info',
    title: '1. Приветствие + краткое инфо.',
    buildMessage: buildGreetingBriefInfoQuickReplyMessage
  },
  {
    id: 'client-documents',
    title: '2. Какие доки от клиента.',
    buildMessage: buildClientDocumentsQuickReplyMessage
  },
  {
    id: 'certificate',
    title: '3. Свидетельство.',
    buildMessage: buildCertificateQuickReplyMessage
  },
  {
    id: 'program-details-expanded',
    title: '4. Развёрнуто по программе.',
    buildMessage: buildExpandedProgramQuickReplyMessage
  }
];

const DEFAULT_QUICK_REPLY_ID = QUICK_REPLY_DEFINITIONS[0]?.id ?? '';

const fetchAuthorizedJsonUnavailable: AuthorizedJsonRequest = async <T,>(): Promise<T> => {
  throw new Error('API client is not ready.');
};

/** Возвращает фон главной сцены под выбранную тему, не ломая существующий wallpaper. */
function buildStageBackground(theme: AppTheme): CSSProperties {
  if (theme === 'light') {
    return {
      backgroundImage: [
        'linear-gradient(180deg, rgba(243, 247, 252, 0.92) 0%, rgba(229, 236, 244, 0.76) 100%)',
        'radial-gradient(circle at top left, rgba(94, 109, 255, 0.14), transparent 28%)',
        'radial-gradient(circle at bottom right, rgba(86, 170, 147, 0.12), transparent 34%)',
        `url(${telegramWallpaperUrl})`
      ].join(', ')
    };
  }

  return {
    backgroundImage: [
      'linear-gradient(180deg, rgba(9, 10, 15, 0.92) 0%, rgba(9, 10, 15, 0.72) 100%)',
      'radial-gradient(circle at top left, rgba(82, 51, 135, 0.18), transparent 28%)',
      'radial-gradient(circle at bottom right, rgba(168, 121, 47, 0.14), transparent 34%)',
      `url(${telegramWallpaperUrl})`
    ].join(', ')
  };
}

function buildPublicAssetUrl(assetPath: string): string {
  if (typeof window === 'undefined') {
    return assetPath;
  }

  const normalizedAssetPath = assetPath.replace(/^\/+/, '');
  const publicUrl = process.env.PUBLIC_URL?.trim() ?? '';
  const baseUrl = publicUrl && publicUrl !== '.'
    ? new URL(publicUrl.endsWith('/') ? publicUrl : `${publicUrl}/`, window.location.origin)
    : new URL('/', window.location.origin);

  return new URL(normalizedAssetPath, baseUrl).toString();
}

function buildReleaseProbeUrl(assetPath: string, probeToken: string): string {
  const assetUrl = new URL(buildPublicAssetUrl(assetPath), typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
  assetUrl.searchParams.set('releaseProbe', probeToken);
  return assetUrl.toString();
}

function buildReleaseApiProbeUrl(apiPath: string, probeToken: string): string {
  const apiUrl = new URL(buildApiUrl(apiPath), typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
  apiUrl.searchParams.set('releaseProbe', probeToken);
  return apiUrl.toString();
}

function normalizeReleaseMetadataValue(value: string | null | undefined): string | null {
  const normalizedValue = value?.trim() ?? '';
  return normalizedValue ? normalizedValue : null;
}

function getCurrentReleaseMetadataSnapshot(): ReleaseMetadataSnapshot {
  return {
    buildId: normalizeReleaseMetadataValue(process.env.REACT_APP_RELEASE_BUILD_ID),
    builtAt: normalizeReleaseMetadataValue(process.env.REACT_APP_RELEASE_BUILT_AT)
  };
}

function normalizeReleaseAssetPath(value: string | null): string | null {
  if (!value) {
    return null;
  }

  try {
    const normalizedUrl = new URL(value, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
    return normalizedUrl.pathname;
  } catch {
    return value.startsWith('/') ? value : `/${value}`;
  }
}

function findCurrentReleaseAssetPath(selector: string, attributeName: 'src' | 'href', assetPattern: RegExp): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const matchingElement = Array.from(document.querySelectorAll(selector))
    .find((element) => {
      const candidateValue = element.getAttribute(attributeName);
      const normalizedPath = normalizeReleaseAssetPath(candidateValue);

      return normalizedPath ? assetPattern.test(normalizedPath) : false;
    });

  return normalizeReleaseAssetPath(matchingElement?.getAttribute(attributeName) ?? null);
}

function getCurrentReleaseAssetSnapshot(): ReleaseAssetSnapshot {
  return {
    mainScriptPath: findCurrentReleaseAssetPath('script[src]', 'src', /\/static\/js\/main\.[^/]+\.js$/i),
    mainStylePath: findCurrentReleaseAssetPath('link[rel="stylesheet"][href]', 'href', /\/static\/css\/main\.[^/]+\.css$/i)
  };
}

function getReleaseAssetSnapshotFromManifest(manifestPayload: ReleaseManifestPayload): ReleaseAssetSnapshot {
  const manifestRecord = getRecord(manifestPayload);
  const filesRecord = getRecord(manifestRecord ? manifestRecord.files : null);

  return {
    mainScriptPath: normalizeReleaseAssetPath(getRecordString(filesRecord, 'main.js')),
    mainStylePath: normalizeReleaseAssetPath(getRecordString(filesRecord, 'main.css'))
  };
}

function getReleaseMetadataSnapshot(payload: ReleaseMetadataPayload): ReleaseMetadataSnapshot {
  const releaseMetadataRecord = getRecord(payload);

  return {
    buildId: normalizeReleaseMetadataValue(getRecordString(releaseMetadataRecord, 'buildId')),
    builtAt: normalizeReleaseMetadataValue(getRecordString(releaseMetadataRecord, 'builtAt'))
  };
}

function extractReleaseAssetPathFromHtml(html: string, assetPattern: RegExp): string | null {
  const match = html.match(assetPattern);
  return normalizeReleaseAssetPath(match?.[1] ?? null);
}

function getReleaseAssetSnapshotFromHtml(html: string): ReleaseAssetSnapshot {
  return {
    mainScriptPath: extractReleaseAssetPathFromHtml(
      html,
      /<script[^>]+src=["']([^"']*\/static\/js\/main\.[^"']+\.js(?:\?[^"']*)?)["']/i
    ),
    mainStylePath: extractReleaseAssetPathFromHtml(
      html,
      /<link[^>]+href=["']([^"']*\/static\/css\/main\.[^"']+\.css(?:\?[^"']*)?)["'][^>]*>/i
    )
  };
}

function hasNewReleaseAssets(currentSnapshot: ReleaseAssetSnapshot, latestSnapshot: ReleaseAssetSnapshot): boolean {
  const latestMainScriptPath = latestSnapshot.mainScriptPath;
  const latestMainStylePath = latestSnapshot.mainStylePath;

  const isMainScriptChanged = Boolean(
    latestMainScriptPath
    && currentSnapshot.mainScriptPath
    && latestMainScriptPath !== currentSnapshot.mainScriptPath
  );
  const isMainStyleChanged = Boolean(
    latestMainStylePath
    && currentSnapshot.mainStylePath
    && latestMainStylePath !== currentSnapshot.mainStylePath
  );

  return isMainScriptChanged || isMainStyleChanged;
}

function hasNewReleaseMetadata(currentSnapshot: ReleaseMetadataSnapshot, latestSnapshot: ReleaseMetadataSnapshot): boolean {
  const isBuildIdChanged = Boolean(
    latestSnapshot.buildId
    && currentSnapshot.buildId
    && latestSnapshot.buildId !== currentSnapshot.buildId
  );
  const isBuildTimeChanged = Boolean(
    !latestSnapshot.buildId
    && latestSnapshot.builtAt
    && currentSnapshot.builtAt
    && latestSnapshot.builtAt !== currentSnapshot.builtAt
  );

  return isBuildIdChanged || isBuildTimeChanged;
}

function getRequestedChatIdFromLocation(): number | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const chatIdParam = new URLSearchParams(window.location.search).get('chatId');

  if (!chatIdParam) {
    return null;
  }

  const parsedChatId = Number.parseInt(chatIdParam, 10);
  return Number.isSafeInteger(parsedChatId) && parsedChatId > 0 ? parsedChatId : null;
}

function parseSidebarSectionId(value: string | null): SidebarSectionId | null {
  return value === 'chats' || value === 'lms' || value === 'wds' || value === 'uis'
    ? value
    : null;
}

function getRequestedSidebarSectionFromLocation(): SidebarSectionId | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const sectionParam = new URLSearchParams(window.location.search).get(SIDEBAR_SECTION_QUERY_PARAM);
  return parseSidebarSectionId(sectionParam);
}

function getRequestedWidgetChatIdFromLocation(): number | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const wdsChatIdParam = new URLSearchParams(window.location.search).get(WDS_CHAT_ID_QUERY_PARAM);

  if (!wdsChatIdParam) {
    return null;
  }

  const parsedChatId = Number.parseInt(wdsChatIdParam, 10);
  return Number.isSafeInteger(parsedChatId) && parsedChatId > 0 ? parsedChatId : null;
}

function getRequestedUisGroupIdFromLocation(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const uisGroupIdParam = new URLSearchParams(window.location.search).get(UIS_GROUP_ID_QUERY_PARAM);
  return uisGroupIdParam?.trim() || null;
}

function getStoredSidebarSection(): SidebarSectionId {
  if (typeof window === 'undefined') {
    return 'chats';
  }

  const requestedSidebarSection = getRequestedSidebarSectionFromLocation();

  if (requestedSidebarSection !== null) {
    return requestedSidebarSection;
  }

  if (getRequestedChatIdFromLocation() !== null) {
    return 'chats';
  }

  try {
    const storedSection = parseSidebarSectionId(window.sessionStorage.getItem(ACTIVE_SIDEBAR_SECTION_STORAGE_KEY));

    return storedSection !== null
      ? storedSection
      : 'chats';
  } catch {
    return 'chats';
  }
}

function persistSidebarSection(sectionId: SidebarSectionId): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(ACTIVE_SIDEBAR_SECTION_STORAGE_KEY, sectionId);
  } catch {
    // Ignore storage failures and keep the UI functional.
  }
}

function commitLocation(nextUrl: URL): void {
  nextUrl.searchParams.sort();

  const nextHref = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;

  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.sort();
  const currentHref = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;

  if (nextHref !== currentHref) {
    window.history.replaceState(window.history.state, '', nextHref);
  }
}

function syncChatIdInLocation(chatId: number | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  const nextUrl = new URL(window.location.href);

  if (chatId === null) {
    nextUrl.searchParams.delete('chatId');
  } else {
    nextUrl.searchParams.set('chatId', String(chatId));
  }

  commitLocation(nextUrl);
}

function syncSidebarSectionInLocation(sectionId: SidebarSectionId): void {
  if (typeof window === 'undefined') {
    return;
  }

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set(SIDEBAR_SECTION_QUERY_PARAM, sectionId);
  commitLocation(nextUrl);
}

function syncWidgetChatIdInLocation(wdsChatId: number | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  const nextUrl = new URL(window.location.href);

  if (wdsChatId === null) {
    nextUrl.searchParams.delete(WDS_CHAT_ID_QUERY_PARAM);
  } else {
    nextUrl.searchParams.set(WDS_CHAT_ID_QUERY_PARAM, String(wdsChatId));
  }

  commitLocation(nextUrl);
}

function syncUisGroupIdInLocation(uisGroupId: string | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  const nextUrl = new URL(window.location.href);

  if (!uisGroupId) {
    nextUrl.searchParams.delete(UIS_GROUP_ID_QUERY_PARAM);
  } else {
    nextUrl.searchParams.set(UIS_GROUP_ID_QUERY_PARAM, uisGroupId);
  }

  commitLocation(nextUrl);
}

type LocationParamsPatch = {
  section?: SidebarSectionId | null;
  chatId?: number | null;
  wdsChatId?: number | null;
  uisGroupId?: string | null;
};

function applyLocationParams(patch: LocationParamsPatch): void {
  if (typeof window === 'undefined') {
    return;
  }

  const nextUrl = new URL(window.location.href);

  if (patch.section !== undefined) {
    if (patch.section === null) {
      nextUrl.searchParams.delete(SIDEBAR_SECTION_QUERY_PARAM);
    } else {
      nextUrl.searchParams.set(SIDEBAR_SECTION_QUERY_PARAM, patch.section);
    }
  }

  if (patch.chatId !== undefined) {
    if (patch.chatId === null) {
      nextUrl.searchParams.delete('chatId');
    } else {
      nextUrl.searchParams.set('chatId', String(patch.chatId));
    }
  }

  if (patch.wdsChatId !== undefined) {
    if (patch.wdsChatId === null) {
      nextUrl.searchParams.delete(WDS_CHAT_ID_QUERY_PARAM);
    } else {
      nextUrl.searchParams.set(WDS_CHAT_ID_QUERY_PARAM, String(patch.wdsChatId));
    }
  }

  if (patch.uisGroupId !== undefined) {
    if (patch.uisGroupId === null) {
      nextUrl.searchParams.delete(UIS_GROUP_ID_QUERY_PARAM);
    } else {
      nextUrl.searchParams.set(UIS_GROUP_ID_QUERY_PARAM, patch.uisGroupId);
    }
  }

  commitLocation(nextUrl);
}

function toErrorMessage(error: unknown, fallbackMessage: string): string {
  return error instanceof Error ? error.message : fallbackMessage;
}

function toDisplayErrorMessage(error: unknown, fallbackMessage: string): string {
  const message = toErrorMessage(error, fallbackMessage);
  return message === 'Failed to fetch' ? fallbackMessage : message;
}

function getOperatorInitials(operator: OperatorProfile): string {
  const first = operator.firstName?.trim().charAt(0) ?? '';
  const last = operator.lastName?.trim().charAt(0) ?? '';
  const initials = `${last}${first}`.trim();

  return initials || operator.loginName.slice(0, 2).toUpperCase();
}

function getTitleInitials(title: string): string {
  const parts = title
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    return '??';
  }

  const first = parts[0]?.charAt(0) ?? '';
  const second = parts[1]?.charAt(0) ?? parts[0]?.charAt(1) ?? '';
  return `${first}${second}`.toUpperCase();
}

function buildAvatarStateKey(identityKey: string, avatarUrl: string): string {
  return `${identityKey}::${avatarUrl}`;
}

function buildAvatarRetryUrl(avatarUrl: string, attempt: number): string {
  const separator = avatarUrl.includes('?') ? '&' : '?';
  return `${avatarUrl}${separator}avatarRetry=${attempt}`;
}

function resolveAvatarImageSrc(
  identityKey: string,
  avatarUrl: string | null,
  avatarLoadAttempts: Record<string, AvatarLoadAttempt>
): string | null {
  if (!avatarUrl) {
    return null;
  }

  const avatarStateKey = buildAvatarStateKey(identityKey, avatarUrl);
  const avatarLoadAttempt = avatarLoadAttempts[avatarStateKey] ?? 0;

  if (avatarLoadAttempt >= 2) {
    return null;
  }

  return avatarLoadAttempt === 1
    ? buildAvatarRetryUrl(avatarUrl, avatarLoadAttempt)
    : avatarUrl;
}

function extractClientGreetingName(title: string): string | null {
  const words = title.match(/[A-Za-zА-Яа-яЁё][A-Za-zА-Яа-яЁё-]*/g);

  if (!words) {
    return null;
  }

  return words.find((word) => !QUICK_REPLY_IGNORED_TITLE_TOKENS.has(word.toLowerCase())) ?? null;
}

function formatQuickReplyProgramLabel(programName: string | null): string {
  const normalizedProgramName = programName?.trim().toLowerCase() ?? '';

  if (!normalizedProgramName) {
    return 'обучение';
  }

  if (normalizedProgramName.includes('переподготов')) {
    return 'проф.переподготовку';
  }

  if (normalizedProgramName.includes('повыш') && normalizedProgramName.includes('квалификац')) {
    return 'повышение квалификации';
  }

  return `${normalizedProgramName.charAt(0)}${normalizedProgramName.slice(1)}`;
}

function buildQuickReplyCourseSummaryLabel(chat: QuickReplyChat): string | null {
  const courseLabel = chat.courseName?.trim() || chat.courseTitle?.trim() || null;

  if (!courseLabel) {
    return null;
  }

  return chat.volumeHours === null
    ? courseLabel
    : `${courseLabel} ${chat.volumeHours} часов`;
}

function buildGreetingBriefInfoQuickReplyMessage(chat: QuickReplyChat): string {
  const clientName = extractClientGreetingName(chat.title);
  const courseLabel = chat.courseName?.trim() || chat.courseTitle?.trim() || null;
  const greetingLine = clientName
    ? `Здравствуйте, ${clientName}! Это, менеджер учебного центра YaSkill.`
    : 'Здравствуйте! Это, менеджер учебного центра YaSkill.';
  const applicationLine = courseLabel
    ? `Вижу вашу заявку на ${formatQuickReplyProgramLabel(chat.programName)} по программе «${courseLabel}» — спасибо за доверие!`
    : null;

  return [
    greetingLine,
    ...(applicationLine ? [applicationLine] : []),
    'У нас обучение полностью дистанционное:',
    '🔹Все материалы, задания и связь с преподавателями — в личном кабинете.',
    '🔹Учитесь в удобном темпе, из любого места;',
    '🔹Преподаватели и кураторы YaSkill на связи для поддержки;',
    'Диплом выдаётся после успешного завершения — в электронном виде + оригинал по почте.',
    'Остались вопросы? С радостью расскажем подробнее!',
    '',
    'Подскажите, удобно ли сейчас кратко обсудить детали? Или напишите вопросы текстом — отвечу максимально подробно!'
  ].join('\n');
}

function buildNextActionsQuickReplyMessage(_chat: QuickReplyChat): string {
  return [
    '📌 Начать обучение просто: Высшее или среднее проф. образование — единственное требование.',
    '🎓 По завершении курса Вы получите бессрочный диплом, который действует на всей территории РФ и заносится в Государственную систему ФИС ФРДО.',
    '📦 Диплом отправляем почтой России с возможностью отслеживания.',
    '',
    '📥 Для оформления договора требуется минимум документов:',
    '🔹 Сканы паспорта',
    '🔹 СНИЛС',
    '🔹 Скан диплома (без приложения)',
    '🔹 Реквизиты организации (при оплате от юр. лица)',
    '🔹 Контактный телефон',
    '🔹 Название курса и количество часов обучения',
    '❗️Документы можно прислать в виде фото/сканов'
  ].join('\n');
}

function buildCertificateQuickReplyMessage(chat: QuickReplyChat): string {
  const courseSummaryLabel = buildQuickReplyCourseSummaryLabel(chat);
  const trainingLine = courseSummaryLabel
    ? `Вы можете пройти у нас обучение по программе «${courseSummaryLabel}» — знания и навыки будут доступны сразу.`
    : 'Вы можете пройти у нас обучение — знания и навыки будут доступны сразу.';

  return [
    trainingLine,
    '📌 Важный момент: диплом о профессиональной переподготовке выдаётся при наличии высшего или среднего профессионального образования. Поскольку у вас сейчас окончание школы, после завершения курса мы выдадим:',
    '• Свидетельство установленного образца',
    '• Справку об обучении.',
    '💡 Эти документы подтвердят ваши знания и подойдут для портфолио, работы с частными клиентами или дальнейшего развития. А когда вы получите среднее профессиональное или высшее образование — мы сможем выдать диплом о профессиональной переподготовке и внести запись в реестр ФИС ФРДО.',
    'Если такой вариант вам подходит — с радостью помогу с оформлением!'
  ].join('\n');
}

function buildClientDocumentsQuickReplyMessage(_chat: QuickReplyChat): string {
  return [
    '📌 Начать обучение просто: Высшее или среднее проф. образование — единственное требование.',
    '🎓 По завершении курса Вы получите бессрочный диплом, который действует на всей территории РФ и заносится в Государственную систему ФИС ФРДО.',
    '📦 Диплом отправляем почтой России с возможностью отслеживания.',
    '',
    '📥 Для оформления договора требуется минимум документов:',
    '🔹 Сканы паспорта;',
    '🔹 СНИЛС;',
    '🔹 Скан диплома (без приложения);',
    '🔹 Реквизиты организации (при оплате от юр. лица);',
    '🔹 Контактный телефон;',
    '🔹 Название курса и количество часов обучения;',
    '❗️Документы можно прислать в виде фото/сканов.'
  ].join('\n');
}

function formatQuickReplyProgramTypeLabel(programName: string | null, volumeHours: number | null): string {
  const normalizedProgramName = programName?.trim().toLowerCase() ?? '';

  if (normalizedProgramName.includes('переподготов') || (volumeHours !== null && volumeHours >= 250)) {
    return 'Профессиональная переподготовка';
  }

  if (normalizedProgramName.includes('повыш') || (volumeHours !== null && volumeHours < 250)) {
    return 'Повышение квалификации';
  }

  return 'Обучение';
}

function formatQuickReplyQualification(courseName: string): string {
  return courseName.trim().toLocaleLowerCase('ru-RU');
}

function formatQuickReplyNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(value).replace(',', '.');
}

function formatQuickReplyRubles(amount: number): string {
  return `${Math.round(amount)} рублей`;
}

function calculateQuickReplyFullPaymentDiscount(sum: number): { discountAmount: number; discountedSum: number } {
  const discountAmount = Math.ceil(((sum * 15) / 100) / 500) * 500;

  return {
    discountAmount,
    discountedSum: Math.max(0, sum - discountAmount)
  };
}

function formatQuickReplyDuration(durationEducation: number | null, isWeek: boolean): string {
  if (durationEducation === null || !Number.isFinite(durationEducation) || durationEducation <= 0) {
    return 'уточняется';
  }

  const formattedValue = formatQuickReplyNumber(durationEducation);

  if (isWeek) {
    return `${formattedValue} ${formatRussianPlural(Math.ceil(durationEducation), 'неделя', 'недели', 'недель')}`;
  }

  return `${formattedValue} ${formatRussianPlural(Math.ceil(durationEducation), 'месяц', 'месяца', 'месяцев')}`;
}

function formatQuickReplyInstallment(program: ChatCourseProgramOption): string | null {
  if (program.sum === null || !Number.isFinite(program.sum) || program.volumeHours < 250) {
    return null;
  }

  const durationEducation = program.durationEducation;

  if (durationEducation === null || !Number.isFinite(durationEducation) || durationEducation <= 0 || program.isWeek) {
    return null;
  }

  const paymentCount = Math.max(1, Math.ceil(durationEducation));
  const installmentAmount = Math.ceil(program.sum / paymentCount);

  if (paymentCount === 2) {
    return `2 платежа по ${formatQuickReplyRubles(installmentAmount)} (в начале и за две недели до окончания обучения)`;
  }

  return formatQuickReplyRubles(installmentAmount);
}

function buildQuickReplyProgramPaymentLines(program: ChatCourseProgramOption): string[] {
  const paymentLines = [
    `📚 ${program.volumeHours} часов`,
    `🔹 Срок обучения: ${formatQuickReplyDuration(program.durationEducation, program.isWeek)}`,
    `🔹 Стоимость обучения ${program.sum === null ? 'уточняется' : formatQuickReplyRubles(program.sum)}`
  ];
  const installmentLabel = formatQuickReplyInstallment(program);

  if (installmentLabel) {
    paymentLines.push(`🔹 Рассрочка: ${installmentLabel}`);
  }

  if (program.sum !== null && Number.isFinite(program.sum) && program.volumeHours >= 250) {
    const discount = calculateQuickReplyFullPaymentDiscount(program.sum);
    paymentLines.push(`❗️ При полной оплате в течении 10 дней, после заключения договора — дополнительная скидка ${formatQuickReplyRubles(discount.discountAmount)}, а итоговая стоимость обучения с учетом скидки: ${formatQuickReplyRubles(discount.discountedSum)}`);
  }

  paymentLines.push('•••••••••••••••');
  return paymentLines;
}

function getQuickReplyRelevantCoursePrograms(chat: QuickReplyChat, coursePrograms: ChatCourseProgramOption[]): ChatCourseProgramOption[] {
  const selectedProgram = coursePrograms.find((program) => program.courseProgramId === chat.courseProgramId) ?? null;
  const selectedVolumeHours = selectedProgram?.volumeHours ?? chat.volumeHours;

  if (selectedVolumeHours !== null) {
    const isRetraining = selectedVolumeHours >= 250;
    const filteredPrograms = coursePrograms.filter((program) => (program.volumeHours >= 250) === isRetraining);

    if (filteredPrograms.length > 0) {
      return filteredPrograms;
    }
  }

  return coursePrograms;
}

function formatQuickReplyLongStartDate(startEducationDate: string | null): string | null {
  const normalizedValue = startEducationDate?.trim() ?? '';

  if (!normalizedValue) {
    return null;
  }

  const datePartsMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const dateValue = datePartsMatch
    ? new Date(Number(datePartsMatch[1]), Number(datePartsMatch[2]) - 1, Number(datePartsMatch[3]))
    : new Date(normalizedValue);

  if (Number.isNaN(dateValue.getTime())) {
    return null;
  }

  const monthNames = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря'
  ];

  return `${dateValue.getDate()} ${monthNames[dateValue.getMonth()]} ${dateValue.getFullYear()} г.`;
}

function buildExpandedProgramQuickReplyMessage(chat: QuickReplyChat, context: QuickReplyBuildContext): string | null {
  const courseName = chat.courseName?.trim() || chat.courseTitle?.trim() || null;

  if (!courseName || chat.courseProgramId === null || context.courseProgramsLoading || context.courseProgramsError) {
    return null;
  }

  const coursePrograms = getQuickReplyRelevantCoursePrograms(chat, context.coursePrograms)
    .sort((left, right) => left.volumeHours - right.volumeHours || left.courseProgramId - right.courseProgramId);

  if (coursePrograms.length === 0) {
    return null;
  }

  const selectedProgram = coursePrograms.find((program) => program.courseProgramId === chat.courseProgramId) ?? coursePrograms[0];
  const programTypeLabel = formatQuickReplyProgramTypeLabel(selectedProgram.programName ?? chat.programName, selectedProgram.volumeHours ?? chat.volumeHours);
  const startDateLabel = formatQuickReplyLongStartDate(chat.startEducationDate);
  const programPaymentLines = coursePrograms.flatMap((program, index) => [
    ...buildQuickReplyProgramPaymentLines(program),
    ...(index === coursePrograms.length - 1 ? [] : [''])
  ]);

  return [
    `🎓 ${programTypeLabel} «${courseName}».`,
    `❗️ Квалификация: ${formatQuickReplyQualification(courseName)}`,
    ...(startDateLabel ? [`🎓 Ваше обучение начнется: ${startDateLabel}`] : []),
    '',
    '➡️ Начните путь к востребованной профессии прямо сейчас — выберите подходящий формат обучения:',
    ...programPaymentLines,
    '',
    '📌 Начать обучение просто: Высшее или среднее проф. образование — единственное требование.',
    '🎓 По завершении курса Вы получите бессрочный диплом, который действует на всей территории РФ и заносится в Государственную систему ФИС ФРДО.',
    '📦 Диплом отправляем почтой России с возможностью отслеживания.',
    '',
    '📥 Для оформления договора требуется минимум документов, отправьте на info@yaskill.ru:',
    '🔹 Сканы паспорта',
    '🔹 СНИЛС',
    '🔹 Скан диплома (без приложения)',
    '🔹 Реквизиты организации (при оплате от юр. лица)',
    '🔹 Контактный телефон',
    '🔹 Название курса и количество часов обучения',
    '❗️Документы можно прислать в виде фото/сканов здесь или на почту info@yaskill.ru.',
    '',
    '📍 Оплата возможна любым удобным способом:',
    '💳 Онлайн — банковской картой и др.',
    '💳 Через Сбербанк Онлайн / приложения других банков',
    '💳 В отделении банка',
    '❗️Комиссию уточняйте в вашем банке',
    '',
    '📌 Реквизиты для оплаты предоставим после заключения договора.',
    '🔔 Сделайте шаг к новой профессии уже сегодня!'
  ].join('\n');
}

function formatChatListTimestamp(value: string | null): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return '';
  }

  const now = new Date();
  const isSameDay = now.toDateString() === date.toDateString();

  return new Intl.DateTimeFormat('ru-RU', isSameDay
    ? { hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: '2-digit' }).format(date);
}

function formatMessageTimestamp(value: string): string {
  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

const RUSSIAN_MONTH_LABELS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря'
] as const;

const RUSSIAN_WEEKDAY_LABELS = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'] as const;

function formatMessagePanelTimestamp(value: string, joiner: ',' | 'в' = ','): string {
  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return '';
  }

  const day = date.getDate();
  const weekdayLabel = RUSSIAN_WEEKDAY_LABELS[date.getDay()] ?? '';
  const monthLabel = RUSSIAN_MONTH_LABELS[date.getMonth()] ?? '';
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return joiner === 'в'
    ? `${weekdayLabel} ${day} ${monthLabel} ${year} в ${hours}:${minutes}`
    : `${weekdayLabel} ${day} ${monthLabel} ${year}, ${hours}:${minutes}`;
}

function formatDateTimeLocalValue(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function parseReminderDateTimeValue(value: string): Date | null {
  const date = new Date(value);

  return Number.isFinite(date.getTime()) ? date : null;
}

function buildReminderMinDateTimeValue(): string {
  const minDate = new Date();
  minDate.setMinutes(minDate.getMinutes() + 1, 0, 0);
  return formatDateTimeLocalValue(minDate);
}

function buildDefaultReminderDateTimeValue(): string {
  const nextDate = new Date();
  nextDate.setMinutes(nextDate.getMinutes() + 30, 0, 0);
  return formatDateTimeLocalValue(nextDate);
}

function buildReminderFormDateTimeValue(value: string | null | undefined): string {
  if (!value) {
    return buildDefaultReminderDateTimeValue();
  }

  const date = parseReminderDateTimeValue(value);

  return date
    ? formatDateTimeLocalValue(date)
    : buildDefaultReminderDateTimeValue();
}

function formatFileSize(fileSize: number | null): string | null {
  if (!fileSize || fileSize <= 0) {
    return null;
  }

  if (fileSize < 1024) {
    return `${fileSize} Б`;
  }

  if (fileSize < 1024 * 1024) {
    return `${(fileSize / 1024).toFixed(1)} КБ`;
  }

  if (fileSize < 1024 * 1024 * 1024) {
    return `${(fileSize / (1024 * 1024)).toFixed(1)} МБ`;
  }

  return `${(fileSize / (1024 * 1024 * 1024)).toFixed(1)} ГБ`;
}

function formatCourseAttachmentDisplayFileName(fileName: string): string {
  return fileName
    .replace(/выпускн(?:ая|ой)_работ(?:а|ы)/gi, 'ВР')
    .replace(/итоговое_тестирование/gi, 'ИТ');
}

function hasTransferredFiles(dataTransfer: DataTransfer | null): boolean {
  return Array.from(dataTransfer?.types ?? []).includes('Files');
}

function getMessageFileKey(file: OperatorChatMessageFile): string {
  return `${file.platform ?? 'chats'}:${file.fileId}`;
}

function buildComposerFileKey(file: Pick<File, 'name' | 'size' | 'lastModified'>): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function formatDuration(durationSeconds: number | null): string | null {
  if (!durationSeconds || durationSeconds <= 0) {
    return null;
  }

  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const AUDIO_FILE_EXTENSIONS = new Set(['aac', 'm4a', 'mp3', 'oga', 'ogg', 'opus', 'wav', 'webm']);
const STRUCTURED_DOCUMENT_PREVIEW_EXTENSIONS = new Set(['doc', 'txt', 'xls', 'xlsx']);
const STRUCTURED_DOCUMENT_PREVIEW_MIME_TYPES = new Set([
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain'
]);
const GENERIC_ATTACHMENT_FILE_NAMES = new Set(['animation', 'audio', 'document', 'file', 'image', 'photo', 'sticker', 'video', 'video-note', 'voice']);
const MIME_TYPE_EXTENSION_MAP: Record<string, string> = {
  'application/pdf': 'pdf',
  'audio/aac': 'aac',
  'audio/mp3': 'mp3',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'image/gif': 'gif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'video/mp4': 'mp4',
  'video/webm': 'webm'
};

function normalizeAttachmentFileNameCandidate(fileName: string | null): string | null {
  const trimmedFileName = fileName?.trim() ?? '';
  return trimmedFileName || null;
}

function extractFileExtensionFromName(fileName: string | null): string {
  const normalizedFileName = normalizeAttachmentFileNameCandidate(fileName);

  if (!normalizedFileName) {
    return '';
  }

  const extensionIndex = normalizedFileName.lastIndexOf('.');
  return extensionIndex >= 0 ? normalizedFileName.slice(extensionIndex + 1).toLowerCase() : '';
}

function isGenericAttachmentFileName(fileName: string): boolean {
  const normalizedFileName = fileName.trim().toLowerCase();
  const extensionIndex = normalizedFileName.lastIndexOf('.');
  const baseName = extensionIndex >= 0 ? normalizedFileName.slice(0, extensionIndex) : normalizedFileName;

  return GENERIC_ATTACHMENT_FILE_NAMES.has(baseName);
}

function inferAttachmentFileExtension(file: OperatorChatMessageFile): string {
  const explicitExtension = extractFileExtensionFromName(file.originalFileName) || extractFileExtensionFromName(file.fileName);

  if (explicitExtension) {
    return explicitExtension;
  }

  const normalizedMimeType = file.mimeType?.split(';')[0]?.trim().toLowerCase() ?? '';
  return MIME_TYPE_EXTENSION_MAP[normalizedMimeType] ?? '';
}

function buildFallbackAttachmentFileName(file: OperatorChatMessageFile): string {
  const inferredExtension = inferAttachmentFileExtension(file);

  switch (file.fileType) {
    case 'photo':
    case 'sticker':
      return inferredExtension ? `image.${inferredExtension}` : 'image';
    case 'video':
    case 'video_note':
    case 'animation':
      return inferredExtension ? `video.${inferredExtension}` : 'video';
    case 'audio':
    case 'voice':
      return inferredExtension ? `audio.${inferredExtension}` : 'audio';
    case 'document':
      return inferredExtension ? `document.${inferredExtension}` : 'document';
    default:
      return inferredExtension ? `file.${inferredExtension}` : 'file';
  }
}

function getAttachmentFileName(file: OperatorChatMessageFile): string {
  const candidates = [file.originalFileName, file.fileName]
    .map((candidate) => normalizeAttachmentFileNameCandidate(candidate))
    .filter((candidate): candidate is string => candidate !== null);
  const preferredCandidate = candidates.find((candidate) => !isGenericAttachmentFileName(candidate)) ?? candidates[0];

  if (!preferredCandidate) {
    return buildFallbackAttachmentFileName(file);
  }

  if (extractFileExtensionFromName(preferredCandidate) || !isGenericAttachmentFileName(preferredCandidate)) {
    return preferredCandidate;
  }

  const inferredExtension = inferAttachmentFileExtension(file);
  return inferredExtension ? `${preferredCandidate}.${inferredExtension}` : preferredCandidate;
}

function getAttachmentFileExtension(file: OperatorChatMessageFile): string {
  return extractFileExtensionFromName(getAttachmentFileName(file));
}

function getAttachmentMimeType(file: OperatorChatMessageFile): string | null {
  const normalizedMimeType = file.mimeType?.split(';')[0]?.trim().toLowerCase() ?? '';

  if (normalizedMimeType && normalizedMimeType !== 'application/octet-stream') {
    return normalizedMimeType;
  }

  switch (getAttachmentFileExtension(file)) {
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'gif':
      return 'image/gif';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'pdf':
      return 'application/pdf';
    case 'png':
      return 'image/png';
    case 'txt':
      return 'text/plain';
    case 'webp':
      return 'image/webp';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    default:
      return normalizedMimeType || null;
  }
}

function normalizeAttachmentPreviewBlob(file: OperatorChatMessageFile, blob: Blob): Blob {
  const expectedMimeType = getAttachmentMimeType(file);

  if (!expectedMimeType || blob.type === expectedMimeType) {
    return blob;
  }

  return blob.slice(0, blob.size, expectedMimeType);
}

function buildAudioAttachmentTitle(file: OperatorChatMessageFile): string {
  return file.fileType === 'voice' ? 'Голосовое сообщение' : getAttachmentFileName(file);
}

function formatAudioRemainingDuration(durationSeconds: number | null, currentTimeSeconds: number): string {
  if (!durationSeconds || durationSeconds <= 0) {
    return '--:--';
  }

  return formatDuration(Math.max(0, Math.ceil(durationSeconds - currentTimeSeconds))) ?? '--:--';
}

function normalizePhoneNumber(phoneNumber: string | null): string | null {
  if (!phoneNumber) {
    return null;
  }

  const digits = phoneNumber.replace(/\D+/g, '');
  return digits || null;
}

function isPhoneCountryCode(country: string): country is PhoneCountryCode {
  return country in PHONE_COUNTRY_MAP;
}

function normalizePhoneDigits(input: string): string {
  return input.replace(/\D+/g, '');
}

function extractLocalPhoneDigits(input: string, country: PhoneCountryCode): string {
  const config = PHONE_COUNTRY_MAP[country];
  let digits = normalizePhoneDigits(input);

  if (digits.startsWith(config.dialCode) && digits.length > config.localDigits) {
    digits = digits.slice(config.dialCode.length);
  }

  if ((country === 'RU' || country === 'KZ') && digits.startsWith('8') && digits.length === config.localDigits + 1) {
    digits = digits.slice(1);
  }

  return digits.slice(0, config.localDigits);
}

function formatRuLikePhone(digits: string): string {
  if (digits.length === 0) {
    return '';
  }

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  }

  if (digits.length <= 8) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
}

function formatByPhone(digits: string): string {
  if (digits.length === 0) {
    return '';
  }

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 5) {
    return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  }

  if (digits.length <= 7) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
  }

  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
}

function getPhoneCountryConfig(country?: string | null): PhoneCountryConfig {
  if (country && isPhoneCountryCode(country)) {
    return PHONE_COUNTRY_MAP[country];
  }

  return PHONE_COUNTRY_MAP.RU;
}

function formatLocalPhone(input: string, country: PhoneCountryCode): string {
  const digits = extractLocalPhoneDigits(input, country);

  if (country === 'BY') {
    return formatByPhone(digits);
  }

  return formatRuLikePhone(digits);
}

function buildPhoneValue(input: string, country?: string | null): string {
  const resolvedCountry = getPhoneCountryConfig(country).code;
  const localDigits = extractLocalPhoneDigits(input, resolvedCountry);

  return `${PHONE_COUNTRY_MAP[resolvedCountry].dialCode}${localDigits}`;
}

function formatPhoneValue(input: string, country?: string | null): string {
  const resolvedCountry = getPhoneCountryConfig(country).code;
  const config = PHONE_COUNTRY_MAP[resolvedCountry];
  const localPhone = formatLocalPhone(input, resolvedCountry);

  return localPhone.length > 0 ? `+${config.dialCode} ${localPhone}` : `+${config.dialCode}`;
}

function phoneMask(input: string, country?: string | null): { error: boolean; data: string } {
  const resolvedCountry = getPhoneCountryConfig(country).code;
  const maskedPhone = formatLocalPhone(input, resolvedCountry);
  const localDigits = extractLocalPhoneDigits(input, resolvedCountry);
  const config = PHONE_COUNTRY_MAP[resolvedCountry];

  return {
    error: localDigits.length !== config.localDigits,
    data: maskedPhone
  };
}

function isValidPhoneForCountry(input: string, country?: string | null): boolean {
  const config = getPhoneCountryConfig(country);
  const normalizedPhone = normalizePhoneDigits(input);

  return normalizedPhone.startsWith(config.dialCode) && normalizedPhone.length === config.dialCode.length + config.localDigits;
}

function buildPhoneHref(phoneNumber: string | null): string | null {
  if (!phoneNumber) {
    return null;
  }

  const trimmedValue = phoneNumber.trim();
  const digits = trimmedValue.replace(/\D+/g, '');

  if (!digits) {
    return null;
  }

  return `tel:${trimmedValue.startsWith('+') ? `+${digits}` : digits}`;
}

function buildApplicationHref(crmApplicationId: number | null): string | null {
  if (crmApplicationId === null) {
    return null;
  }

  return `https://crm.iktbox.ru/lk/applications/${crmApplicationId}`;
}

function buildCourseHref(pageCanonical: string | null): string | null {
  if (!pageCanonical) {
    return null;
  }

  const normalizedValue = pageCanonical.trim();

  if (!normalizedValue) {
    return null;
  }

  try {
    return new URL(normalizedValue).toString();
  } catch {
    if (normalizedValue.startsWith('/')) {
      return `https://yaskill.ru${normalizedValue}`;
    }

    return null;
  }
}

function buildMessageActionHref(value: string | null): string | null {
  if (!value) {
    return null;
  }

  try {
    const actionUrl = new URL(value);
    return actionUrl.protocol === 'http:' || actionUrl.protocol === 'https:' ? actionUrl.toString() : null;
  } catch {
    return null;
  }
}

function formatChatApplicationLabel(crmApplicationId: number | null): string {
  return crmApplicationId === null ? 'Не указана' : String(crmApplicationId);
}

function formatChatApplicationNumberLabel(crmApplicationId: number | null): string {
  return crmApplicationId === null ? '----' : String(crmApplicationId);
}

function getMessageRoleLabel(message: OperatorChatMessage): string {
  if (message.direction === 'incoming') {
    return 'Клиент';
  }

  if (isCrmOriginatedMessage(message)) {
    return 'CRM';
  }

  if (isAiAutoresponderMessage(message)) {
    return 'AI';
  }

  return message.senderKind === 'operator' ? 'Оператор' : 'Бот';
}

function getMessageActorLabel(message: OperatorChatMessage, chatTitle: string): string {
  if (isCrmOriginatedMessage(message)) {
    return 'Отправлено из CRM';
  }

  const trimmedSenderName = message.senderName?.trim();

  if (trimmedSenderName) {
    return trimmedSenderName;
  }

  if (message.direction === 'incoming') {
    return chatTitle;
  }

  if (isAiAutoresponderMessage(message)) {
    return 'AI автоответчик';
  }

  if (message.senderKind === 'bot') {
    return 'Автоответ YaSkill';
  }

  return 'Менеджер YaSkill';
}

function formatChatCardApplicationLabel(crmApplicationId: number | null): string {
  return crmApplicationId === null ? '----' : `Заявка: ${formatChatApplicationLabel(crmApplicationId)}`;
}

function formatDemoAccessLabel(demoAccessGranted: boolean): string {
  return demoAccessGranted ? 'Демо предоставлен' : 'Демо не предоставлен';
}

function formatStartEducationDateValue(startEducationDate: string | null): string | null {
  const normalizedValue = startEducationDate?.trim() ?? '';

  if (!normalizedValue) {
    return null;
  }

  const datePartsMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (datePartsMatch) {
    const [, year, month, day] = datePartsMatch;
    return `${day}.${month}.${year}`;
  }

  const parsedValue = new Date(normalizedValue);

  if (Number.isNaN(parsedValue.getTime())) {
    return normalizedValue;
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(parsedValue);
}

function formatStartEducationDateLabel(startEducationDate: string | null): string | null {
  const formattedValue = formatStartEducationDateValue(startEducationDate);
  return formattedValue ? `Старт: ${formattedValue}` : null;
}

function formatUnreadCount(unreadCount: number): string {
  return unreadCount > 99 ? '99+' : String(unreadCount);
}

function getPlatformBadgeLabel(platform: ChatPlatform): string {
  if (platform === 'telegram') {
    return 'TG';
  }

  if (platform === 'max') {
    return 'MAX';
  }

  return 'WDS';
}

function getTimestampValue(timestamp: string | null): number {
  if (!timestamp) {
    return 0;
  }

  const parsedValue = Date.parse(timestamp);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function hasPreferredThreadValue<T>(value: T | null | undefined): value is T {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return value !== null && value !== undefined;
}

function pickPreferredThreadChats(chats: OperatorChatSummary[]): OperatorChatSummary[] {
  return [...chats].sort((left, right) => {
    if (left.platform === right.platform) {
      return 0;
    }

    return left.platform === 'max' ? -1 : 1;
  });
}

function pickPreferredThreadValue<T>(
  chats: OperatorChatSummary[],
  selector: (chat: OperatorChatSummary) => T | null | undefined
): T | null {
  for (const chat of pickPreferredThreadChats(chats)) {
    const value = selector(chat);

    if (hasPreferredThreadValue(value)) {
      return value;
    }
  }

  return null;
}

function findChatThreadByChatId(threads: ChatThreadSummary[], chatId: number | null): ChatThreadSummary | null {
  if (chatId === null) {
    return null;
  }

  return threads.find((thread) => thread.chatIds.includes(chatId)) ?? null;
}

function resolveChatThreadId(threads: ChatThreadSummary[], chatId: number | null): number | null {
  return findChatThreadByChatId(threads, chatId)?.chatId ?? null;
}

function buildChatThreads(chats: OperatorChatSummary[]): ChatThreadSummary[] {
  const phoneUsage = new Map<string, number>();

  chats.forEach((chat) => {
    const normalizedPhoneNumber = normalizePhoneNumber(chat.phoneNumber);

    if (!normalizedPhoneNumber) {
      return;
    }

    phoneUsage.set(normalizedPhoneNumber, (phoneUsage.get(normalizedPhoneNumber) ?? 0) + 1);
  });

  const groupedChats = new Map<string, OperatorChatSummary[]>();

  chats.forEach((chat) => {
    const normalizedPhoneNumber = normalizePhoneNumber(chat.phoneNumber);
    const isPhoneGroup = Boolean(normalizedPhoneNumber && (phoneUsage.get(normalizedPhoneNumber) ?? 0) > 1);
    const sectionKey = isPhoneGroup ? `phone:${normalizedPhoneNumber}` : `chat:${chat.chatId}`;
    const existingChats = groupedChats.get(sectionKey);

    if (existingChats) {
      existingChats.push(chat);
      return;
    }

    groupedChats.set(sectionKey, [chat]);
  });

  return Array.from(groupedChats.entries()).map(([key, threadChats]) => {
    const preferredChats = pickPreferredThreadChats(threadChats);
    const primaryChat = preferredChats[0] ?? threadChats[0];
    const latestChat = [...threadChats].sort((left, right) => {
      const timestampDiff = getTimestampValue(right.lastMessageAt ?? right.createdAt) - getTimestampValue(left.lastMessageAt ?? left.createdAt);

      if (timestampDiff !== 0) {
        return timestampDiff;
      }

      return right.chatId - left.chatId;
    })[0] ?? primaryChat;
    const lastIncomingMessageAt = threadChats
      .map((chat) => chat.lastIncomingMessageAt)
      .filter((value): value is string => Boolean(value))
      .sort((left, right) => getTimestampValue(right) - getTimestampValue(left))[0] ?? null;
    const activeReminderAt = threadChats
      .map((chat) => chat.activeReminderAt)
      .filter((value): value is string => Boolean(value))
      .sort((left, right) => getTimestampValue(left) - getTimestampValue(right))[0] ?? null;
    const linkedWidgetChats = Array.from(
      threadChats
        .flatMap((chat) => chat.linkedWidgetChats ?? [])
        .sort((left, right) => {
          const timestampDiff = getTimestampValue(right.lastMessageAt ?? right.createdAt) - getTimestampValue(left.lastMessageAt ?? left.createdAt);

          if (timestampDiff !== 0) {
            return timestampDiff;
          }

          return right.chatId - left.chatId;
        })
        .reduce<Map<number, LinkedWidgetChatLinkSummary>>((accumulator, linkedChat) => {
          if (!accumulator.has(linkedChat.chatId)) {
            accumulator.set(linkedChat.chatId, linkedChat);
          }

          return accumulator;
        }, new Map<number, LinkedWidgetChatLinkSummary>())
        .values()
    );

    return {
      ...primaryChat,
      key,
      chats: threadChats,
      chatIds: threadChats.map((chat) => chat.chatId),
      title: pickPreferredThreadValue(threadChats, (chat) => chat.title) ?? primaryChat.title,
      platformUsername: pickPreferredThreadValue(threadChats, (chat) => chat.platformUsername),
      phoneNumber: pickPreferredThreadValue(threadChats, (chat) => chat.phoneNumber),
      email: pickPreferredThreadValue(threadChats, (chat) => chat.email),
      avatarUrl: pickPreferredThreadValue(threadChats, (chat) => chat.avatarUrl),
      courseProgramId: pickPreferredThreadValue(threadChats, (chat) => chat.courseProgramId) ?? primaryChat.courseProgramId,
      pageCanonical: pickPreferredThreadValue(threadChats, (chat) => chat.pageCanonical),
      courseName: pickPreferredThreadValue(threadChats, (chat) => chat.courseName),
      programId: pickPreferredThreadValue(threadChats, (chat) => chat.programId) ?? primaryChat.programId,
      programName: pickPreferredThreadValue(threadChats, (chat) => chat.programName),
      volumeHours: pickPreferredThreadValue(threadChats, (chat) => chat.volumeHours) ?? primaryChat.volumeHours,
      courseTitle: pickPreferredThreadValue(threadChats, (chat) => chat.courseTitle),
      aiAutoresponderEnabled: threadChats.every((chat) => chat.aiAutoresponderEnabled),
      crmApplicationId: pickPreferredThreadValue(threadChats, (chat) => chat.crmApplicationId) ?? primaryChat.crmApplicationId,
      demoAccessGranted: threadChats.some((chat) => chat.demoAccessGranted),
      startEducationDate: pickPreferredThreadValue(threadChats, (chat) => chat.startEducationDate),
      unreadCount: threadChats.reduce((total, chat) => total + Math.max(0, chat.unreadCount), 0),
      lastMessagePreview: latestChat.lastMessagePreview ?? pickPreferredThreadValue(threadChats, (chat) => chat.lastMessagePreview),
      lastMessageAt: latestChat.lastMessageAt ?? primaryChat.lastMessageAt,
      lastIncomingMessageAt,
      activeReminderAt,
      hasTriggeredReminder: threadChats.some((chat) => chat.hasTriggeredReminder),
      createdAt: latestChat.createdAt,
      linkedWidgetChats,
      hasTelegramChat: threadChats.some((chat) => chat.platform === 'telegram'),
      hasMaxChat: threadChats.some((chat) => chat.platform === 'max')
    };
  });
}

function buildChatSearchTarget(chat: OperatorChatSummary): string {
  return [
    chat.title,
    chat.platformUsername,
    chat.phoneNumber,
    chat.email,
    chat.courseTitle,
    chat.courseName,
    chat.programName,
    chat.pageCanonical,
    chat.programId === null ? null : String(chat.programId),
    chat.courseProgramId === null ? null : String(chat.courseProgramId),
    chat.volumeHours === null ? null : String(chat.volumeHours),
    chat.startEducationDate,
    chat.lastMessagePreview,
    chat.crmApplicationId === null ? null : String(chat.crmApplicationId)
  ].filter(Boolean).join(' ').toLowerCase();
}

function buildChatThreadSearchTarget(thread: ChatThreadSummary): string {
  return [
    ...thread.chats.map((chat) => buildChatSearchTarget(chat)),
    thread.hasMaxChat ? 'max' : null,
    thread.hasTelegramChat ? 'tg telegram' : null,
    thread.linkedWidgetChats.length > 0 ? 'wds widget сайт' : null
  ].filter(Boolean).join(' ').toLowerCase();
}

function buildWidgetChatSearchTarget(chat: WidgetChatSummary): string {
  return [
    chat.title,
    chat.currentPageTitle,
    chat.currentPagePath,
    chat.currentPageUrl,
    chat.siteOrigin,
    formatWidgetPageLabel(chat),
    formatWidgetPageCaption(chat),
    chat.lastMessagePreview,
    ...chat.linkedChats.map((linkedChat) => [linkedChat.platform, linkedChat.title, linkedChat.phoneNumber].filter(Boolean).join(' '))
  ].filter(Boolean).join(' ').toLowerCase();
}

function buildWidgetPageHref(pageUrl: string | null): string | null {
  if (!pageUrl) {
    return null;
  }

  try {
    const parsedUrl = new URL(pageUrl);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:' ? parsedUrl.toString() : null;
  } catch {
    return null;
  }
}

function normalizeWidgetPageTitle(pageTitle: string | null): string | null {
  const trimmedTitle = pageTitle?.trim();

  if (!trimmedTitle) {
    return null;
  }

  const normalizedTitle = trimmedTitle
    .replace(/^YaSkill\s*[|\-–—•]\s*/i, '')
    .replace(/^Яа?Skill\s*[|\-–—•]\s*/i, '')
    .replace(/\s*[|\-–—•]\s*YaSkill$/i, '')
    .replace(/\s*[|\-–—•]\s*Яа?Skill$/i, '')
    .trim();

  return normalizedTitle || null;
}

function resolveWidgetPagePath(pageInfo: WidgetPageInfo): string | null {
  const explicitPath = pageInfo.currentPagePath?.trim();

  if (explicitPath) {
    return explicitPath.startsWith('/') ? explicitPath : `/${explicitPath}`;
  }

  if (!pageInfo.currentPageUrl) {
    return null;
  }

  try {
    const parsedUrl = new URL(pageInfo.currentPageUrl);
    return `${parsedUrl.pathname}${parsedUrl.search}` || '/';
  } catch {
    return null;
  }
}

function normalizeWidgetPagePathname(pagePath: string | null): string | null {
  if (!pagePath) {
    return null;
  }

  const [pathname] = pagePath.split(/[?#]/, 1);
  const normalizedPathname = pathname.replace(/\/+$/, '');

  return normalizedPathname || '/';
}

function decodeWidgetPathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function humanizeWidgetPathSegment(segment: string): string {
  const normalizedSegment = decodeWidgetPathSegment(segment)
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalizedSegment) {
    return 'Страница не определена';
  }

  return normalizedSegment.charAt(0).toUpperCase() + normalizedSegment.slice(1);
}

function isWidgetCourseLikePath(pathname: string | null): boolean {
  if (!pathname || pathname === '/') {
    return false;
  }

  return /\/(?:program|programs|programme|course|courses|kurs|kursy|landing|profession|professiya|programma|programmy)(?:\/|$)/i.test(pathname);
}

function formatGenericWidgetPageLabel(title: string): string {
  if (title.length < 2) {
    return `Страница ${title.toLowerCase()}`;
  }

  const nextCharacter = title.charAt(1);
  const isAcronym = nextCharacter === nextCharacter.toUpperCase() && nextCharacter !== nextCharacter.toLowerCase();
  const adjustedTitle = isAcronym ? title : `${title.charAt(0).toLowerCase()}${title.slice(1)}`;

  return `Страница ${adjustedTitle}`;
}

function resolveWidgetPageIdentity(pageInfo: WidgetPageInfo): { label: string; caption: string } {
  const normalizedTitle = normalizeWidgetPageTitle(pageInfo.currentPageTitle);
  const pagePath = resolveWidgetPagePath(pageInfo);
  const pathname = normalizeWidgetPagePathname(pagePath);

  if (pathname === '/') {
    return {
      label: 'Главная страница',
      caption: pagePath ?? pageInfo.currentPageUrl ?? '/'
    };
  }

  if (pathname && /^\/(?:contacts?|kontakty)(?:\/|$)/i.test(pathname)) {
    return {
      label: 'Страница контакты',
      caption: pagePath ?? pageInfo.currentPageUrl ?? 'URL страницы не передан'
    };
  }

  if (pathname && /^\/(?:license|licence|licenses|licenziya|licenzii|litsenziya|litsenzii)(?:\/|$)/i.test(pathname)) {
    return {
      label: 'Страница лицензия',
      caption: pagePath ?? pageInfo.currentPageUrl ?? 'URL страницы не передан'
    };
  }

  if (normalizedTitle && isWidgetCourseLikePath(pathname)) {
    return {
      label: normalizedTitle,
      caption: pagePath ?? pageInfo.currentPageUrl ?? normalizedTitle
    };
  }

  if (!normalizedTitle && isWidgetCourseLikePath(pathname)) {
    const pathSegments = pathname?.split('/').filter(Boolean) ?? [];
    const lastPathSegment = pathSegments[pathSegments.length - 1] ?? '';

    return {
      label: humanizeWidgetPathSegment(lastPathSegment),
      caption: pagePath ?? pageInfo.currentPageUrl ?? 'URL страницы не передан'
    };
  }

  if (normalizedTitle) {
    return {
      label: formatGenericWidgetPageLabel(normalizedTitle),
      caption: pagePath ?? pageInfo.currentPageUrl ?? normalizedTitle
    };
  }

  if (pathname && pathname !== '/') {
    const pathSegments = pathname.split('/').filter(Boolean);
    const lastPathSegment = pathSegments[pathSegments.length - 1] ?? '';

    return {
      label: formatGenericWidgetPageLabel(humanizeWidgetPathSegment(lastPathSegment)),
      caption: pagePath ?? pageInfo.currentPageUrl ?? 'URL страницы не передан'
    };
  }

  return {
    label: 'Страница не определена',
    caption: pageInfo.currentPageUrl ?? 'URL страницы не передан'
  };
}

function formatWidgetPageLabel(pageInfo: WidgetPageInfo): string {
  return resolveWidgetPageIdentity(pageInfo).label;
}

function formatWidgetPageCaption(pageInfo: WidgetPageInfo): string {
  return resolveWidgetPageIdentity(pageInfo).caption;
}

function formatWidgetLinkedChatLabel(linkedChat: WidgetChatLinkedSummary): string {
  const platformLabel = linkedChat.platform === 'telegram' ? 'TG' : 'MAX';
  return [platformLabel, linkedChat.title, linkedChat.phoneNumber].filter(Boolean).join(' • ');
}

function formatWidgetLinkTargetLabel(chat: OperatorChatSummary): string {
  const platformLabel = chat.platform === 'telegram' ? 'TG' : chat.platform === 'max' ? 'MAX' : 'WDS';
  return [
    platformLabel,
    chat.title,
    chat.phoneNumber,
    chat.courseName ?? chat.courseTitle,
    chat.crmApplicationId === null ? null : `Заявка ${chat.crmApplicationId}`
  ].filter(Boolean).join(' • ');
}

function formatRussianPlural(value: number, one: string, few: string, many: string): string {
  const absoluteValue = Math.abs(value);
  const lastTwoDigits = absoluteValue % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return many;
  }

  const lastDigit = absoluteValue % 10;

  if (lastDigit === 1) {
    return one;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return few;
  }

  return many;
}

function formatCourseVolumeHours(volumeHours: number | null): string {
  return volumeHours === null ? 'Часы не указаны' : `${volumeHours} ${formatRussianPlural(volumeHours, 'час', 'часа', 'часов')}`;
}

function formatCourseProgramAmount(amount: number | null): string | null {
  if (amount === null || !Number.isFinite(amount)) {
    return null;
  }

  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(amount)} ₽`;
}

function formatCourseProgramOptionLabel(volumeHours: number, amount: number | null): string {
  const formattedHours = formatCourseVolumeHours(volumeHours);
  const formattedAmount = formatCourseProgramAmount(amount);

  return formattedAmount ? `${formattedHours} - ${formattedAmount}` : formattedHours;
}

function normalizeCourseOptionLabel(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function getChatCourseDisplayName(chat: { courseName: string | null; courseTitle: string | null } | null): string {
  return (chat?.courseName ?? chat?.courseTitle ?? '').trim();
}

function getRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

function getRecordString(record: Record<string, unknown> | null, key: string): string | null {
  if (!record) {
    return null;
  }

  const value = record[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function getRecordNumber(record: Record<string, unknown> | null, key: string): number | null {
  if (!record) {
    return null;
  }

  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getRecordBoolean(record: Record<string, unknown> | null, key: string): boolean {
  return record?.[key] === true;
}

function isAiAutoresponderMessage(message: OperatorChatMessage): boolean {
  const metadata = getRecord(message.metadata);
  return message.senderKind === 'ai'
    || getRecordBoolean(metadata, 'aiAutoresponder');
}

function isCrmOriginatedMessage(message: OperatorChatMessage): boolean {
  const metadata = getRecord(message.metadata);
  return getRecordString(metadata, 'source') === 'crm'
    || getRecordBoolean(metadata, 'sentFromCrm');
}

function getOperatorMessageBatchId(message: OperatorChatMessage): string | null {
  return getRecordString(getRecord(message.metadata), 'operatorMessageBatchId');
}

function getOperatorMessageBatchAttachmentCount(message: OperatorChatMessage): number | null {
  return getRecordNumber(getRecord(message.metadata), 'operatorMessageBatchAttachmentCount');
}

function getOperatorMessageBatchSequence(message: OperatorChatMessage): number | null {
  return getRecordNumber(getRecord(message.metadata), 'operatorMessageBatchSequence');
}

function compareOperatorChatMessages(left: OperatorChatMessage, right: OperatorChatMessage): number {
  const timestampDiff = getTimestampValue(left.sentAt) - getTimestampValue(right.sentAt);

  if (timestampDiff !== 0) {
    return timestampDiff;
  }

  if (left.messageRole === right.messageRole) {
    if (left.chatId !== right.chatId) {
      return left.chatId - right.chatId;
    }

    return left.messageId - right.messageId;
  }

  return left.messageRole === 'chat' ? -1 : 1;
}

async function fetchChatThreadMessages(
  fetchAuthorizedJson: AuthorizedJsonRequest,
  chatIds: number[]
): Promise<OperatorChatMessage[]> {
  const uniqueChatIds = Array.from(new Set(chatIds));
  if (uniqueChatIds.length === 0) {
    return [];
  }

  const settledMessageGroups = await Promise.allSettled(uniqueChatIds.map((chatId) => getOperatorChatMessages(fetchAuthorizedJson, chatId)));
  const messageGroups = settledMessageGroups
    .filter((result): result is PromiseFulfilledResult<OperatorChatMessage[]> => result.status === 'fulfilled')
    .map((result) => result.value);

  if (messageGroups.length === 0) {
    const firstRejectedResult = settledMessageGroups.find((result): result is PromiseRejectedResult => result.status === 'rejected');

    if (firstRejectedResult) {
      throw firstRejectedResult.reason instanceof Error
        ? firstRejectedResult.reason
        : new Error('Не удалось загрузить сообщения сервиса.');
    }

    return [];
  }

  return messageGroups.flat().sort(compareOperatorChatMessages);
}

function compareOperatorChatHistoryEntries(left: OperatorChatHistoryEntry, right: OperatorChatHistoryEntry): number {
  const timestampDiff = Date.parse(right.occurredAt) - Date.parse(left.occurredAt);

  if (timestampDiff !== 0) {
    return timestampDiff;
  }

  return right.logId - left.logId;
}

function formatChatHistoryTimestamp(value: string): string {
  const parsedValue = new Date(value);

  if (Number.isNaN(parsedValue.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(parsedValue);
}

async function fetchChatThreadHistory(
  fetchAuthorizedJson: AuthorizedJsonRequest,
  chatIds: number[]
): Promise<OperatorChatHistoryEntry[]> {
  const uniqueChatIds = Array.from(new Set(chatIds));

  if (uniqueChatIds.length === 0) {
    return [];
  }

  const settledHistoryGroups = await Promise.allSettled(uniqueChatIds.map((chatId) => getOperatorChatHistory(fetchAuthorizedJson, chatId)));
  const historyGroups = settledHistoryGroups
    .filter((result): result is PromiseFulfilledResult<OperatorChatHistoryEntry[]> => result.status === 'fulfilled')
    .map((result) => result.value);

  if (historyGroups.length === 0) {
    const firstRejectedResult = settledHistoryGroups.find((result): result is PromiseRejectedResult => result.status === 'rejected');

    if (firstRejectedResult) {
      throw firstRejectedResult.reason instanceof Error
        ? firstRejectedResult.reason
        : new Error('Не удалось загрузить историю чата.');
    }

    return [];
  }

  return historyGroups.flat().sort(compareOperatorChatHistoryEntries);
}

function buildDisplayedChatMessages(messages: OperatorChatMessage[]): DisplayedChatMessage[] {
  const displayedMessages: DisplayedChatMessage[] = [];

  for (let index = 0; index < messages.length; index += 1) {
    const message = messages[index];
    const batchId = getOperatorMessageBatchId(message);

    if (!batchId || message.direction !== 'outgoing' || message.senderKind !== 'operator') {
      displayedMessages.push({
        ...message,
        sourceMessages: [message],
        groupedMessageIds: [message.messageId],
        renderTextBelowAttachments: false
      });
      continue;
    }

    const batchMessages = [message];

    while (index + 1 < messages.length) {
      const nextMessage = messages[index + 1];

      if (
        nextMessage.direction !== 'outgoing'
        || nextMessage.senderKind !== 'operator'
        || nextMessage.chatId !== message.chatId
        || getOperatorMessageBatchId(nextMessage) !== batchId
      ) {
        break;
      }

      batchMessages.push(nextMessage);
      index += 1;
    }

    const orderedBatchMessages = [...batchMessages].sort((left, right) => {
      const leftSequence = getOperatorMessageBatchSequence(left) ?? Number.MAX_SAFE_INTEGER;
      const rightSequence = getOperatorMessageBatchSequence(right) ?? Number.MAX_SAFE_INTEGER;

      if (leftSequence !== rightSequence) {
        return leftSequence - rightSequence;
      }

      const leftTime = Date.parse(left.sentAt);
      const rightTime = Date.parse(right.sentAt);

      if (leftTime !== rightTime) {
        return leftTime - rightTime;
      }

      return left.messageId - right.messageId;
    });

    const groupedFiles = orderedBatchMessages.flatMap((item) => item.files);
    const groupedText = [...orderedBatchMessages]
      .reverse()
      .map((item) => item.textContent?.trim() || null)
      .find((value): value is string => Boolean(value)) ?? null;
    const statusMessage = orderedBatchMessages[orderedBatchMessages.length - 1];
    const groupedMessageIds = orderedBatchMessages.map((item) => item.messageId);
    const expectedAttachmentCount = orderedBatchMessages
      .map((item) => getOperatorMessageBatchAttachmentCount(item))
      .find((value): value is number => typeof value === 'number' && value > 0)
      ?? groupedFiles.length;
    const hasMultipleFiles = Math.max(groupedFiles.length, expectedAttachmentCount) > 1;
    const hasSeparateTextMessage = orderedBatchMessages.some((item) => item.files.length === 0 && Boolean(item.textContent?.trim()));
    const latestEditedAt = [...orderedBatchMessages]
      .reverse()
      .find((item) => item.editedAt !== null)?.editedAt ?? null;
    const groupedClientReadAt = orderedBatchMessages.every((item) => item.clientReadAt !== null)
      ? [...orderedBatchMessages].reverse().find((item) => item.clientReadAt !== null)?.clientReadAt ?? null
      : null;
    const groupedOperatorReadAt = orderedBatchMessages.every((item) => item.operatorReadAt !== null)
      ? [...orderedBatchMessages].reverse().find((item) => item.operatorReadAt !== null)?.operatorReadAt ?? null
      : null;

    displayedMessages.push({
      ...statusMessage,
      messageId: groupedMessageIds[0],
      textContent: groupedText,
      editedAt: latestEditedAt,
      clientReadAt: groupedClientReadAt,
      operatorReadAt: groupedOperatorReadAt,
      files: groupedFiles,
      sourceMessages: orderedBatchMessages,
      groupedMessageIds,
      renderTextBelowAttachments: (hasMultipleFiles || hasSeparateTextMessage) && groupedFiles.length > 0 && groupedText !== null
    });
  }

  return displayedMessages;
}

function isImageFile(file: OperatorChatMessageFile): boolean {
  return file.fileType === 'photo'
    || file.fileType === 'sticker'
    || Boolean(file.mimeType?.startsWith('image/'));
}

function isAudioFile(file: OperatorChatMessageFile): boolean {
  return file.fileType === 'audio'
    || file.fileType === 'voice'
    || Boolean(file.mimeType?.startsWith('audio/'))
    || AUDIO_FILE_EXTENSIONS.has(getAttachmentFileExtension(file));
}

function isVideoFile(file: OperatorChatMessageFile): boolean {
  return file.fileType === 'video'
    || file.fileType === 'video_note'
    || file.fileType === 'animation'
    || Boolean(file.mimeType?.startsWith('video/'));
}

function isPdfFile(file: OperatorChatMessageFile): boolean {
  return file.mimeType?.split(';')[0]?.trim().toLowerCase() === 'application/pdf'
    || getAttachmentFileExtension(file) === 'pdf';
}

function supportsStructuredDocumentPreview(file: OperatorChatMessageFile): boolean {
  const normalizedMimeType = file.mimeType?.split(';')[0]?.trim().toLowerCase() ?? '';
  const fileExtension = getAttachmentFileExtension(file);

  return STRUCTURED_DOCUMENT_PREVIEW_EXTENSIONS.has(fileExtension)
    || STRUCTURED_DOCUMENT_PREVIEW_MIME_TYPES.has(normalizedMimeType);
}

function isDocxFile(file: OperatorChatMessageFile): boolean {
  const normalizedMimeType = file.mimeType?.split(';')[0]?.trim().toLowerCase() ?? '';
  return getAttachmentFileExtension(file) === 'docx'
    || normalizedMimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}

function isPreviewableFile(file: OperatorChatMessageFile): boolean {
  return isImageFile(file) || isAudioFile(file) || isVideoFile(file);
}

function shouldRenderAttachmentAsFileCard(file: OperatorChatMessageFile): boolean {
  return true;
}

function buildAttachmentMetaLabel(file: OperatorChatMessageFile): string {
  const fileSizeLabel = formatFileSize(file.fileSize);
  const fileSizeMetaLabel = fileSizeLabel ? `Размер: ${fileSizeLabel}` : null;
  const durationLabel = formatDuration(file.durationSeconds);

  return [fileSizeMetaLabel, durationLabel].filter(Boolean).join(' • ');
}

function getFilePreviewMode(file: OperatorChatMessageFile): 'image' | 'video' | 'audio' | 'document' {
  if (isImageFile(file)) {
    return 'image';
  }

  if (isVideoFile(file)) {
    return 'video';
  }

  if (isAudioFile(file)) {
    return 'audio';
  }

  return 'document';
}

function buildDocumentPreviewHtmlFrame(html: string): string {
  return [
    '<!doctype html>',
    '<html lang="ru">',
    '<head>',
    '<meta charset="utf-8" />',
    '<meta name="viewport" content="width=device-width, initial-scale=1" />',
    '<style>',
    'html, body { margin: 0; padding: 0; background: #eef1f7; color: #1f2430; }',
    'body { font: 400 16px/1.65 Georgia, "Times New Roman", serif; }',
    '.page { box-sizing: border-box; width: min(920px, 100%); min-height: 100vh; margin: 0 auto; padding: 40px 48px 56px; background: #ffffff; box-shadow: 0 20px 44px rgba(15, 23, 42, 0.16); }',
    'p { margin: 0 0 1em; }',
    'h1, h2, h3, h4, h5, h6 { margin: 0 0 0.75em; line-height: 1.25; color: #18202d; }',
    'ul, ol { margin: 0 0 1em 1.4em; }',
    'table { width: 100%; border-collapse: collapse; margin: 0 0 1.25em; }',
    'td, th { border: 1px solid #d6dbe6; padding: 8px 10px; vertical-align: top; }',
    'img { max-width: 100%; height: auto; }',
    'a { color: #355edb; }',
    '@media (max-width: 760px) { .page { padding: 22px 18px 28px; box-shadow: none; } }',
    '</style>',
    '</head>',
    '<body>',
    '<main class="page">',
    html,
    '</main>',
    '</body>',
    '</html>'
  ].join('');
}

function buildMessageFallback(message: OperatorChatMessage): string | null {
  const attachmentStorageWarning = getAttachmentStorageWarning(message);

  if (attachmentStorageWarning) {
    return attachmentStorageWarning;
  }

  if (message.files.length > 0) {
    return null;
  }

  switch (message.messageType) {
    case 'photo':
      return message.direction === 'outgoing' ? 'Изображение отправлено' : 'Изображение недоступно';
    case 'document':
      return message.direction === 'outgoing' ? 'Документ отправлен' : 'Документ недоступен';
    case 'audio':
      return message.direction === 'outgoing' ? 'Аудиофайл отправлен' : 'Аудиофайл недоступен';
    case 'voice':
      return message.direction === 'outgoing' ? 'Голосовое сообщение отправлено' : 'Голосовое сообщение недоступно';
    case 'video':
    case 'video_note':
      return message.direction === 'outgoing' ? 'Видеофайл отправлен' : 'Видеофайл недоступен';
    case 'animation':
      return message.direction === 'outgoing' ? 'Анимация отправлена' : 'Анимация недоступна';
    case 'contact':
      return 'Контакт';
    case 'location':
      return 'Геопозиция';
    case 'share':
      return 'Ссылка';
    case 'sticker':
      return 'Стикер';
    case 'unknown':
      return 'Сообщение без текстового описания';
    default:
      return null;
  }
}

function getAttachmentStorageWarning(message: OperatorChatMessage): string | null {
  const attachmentStorage = getRecord(getRecord(message.metadata)?.attachmentStorage);
  const status = getRecordString(attachmentStorage, 'status');

  if (status !== 'failed' && status !== 'partial') {
    return null;
  }

  const rawFailures = attachmentStorage?.failures;
  const failures = Array.isArray(rawFailures)
    ? rawFailures.map((failure) => getRecord(failure)).filter((failure): failure is Record<string, unknown> => failure !== null)
    : [];
  const failureCodes = new Set(failures.map((failure) => getRecordString(failure, 'code')).filter(Boolean));
  const maxFileSizeLabel = formatFileSize(getRecordNumber(attachmentStorage, 'maxFileSizeBytes'));

  if (status === 'partial') {
    return 'Часть вложений не сохранена. Попросите клиента повторно отправить отсутствующие файлы.';
  }

  if (failureCodes.has('size_limit')) {
    return `Вложение не сохранено: размер превышает допустимый лимит${maxFileSizeLabel ? ` ${maxFileSizeLabel}` : ''}. Попросите клиента уменьшить файл и отправить его повторно.`;
  }

  if (failureCodes.has('source_unavailable')) {
    return 'Вложение недоступно: платформа не передала файл для загрузки. Попросите клиента отправить его повторно.';
  }

  return 'Вложение не сохранено из-за ошибки загрузки. Попросите клиента отправить его повторно.';
}

function isOperatorReminderMessage(message: OperatorChatMessage): boolean {
  return message.messageRole === 'reminder' && typeof message.reminderId === 'number' && message.reminderId > 0;
}

function getReminderCreatorName(message: OperatorChatMessage): string | null {
  if (!isOperatorReminderMessage(message)) {
    return null;
  }

  return getRecordString(getRecord(message.metadata), 'createdByOperatorName');
}

/** Контейнер главной страницы сервиса уведомлений. */
export const MainContainer = () => {
  const [authViewState, setAuthViewState] = useState<AuthViewState>('booting');
  const [authSession, setAuthSession] = useState<AuthSuccessResponse | null>(null);
  // Тема хранится отдельно для каждого оператора, но до входа использует общий fallback браузера.
  const { theme, setTheme: setThemePreference } = useTheme(authSession?.operator.loginId ?? null);
  const [loginFormState, setLoginFormState] = useState<LoginFormState>(initialLoginFormState);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isReleaseUpdateModalOpen, setIsReleaseUpdateModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [loginPending, setLoginPending] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [activeSidebarSection, setActiveSidebarSection] = useState<SidebarSectionId>(() => getStoredSidebarSection());
  const [aiAutoresponderSettings, setAiAutoresponderSettings] = useState<AiAutoresponderSettings | null>(null);
  const [isAiAutoresponderSettingsLoading, setIsAiAutoresponderSettingsLoading] = useState(false);
  const [aiAutoresponderUpdatePending, setAiAutoresponderUpdatePending] = useState(false);
  const [chatAiAutoresponderUpdatePendingIds, setChatAiAutoresponderUpdatePendingIds] = useState<number[]>([]);
  const [isUnreadFilterEnabled, setIsUnreadFilterEnabled] = useState(false);
  const [isUisFilterPanelOpen, setIsUisFilterPanelOpen] = useState(false);
  const [uisActiveFilter, setUisActiveFilter] = useState<UisCallFilter>('all');
  const [uisFilterOptions, setUisFilterOptions] = useState<UisFilterOption[]>([]);
  const [uisRefreshRequestVersion, setUisRefreshRequestVersion] = useState(0);
  const [chatSearch, setChatSearch] = useState('');
  const [chatDraft, setChatDraft] = useState('');
  const [composerFiles, setComposerFiles] = useState<File[]>([]);
  const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
  const [isConversationDragActive, setIsConversationDragActive] = useState(false);
  const [chats, setChats] = useState<OperatorChatSummary[]>([]);
  const [widgetChats, setWidgetChats] = useState<WidgetChatSummary[]>([]);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [activeWidgetChatId, setActiveWidgetChatId] = useState<number | null>(() => getRequestedWidgetChatIdFromLocation());
  const [activeUisGroup, setActiveUisGroup] = useState<UisCallGroup | null>(null);
  const [requestedUisGroupId, setRequestedUisGroupId] = useState<string | null>(() => getRequestedUisGroupIdFromLocation());
  const [requestedChatId, setRequestedChatId] = useState<number | null>(() => getRequestedChatIdFromLocation());
  const [activeChatMessages, setActiveChatMessages] = useState<OperatorChatMessage[]>([]);
  const [activeWidgetChatMessages, setActiveWidgetChatMessages] = useState<OperatorChatMessage[]>([]);
  const [isChatsLoading, setIsChatsLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isWidgetChatsLoading, setIsWidgetChatsLoading] = useState(false);
  const [isWidgetMessagesLoading, setIsWidgetMessagesLoading] = useState(false);
  const [sendMessagePending, setSendMessagePending] = useState(false);
  const [createWidgetLinkPending, setCreateWidgetLinkPending] = useState(false);
  const [widgetLinkChatId, setWidgetLinkChatId] = useState('');
  const [deletingMessageId, setDeletingMessageId] = useState<number | null>(null);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState<DisplayedChatMessage | null>(null);
  const [readingMessageId, setReadingMessageId] = useState<number | null>(null);
  const [isQuickReplyModalOpen, setIsQuickReplyModalOpen] = useState(false);
  const [selectedQuickReplyId, setSelectedQuickReplyId] = useState<string>(DEFAULT_QUICK_REPLY_ID);
  const [quickReplyCoursePrograms, setQuickReplyCoursePrograms] = useState<ChatCourseProgramOption[]>([]);
  const [isQuickReplyCourseProgramsLoading, setIsQuickReplyCourseProgramsLoading] = useState(false);
  const [quickReplyCourseProgramsError, setQuickReplyCourseProgramsError] = useState('');
  const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false);
  const [avatarLoadAttempts, setAvatarLoadAttempts] = useState<Record<string, AvatarLoadAttempt>>({});
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [reminderFormState, setReminderFormState] = useState<ReminderFormState>(initialReminderFormState);
  const [createReminderPending, setCreateReminderPending] = useState(false);
  const [isChatPhoneModalOpen, setIsChatPhoneModalOpen] = useState(false);
  const [chatPhoneCountry, setChatPhoneCountry] = useState<PhoneCountryCode>(DEFAULT_CHAT_PHONE_COUNTRY);
  const [chatPhoneInput, setChatPhoneInput] = useState('');
  const [chatPhoneError, setChatPhoneError] = useState('');
  const [isChatPhoneCountryMenuOpen, setIsChatPhoneCountryMenuOpen] = useState(false);
  const [isApplyingChatPhone, setIsApplyingChatPhone] = useState(false);
  const [demoAccessDialogMode, setDemoAccessDialogMode] = useState<DemoAccessDialogMode | null>(null);
  const [isGrantingChatDemoAccess, setIsGrantingChatDemoAccess] = useState(false);
  const [isCourseActionPopoverOpen, setIsCourseActionPopoverOpen] = useState(false);
  const [isCourseSelectionModalOpen, setIsCourseSelectionModalOpen] = useState(false);
  const [courseCatalogOptions, setCourseCatalogOptions] = useState<ChatCourseCatalogCourse[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const [courseSelectionError, setCourseSelectionError] = useState('');
  const [isCourseSelectionLoading, setIsCourseSelectionLoading] = useState(false);
  const [isCourseProgramModalOpen, setIsCourseProgramModalOpen] = useState(false);
  const [courseProgramOptions, setCourseProgramOptions] = useState<ChatCourseProgramOption[]>([]);
  const [selectedCourseProgramId, setSelectedCourseProgramId] = useState('');
  const [courseProgramSelectionError, setCourseProgramSelectionError] = useState('');
  const [isCourseProgramLoading, setIsCourseProgramLoading] = useState(false);
  const [isApplyingChatCourseProgram, setIsApplyingChatCourseProgram] = useState(false);
  const [courseProgramCourseName, setCourseProgramCourseName] = useState<string | null>(null);
  const [isCourseAttachmentModalOpen, setIsCourseAttachmentModalOpen] = useState(false);
  const [courseAttachmentFiles, setCourseAttachmentFiles] = useState<ChatCourseAttachmentFile[]>([]);
  const [widgetAttachmentCourse, setWidgetAttachmentCourse] = useState<ChatCourseCatalogCourse | null>(null);
  const [selectedCourseAttachmentIds, setSelectedCourseAttachmentIds] = useState<string[]>([]);
  const [courseAttachmentError, setCourseAttachmentError] = useState('');
  const [isCourseAttachmentFilesLoading, setIsCourseAttachmentFilesLoading] = useState(false);
  const [isAttachingCourseFiles, setIsAttachingCourseFiles] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState<number | null>(null);
  const [dismissingReminderId, setDismissingReminderId] = useState<number | null>(null);
  const [activeFilePreview, setActiveFilePreview] = useState<FilePreviewModalState | null>(null);
  const [activeAvatarPreview, setActiveAvatarPreview] = useState<AvatarPreviewState | null>(null);
  const [filePreviewUrls, setFilePreviewUrls] = useState<Record<string, string>>({});
  const [audioPlaybackStates, setAudioPlaybackStates] = useState<Record<string, AudioPlaybackState>>({});
  const [isChatHistoryModalOpen, setIsChatHistoryModalOpen] = useState(false);
  const [activeChatHistoryEntries, setActiveChatHistoryEntries] = useState<OperatorChatHistoryEntry[]>([]);
  const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(false);
  const chatThreads = buildChatThreads(chats);
  const activeChat = findChatThreadByChatId(chatThreads, activeChatId);
  const activeWidgetChat = widgetChats.find((chat) => chat.chatId === activeWidgetChatId) ?? null;
  const composerTargetChatId = activeSidebarSection === 'wds' ? activeWidgetChatId : activeSidebarSection === 'chats' ? activeChatId : null;
  const composerApiScope = activeSidebarSection === 'wds' ? 'wds/chats' : 'chats';
  const composerTargetKey = `${activeSidebarSection}:${composerTargetChatId}`;
  const composerTargetKeyRef = useRef(composerTargetKey);
  composerTargetKeyRef.current = composerTargetKey;
  const composerContextChat = activeSidebarSection === 'wds'
    ? chats.find((chat) => chat.courseProgramId !== null && activeWidgetChat?.linkedChats.some((linked) => linked.chatId === chat.chatId))
      ?? chats.find((chat) => activeWidgetChat?.linkedChats.some((linked) => linked.chatId === chat.chatId)) ?? null
    : activeChat;
  const composerCourseSourceId = composerContextChat?.chatId ?? null;
  const composerCourseProgramId = composerContextChat?.courseProgramId ?? null;
  const hasComposerAttachmentMenu = activeSidebarSection === 'wds' || composerCourseProgramId !== null;
  const composerCourseDisplayName = getChatCourseDisplayName(composerContextChat);
  const quickReplyChat: QuickReplyChat | null = composerContextChat ?? (activeSidebarSection === 'wds' && activeWidgetChat ? {
    title: '', courseName: null, courseTitle: null, courseProgramId: null, programName: null, volumeHours: null, startEducationDate: null
  } : null);
  const activeChatThreadKey = activeChat?.chatIds.join(':') ?? '';
  const activeChatCourseDisplayName = getChatCourseDisplayName(activeChat);
  const hasActiveChatDemoAccessPrerequisites = Boolean(
    activeChat
    && normalizePhoneNumber(activeChat.phoneNumber)
    && normalizeCourseOptionLabel(activeChatCourseDisplayName)
    && activeChat.courseProgramId !== null
  );
  const selectedQuickReply = QUICK_REPLY_DEFINITIONS.find((reply) => reply.id === selectedQuickReplyId) ?? QUICK_REPLY_DEFINITIONS[0] ?? null;
  const quickReplyBuildContext: QuickReplyBuildContext = {
    coursePrograms: quickReplyCoursePrograms,
    courseProgramsError: quickReplyCourseProgramsError,
    courseProgramsLoading: isQuickReplyCourseProgramsLoading
  };
  const selectedQuickReplyText = quickReplyChat && selectedQuickReply ? selectedQuickReply.buildMessage(quickReplyChat, quickReplyBuildContext) : null;
  const displayedActiveChatMessages = buildDisplayedChatMessages(activeChatMessages);
  const displayedActiveWidgetChatMessages = buildDisplayedChatMessages(activeWidgetChatMessages);
  const stageBackground = buildStageBackground(theme);

  const refreshRequestRef = useRef<Promise<boolean> | null>(null);
  const refreshSessionRef = useRef<(showExpiredMessage: boolean) => Promise<boolean>>(async () => false);
  const authSessionRef = useRef<AuthSuccessResponse | null>(null);
  const notificationIdRef = useRef(0);
  const notificationTimerIdsRef = useRef<Record<number, number>>({});
  const releaseUpdateReminderTimeoutIdRef = useRef<number | null>(null);
  const releaseUpdateDetectedRef = useRef(false);
  const releaseCheckInFlightRef = useRef(false);
  const releaseMetadataSnapshotRef = useRef<ReleaseMetadataSnapshot | null>(null);
  const releaseAssetSnapshotRef = useRef<ReleaseAssetSnapshot | null>(null);
  const activeChatListRowRef = useRef<HTMLDivElement | null>(null);
  const messageViewportRef = useRef<HTMLDivElement | null>(null);
  const composerFileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentMenuRef = useRef<HTMLDivElement | null>(null);
  const courseAttachmentRequestRef = useRef(0);
  const courseSelectionRequestRef = useRef(0);
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const chatPhoneInputRef = useRef<HTMLInputElement | null>(null);
  const chatPhoneCountryMenuRef = useRef<HTMLDivElement | null>(null);
  const courseActionPopoverRef = useRef<HTMLDivElement | null>(null);
  const reminderDateInputRef = useRef<HTMLInputElement | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const activeChatIdRef = useRef<number | null>(null);
  const activeWidgetChatIdRef = useRef(activeWidgetChatId);
  activeWidgetChatIdRef.current = activeWidgetChatId;
  const requestedChatIdRef = useRef<number | null>(requestedChatId);
  const notificationAudioElementRef = useRef<HTMLAudioElement | null>(null);
  const playIncomingMessageAlertRef = useRef<((variant?: IncomingMessageAlertVariant) => Promise<void>) | null>(null);
  const incomingMessageEventSourceRef = useRef<EventSource | null>(null);
  const incomingMessageRefreshRequestRef = useRef<Promise<void> | null>(null);
  const pendingIncomingMessageRefreshChatIdsRef = useRef<Set<number>>(new Set());
  const conversationDragDepthRef = useRef(0);
  const chatsSnapshotRef = useRef<OperatorChatSummary[]>([]);
  const hasHydratedChatsRef = useRef(false);
  const widgetChatsSnapshotRef = useRef<WidgetChatSummary[]>([]);
  const hasHydratedWidgetChatsRef = useRef(false);
  const activeChatMessagesSnapshotRef = useRef<OperatorChatMessage[]>([]);
  const hasHydratedActiveChatMessagesRef = useRef(false);
  const pendingIncomingMessageAlertRef = useRef(false);
  const hasShownAudioUnlockHintRef = useRef(false);
  const hasRequestedSystemNotificationPermissionRef = useRef(false);
  const hasShownSystemNotificationHintRef = useRef(false);
  const lastIncomingMessageAlertAtRef = useRef(0);
  const lastSystemNotificationAtRef = useRef(0);
  const filePreviewUrlsRef = useRef<Record<string, string>>({});
  const filePreviewBlobCacheRef = useRef<Record<string, Blob>>({});
  const fileDocumentPreviewCacheRef = useRef<Record<string, FileDocumentPreview>>({});
  const failedPreviewFileIdsRef = useRef<Set<string>>(new Set());
  const audioElementRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const showNotificationRef = useRef<(config: ToastConfig) => void>(() => undefined);
  const fetchAuthorizedJsonRef = useRef<AuthorizedJsonRequest>(fetchAuthorizedJsonUnavailable);
  const fetchAuthorizedBlobRef = useRef<(path: string) => Promise<Blob>>(async () => new Blob());
  const isDemoAccessDialogOpen = demoAccessDialogMode !== null;
  const isDeleteConfirmOpen = deleteConfirmMessage !== null;
  const isFilePreviewOpen = activeFilePreview !== null;
  const isAvatarPreviewOpen = activeAvatarPreview !== null;

  const clearFilePreviewUrls = () => {
    Object.values(filePreviewUrlsRef.current).forEach((url) => {
      URL.revokeObjectURL(url);
    });

    filePreviewUrlsRef.current = {};
    filePreviewBlobCacheRef.current = {};
    fileDocumentPreviewCacheRef.current = {};
    failedPreviewFileIdsRef.current.clear();
    setFilePreviewUrls({});
  };

  const clearReleaseUpdateReminder = () => {
    if (releaseUpdateReminderTimeoutIdRef.current !== null) {
      window.clearTimeout(releaseUpdateReminderTimeoutIdRef.current);
      releaseUpdateReminderTimeoutIdRef.current = null;
    }
  };

  const openReleaseUpdateModal = () => {
    clearReleaseUpdateReminder();
    setIsReleaseUpdateModalOpen(true);
  };

  const resetChatPhoneModal = () => {
    setIsChatPhoneModalOpen(false);
    setChatPhoneCountry(DEFAULT_CHAT_PHONE_COUNTRY);
    setChatPhoneInput('');
    setChatPhoneError('');
    setIsChatPhoneCountryMenuOpen(false);
  };

  const resetCourseSelectionModal = () => {
    courseSelectionRequestRef.current += 1;
    setIsCourseSelectionModalOpen(false);
    setCourseCatalogOptions([]);
    setSelectedCourseId('');
    setCourseSearchQuery('');
    setCourseSelectionError('');
    setIsCourseSelectionLoading(false);
  };

  const resetCourseProgramModal = () => {
    setIsCourseProgramModalOpen(false);
    setCourseProgramOptions([]);
    setSelectedCourseProgramId('');
    setCourseProgramSelectionError('');
    setIsCourseProgramLoading(false);
    setCourseProgramCourseName(null);
  };

  const resetCourseAttachmentModal = () => {
    courseAttachmentRequestRef.current += 1;
    setIsCourseAttachmentModalOpen(false);
    setCourseAttachmentFiles([]);
    setWidgetAttachmentCourse(null);
    setSelectedCourseAttachmentIds([]);
    setCourseAttachmentError('');
    setIsCourseAttachmentFilesLoading(false);
    setIsAttachingCourseFiles(false);
  };

  const remindAboutReleaseUpdateLater = () => {
    if (!releaseUpdateDetectedRef.current) {
      setIsReleaseUpdateModalOpen(false);
      return;
    }

    clearReleaseUpdateReminder();
    setIsReleaseUpdateModalOpen(false);

    releaseUpdateReminderTimeoutIdRef.current = window.setTimeout(() => {
      releaseUpdateReminderTimeoutIdRef.current = null;

      if (releaseUpdateDetectedRef.current) {
        setIsReleaseUpdateModalOpen(true);
      }
    }, RELEASE_UPDATE_REMINDER_DELAY_MS);
  };

  const applyReleaseUpdateNow = () => {
    clearReleaseUpdateReminder();

    if (typeof window === 'undefined') {
      return;
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('releaseRefresh', String(Date.now()));
    window.location.replace(nextUrl.toString());
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const closeUisFilterPanel = useCallback(() => {
    setIsUisFilterPanelOpen(false);
  }, []);

  const resetComposerFiles = () => {
    setComposerFiles([]);

    if (composerFileInputRef.current) {
      composerFileInputRef.current.value = '';
    }
  };

  const closeLogoutConfirm = () => {
    if (logoutPending) {
      return;
    }

    setIsLogoutConfirmOpen(false);
  };

  const closeDeleteConfirm = () => {
    if (deletingMessageId !== null) {
      return;
    }

    setDeleteConfirmMessage(null);
  };

  const resetReminderForm = (nextState: ReminderFormState = initialReminderFormState) => {
    setReminderFormState(nextState);
  };

  const closeReminderModal = () => {
    if (createReminderPending) {
      return;
    }

    setIsReminderModalOpen(false);
    setEditingReminderId(null);
    resetReminderForm();
  };

  const closeChatPhoneModal = () => {
    if (isApplyingChatPhone) {
      return;
    }

    resetChatPhoneModal();
  };

  const closeDemoAccessDialog = () => {
    if (isGrantingChatDemoAccess) {
      return;
    }

    setDemoAccessDialogMode(null);
  };

  const closeCourseSelectionModal = () => {
    if (isCourseSelectionLoading) {
      return;
    }

    resetCourseSelectionModal();
  };

  const closeCourseProgramModal = () => {
    if (isCourseProgramLoading || isApplyingChatCourseProgram) {
      return;
    }

    resetCourseProgramModal();
  };

  const closeCourseAttachmentModal = () => {
    if (isCourseAttachmentFilesLoading || isAttachingCourseFiles) {
      return;
    }

    resetCourseAttachmentModal();
  };

  const closeFilePreview = () => {
    setActiveFilePreview(null);
  };

  const closeAvatarPreview = () => {
    setActiveAvatarPreview(null);
  };

  const openLogoutConfirm = () => {
    setIsMenuOpen(false);
    setIsLogoutConfirmOpen(true);
  };

  const openQuickReplyModal = () => {
    if (!composerTargetChatId || !quickReplyChat || sendMessagePending || QUICK_REPLY_DEFINITIONS.length === 0) {
      return;
    }

    setSelectedQuickReplyId((current) => QUICK_REPLY_DEFINITIONS.some((reply) => reply.id === current) ? current : DEFAULT_QUICK_REPLY_ID);
    setIsQuickReplyModalOpen(true);
  };

  const openChatPhoneModal = () => {
    if (activeSidebarSection !== 'chats' || !activeChatId || !activeChat || Boolean(activeChat.phoneNumber) || isApplyingChatPhone) {
      return;
    }

    setChatPhoneCountry(DEFAULT_CHAT_PHONE_COUNTRY);
    setChatPhoneInput('');
    setChatPhoneError('');
    setIsChatPhoneCountryMenuOpen(false);
    setIsChatPhoneModalOpen(true);

    window.requestAnimationFrame(() => {
      chatPhoneInputRef.current?.focus();
    });
  };

  const handleDemoAccessBadgeClick = () => {
    if (activeSidebarSection !== 'chats' || !activeChatId || !activeChat || activeChat.demoAccessGranted || isGrantingChatDemoAccess) {
      return;
    }

    setIsCourseActionPopoverOpen(false);
    setDemoAccessDialogMode(hasActiveChatDemoAccessPrerequisites ? 'confirm' : 'requirements');
  };

  const closeQuickReplyModal = () => {
    if (sendMessagePending) {
      return;
    }

    setIsQuickReplyModalOpen(false);
  };

  const handleQuickReplySelectionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedQuickReplyId(event.target.value);
  };

  const handleQuickReplyInsert = () => {
    if (!selectedQuickReply || !selectedQuickReplyText) {
      showNotification({
        tone: 'error',
        title: 'Быстрый ответ не выбран',
        message: 'Выберите один быстрый ответ перед подстановкой в поле сообщения.'
      });
      return;
    }

    setChatDraft((current) => {
      if (!current.trim()) {
        return selectedQuickReplyText;
      }

      return `${current.replace(/\s*$/, '')}\n\n${selectedQuickReplyText}`;
    });
    setIsQuickReplyModalOpen(false);

    window.requestAnimationFrame(() => {
      const textarea = composerTextareaRef.current;

      if (!textarea) {
        return;
      }

      textarea.focus();
      const endPosition = textarea.value.length;
      textarea.setSelectionRange(endPosition, endPosition);
    });
  };

  const openReminderModal = () => {
    if (composerTargetChatId === null) {
      return;
    }

    setEditingReminderId(null);
    setIsReminderModalOpen(true);
    resetReminderForm({
      remindAt: buildDefaultReminderDateTimeValue(),
      comment: ''
    });

    window.requestAnimationFrame(() => {
      reminderDateInputRef.current?.focus();
    });
  };

  const openReminderEditModal = (message: OperatorChatMessage) => {
    if (composerTargetChatId === null || !isOperatorReminderMessage(message)) {
      return;
    }

    if (message.isReminderTriggered || message.isReminderDismissed || typeof message.reminderId !== 'number') {
      return;
    }

    setEditingReminderId(message.reminderId);
    setIsReminderModalOpen(true);
    resetReminderForm({
      remindAt: buildReminderFormDateTimeValue(message.reminderAt ?? message.sentAt),
      comment: message.textContent ?? ''
    });

    window.requestAnimationFrame(() => {
      reminderDateInputRef.current?.focus();
    });
  };

  const handleReminderFieldChange = (field: keyof ReminderFormState) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setReminderFormState((current) => ({
      ...current,
      [field]: event.target.value
    }));
  };

  const handleChatPhoneInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const maskResult = phoneMask(event.target.value, chatPhoneCountry);

    setChatPhoneInput(maskResult.data);
    setChatPhoneError('');
  };

  const handleChatPhoneCountryChange = (phoneCountry: PhoneCountryCode) => {
    setChatPhoneCountry(phoneCountry);
    setChatPhoneInput(phoneMask(chatPhoneInput, phoneCountry).data);
    setChatPhoneError('');
    setIsChatPhoneCountryMenuOpen(false);

    window.requestAnimationFrame(() => {
      chatPhoneInputRef.current?.focus();
    });
  };

  const handleReminderDatePickerClick = () => {
    const input = reminderDateInputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;

    input?.showPicker?.();
    input?.focus();
  };

  const dismissNotification = (id: number) => {
    const timerId = notificationTimerIdsRef.current[id];

    if (timerId) {
      window.clearTimeout(timerId);
      delete notificationTimerIdsRef.current[id];
    }

    setNotifications((current) => current.filter((notification) => notification.id !== id));
  };

  const showNotification = ({
    tone,
    title,
    message,
    durationMs
  }: ToastConfig) => {
    const id = ++notificationIdRef.current;

    setNotifications((current) => [...current, { id, tone, title, message }]);

    notificationTimerIdsRef.current[id] = window.setTimeout(() => {
      dismissNotification(id);
    }, durationMs ?? (tone === 'error' ? 7000 : 5000));
  };

  showNotificationRef.current = showNotification;

  activeChatIdRef.current = activeChatId;
  requestedChatIdRef.current = requestedChatId;

  const getNotificationAudioElement = (): HTMLAudioElement | null => {
    if (typeof window === 'undefined') {
      return null;
    }

    const audioElement = notificationAudioElementRef.current ?? new Audio(incomingMessageAlertAudioUrl);
    audioElement.preload = 'auto';
    audioElement.volume = 0.9;
    notificationAudioElementRef.current = audioElement;

    return audioElement;
  };

  const requestSystemNotificationPermission = async (): Promise<NotificationPermission | null> => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return null;
    }

    const BrowserNotification = window.Notification;

    if (BrowserNotification.permission !== 'default') {
      return BrowserNotification.permission;
    }

    if (hasRequestedSystemNotificationPermissionRef.current) {
      return BrowserNotification.permission;
    }

    hasRequestedSystemNotificationPermissionRef.current = true;

    try {
      return await BrowserNotification.requestPermission();
    } catch {
      return BrowserNotification.permission;
    }
  };

  const showIncomingMessageSystemNotification = (payload: IncomingMessageStreamEvent) => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    const BrowserNotification = window.Notification;

    if (BrowserNotification.permission !== 'granted') {
      if (!hasShownSystemNotificationHintRef.current) {
        hasShownSystemNotificationHintRef.current = true;
        showNotificationRef.current({
          tone: 'info',
          title: 'Включите системные уведомления',
          message: 'Разрешите уведомления для сайта в браузере, чтобы новые сообщения всплывали в ОС.'
        });
      }

      return;
    }

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();

    if (now - lastSystemNotificationAtRef.current < SYSTEM_NOTIFICATION_MIN_INTERVAL_MS) {
      return;
    }

    lastSystemNotificationAtRef.current = now;

    try {
      const senderName = payload.senderName?.trim() || 'Клиент';
      const platformLabel = getPlatformBadgeLabel(payload.platform);
      const body = payload.preview?.trim() || 'Новое сообщение без текста';
      const systemNotification = new BrowserNotification(`${senderName} · ${platformLabel}`, {
        body,
        icon: systemNotificationIconUrl,
        tag: `incoming-message-${payload.platform}-${payload.chatId}`,
        renotify: true,
        silent: true
      });

      systemNotification.onclick = () => {
        window.focus();

        if (payload.platform === 'widget') {
          setActiveSidebarSection('wds');
          setActiveChatId(null);
          setActiveWidgetChatId(payload.chatId);
          syncSidebarSectionInLocation('wds');
          syncChatIdInLocation(null);
          syncWidgetChatIdInLocation(payload.chatId);
        } else {
          setActiveSidebarSection('chats');
          setActiveWidgetChatId(null);
          setActiveChatId(payload.chatId);
          syncSidebarSectionInLocation('chats');
          syncWidgetChatIdInLocation(null);
          syncChatIdInLocation(payload.chatId);
        }

        systemNotification.close();
      };

      window.setTimeout(() => systemNotification.close(), 12_000);
    } catch {
      return;
    }
  };

  const playIncomingMessageAlert = async (variant: IncomingMessageAlertVariant = 'default') => {
    const audioElement = getNotificationAudioElement();

    if (!audioElement) {
      return;
    }

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();

    if (now - lastIncomingMessageAlertAtRef.current < 650) {
      return;
    }

    lastIncomingMessageAlertAtRef.current = now;

    try {
      audioElement.pause();
      audioElement.currentTime = 0;
      audioElement.playbackRate = variant === 'whistle' ? 1.06 : 1;
      audioElement.volume = variant === 'whistle' ? 0.82 : 0.9;
      pendingIncomingMessageAlertRef.current = false;
      await audioElement.play();
    } catch {
      pendingIncomingMessageAlertRef.current = true;

      if (!hasShownAudioUnlockHintRef.current) {
        hasShownAudioUnlockHintRef.current = true;
        showNotificationRef.current({
          tone: 'info',
          title: 'Звук уведомлений ждёт подтверждения',
          message: 'Браузер заблокировал автозапуск звука. Кликните один раз в окне сервиса, и следующие уведомления будут слышны.'
        });
      }

      return;
    }
  };

  playIncomingMessageAlertRef.current = playIncomingMessageAlert;

  chatsSnapshotRef.current = chats;

  useEffect(() => {
    const timerRegistry = notificationTimerIdsRef.current;

    return () => {
      Object.values(timerRegistry).forEach((timerId) => {
        window.clearTimeout(timerId);
      });

      notificationAudioElementRef.current?.pause();
      notificationAudioElementRef.current = null;

      clearReleaseUpdateReminder();
      pendingIncomingMessageAlertRef.current = false;
      lastIncomingMessageAlertAtRef.current = 0;

      clearFilePreviewUrls();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production') {
      return;
    }

    if (authViewState !== 'authenticated') {
      clearReleaseUpdateReminder();
      releaseUpdateDetectedRef.current = false;
      releaseCheckInFlightRef.current = false;
      releaseMetadataSnapshotRef.current = null;
      releaseAssetSnapshotRef.current = null;
      setIsReleaseUpdateModalOpen(false);
      return;
    }

    releaseMetadataSnapshotRef.current = getCurrentReleaseMetadataSnapshot();
    releaseAssetSnapshotRef.current = getCurrentReleaseAssetSnapshot();

    let isDisposed = false;

    const checkForReleaseUpdate = async () => {
      if (isDisposed || releaseUpdateDetectedRef.current || releaseCheckInFlightRef.current) {
        return;
      }

      const currentMetadataSnapshot = releaseMetadataSnapshotRef.current ?? getCurrentReleaseMetadataSnapshot();
      const currentAssetSnapshot = releaseAssetSnapshotRef.current ?? getCurrentReleaseAssetSnapshot();

      releaseMetadataSnapshotRef.current = currentMetadataSnapshot;
      releaseAssetSnapshotRef.current = currentAssetSnapshot;

      if (
        !currentMetadataSnapshot.buildId
        && !currentMetadataSnapshot.builtAt
        && !currentAssetSnapshot.mainScriptPath
        && !currentAssetSnapshot.mainStylePath
      ) {
        return;
      }

      releaseCheckInFlightRef.current = true;

      try {
        const releaseProbeToken = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const releaseMetadataResponse = await fetch(buildReleaseApiProbeUrl('/api/release', releaseProbeToken), {
          cache: 'no-store',
          headers: {
            'cache-control': 'no-cache',
            pragma: 'no-cache'
          }
        });

        let isNewReleaseDetected = false;

        if (releaseMetadataResponse.ok) {
          const releaseMetadataPayload = await readJson<ReleaseMetadataPayload>(releaseMetadataResponse);

          if (releaseMetadataPayload) {
            const latestMetadataSnapshot = getReleaseMetadataSnapshot(releaseMetadataPayload);
            isNewReleaseDetected = hasNewReleaseMetadata(currentMetadataSnapshot, latestMetadataSnapshot);
          }
        }

        if (!isNewReleaseDetected) {
          const staticReleaseMetadataResponse = await fetch(buildReleaseProbeUrl('/release-meta.json', releaseProbeToken), {
            cache: 'no-store',
            headers: {
              'cache-control': 'no-cache',
              pragma: 'no-cache'
            }
          });

          if (staticReleaseMetadataResponse.ok) {
            const staticReleaseMetadataPayload = await staticReleaseMetadataResponse.json() as ReleaseMetadataPayload;
            const latestStaticMetadataSnapshot = getReleaseMetadataSnapshot(staticReleaseMetadataPayload);

            isNewReleaseDetected = hasNewReleaseMetadata(currentMetadataSnapshot, latestStaticMetadataSnapshot);
          }
        }

        if (!isNewReleaseDetected) {
          const manifestResponse = await fetch(buildReleaseProbeUrl('/asset-manifest.json', releaseProbeToken), {
            cache: 'no-store',
            headers: {
              'cache-control': 'no-cache',
              pragma: 'no-cache'
            }
          });

          if (manifestResponse.ok) {
            const manifestPayload = await manifestResponse.json() as ReleaseManifestPayload;
            const latestSnapshotFromManifest = getReleaseAssetSnapshotFromManifest(manifestPayload);

            isNewReleaseDetected = hasNewReleaseAssets(currentAssetSnapshot, latestSnapshotFromManifest);
          }
        }

        if (!isNewReleaseDetected) {
          const htmlResponse = await fetch(buildReleaseProbeUrl('/index.html', releaseProbeToken), {
            cache: 'no-store',
            headers: {
              'cache-control': 'no-cache',
              pragma: 'no-cache'
            }
          });

          if (htmlResponse.ok) {
            const htmlDocument = await htmlResponse.text();
            const latestSnapshotFromHtml = getReleaseAssetSnapshotFromHtml(htmlDocument);
            isNewReleaseDetected = hasNewReleaseAssets(currentAssetSnapshot, latestSnapshotFromHtml);
          }
        }

        if (isDisposed || !isNewReleaseDetected) {
          return;
        }

        releaseUpdateDetectedRef.current = true;
        openReleaseUpdateModal();
      } catch {
        return;
      } finally {
        releaseCheckInFlightRef.current = false;
      }
    };

    void checkForReleaseUpdate();

    const pollTimerId = window.setInterval(() => {
      void checkForReleaseUpdate();
    }, RELEASE_CHECK_POLL_INTERVAL_MS);

    return () => {
      isDisposed = true;
      releaseCheckInFlightRef.current = false;
      window.clearInterval(pollTimerId);
      clearReleaseUpdateReminder();
      releaseUpdateDetectedRef.current = false;
      releaseMetadataSnapshotRef.current = null;
      releaseAssetSnapshotRef.current = null;
      setIsReleaseUpdateModalOpen(false);
    };
  }, [authViewState]);

  useEffect(() => {
    if (authViewState !== 'authenticated' || typeof window === 'undefined') {
      return;
    }

    const unlockNotificationAudio = (shouldRequestSystemNotificationPermission = false) => {
      void (async () => {
        if (shouldRequestSystemNotificationPermission) {
          await requestSystemNotificationPermission();
        }

        getNotificationAudioElement()?.load();

        if (pendingIncomingMessageAlertRef.current) {
          void playIncomingMessageAlert();
        }
      })();
    };

    const handleNotificationUserActivation = () => {
      unlockNotificationAudio(true);
    };

    const handleNotificationWindowFocus = () => {
      unlockNotificationAudio();
    };

    const handleNotificationAudioVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        unlockNotificationAudio();
      }
    };

    window.addEventListener('pointerdown', handleNotificationUserActivation, { passive: true });
    window.addEventListener('keydown', handleNotificationUserActivation);
    window.addEventListener('focus', handleNotificationWindowFocus);
    document.addEventListener('visibilitychange', handleNotificationAudioVisibilityChange);

    return () => {
      window.removeEventListener('pointerdown', handleNotificationUserActivation);
      window.removeEventListener('keydown', handleNotificationUserActivation);
      window.removeEventListener('focus', handleNotificationWindowFocus);
      document.removeEventListener('visibilitychange', handleNotificationAudioVisibilityChange);
    };
  }, [authViewState]);

  useEffect(() => {
    const handlePopState = () => {
      setRequestedChatId(getRequestedChatIdFromLocation());
      setActiveSidebarSection(getStoredSidebarSection());
      setRequestedUisGroupId(getRequestedUisGroupIdFromLocation());
      setActiveWidgetChatId(getRequestedWidgetChatIdFromLocation());
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    persistSidebarSection(activeSidebarSection);

    if (authViewState === 'authenticated') {
      applyLocationParams({
        section: activeSidebarSection,
        wdsChatId: activeSidebarSection !== 'wds' ? null : undefined,
        uisGroupId: activeSidebarSection !== 'uis' ? null : undefined
      });
    }
  }, [activeSidebarSection, authViewState]);

  const applySession = (nextSession: AuthSuccessResponse) => {
    authSessionRef.current = nextSession;
    setAuthSession(nextSession);
    setAuthViewState('authenticated');
  };

  const resetSession = () => {
    authSessionRef.current = null;
    setIsMenuOpen(false);
    setIsLogoutConfirmOpen(false);
    setAuthSession(null);
    setAuthViewState('guest');
    setChatSearch('');
    setChatDraft('');
    resetComposerFiles();
    setAiAutoresponderSettings(null);
    setIsAiAutoresponderSettingsLoading(false);
    setAiAutoresponderUpdatePending(false);
    setChatAiAutoresponderUpdatePendingIds([]);
    setChats([]);
    setWidgetChats([]);
    setActiveChatId(null);
    setActiveWidgetChatId(null);
    setActiveUisGroup(null);
    setActiveChatMessages([]);
    setActiveWidgetChatMessages([]);
    setIsChatsLoading(false);
    setIsMessagesLoading(false);
    setIsWidgetChatsLoading(false);
    setIsWidgetMessagesLoading(false);
    setSendMessagePending(false);
    setCreateWidgetLinkPending(false);
    setWidgetLinkChatId('');
    setDeletingMessageId(null);
    setDeleteConfirmMessage(null);
    setReadingMessageId(null);
    resetChatPhoneModal();
    setIsApplyingChatPhone(false);
    setIsCourseActionPopoverOpen(false);
    resetCourseSelectionModal();
    resetCourseProgramModal();
    resetCourseAttachmentModal();
    setIsApplyingChatCourseProgram(false);
    setIsAttachmentMenuOpen(false);
    setIsQuickReplyModalOpen(false);
    setSelectedQuickReplyId(DEFAULT_QUICK_REPLY_ID);
    setAvatarLoadAttempts({});
    setActiveFilePreview(null);
    shouldStickToBottomRef.current = true;
    clearFilePreviewUrls();
  };

  useEffect(() => {
    if (!isMenuOpen && !isUisFilterPanelOpen && !isLogoutConfirmOpen && !isDeleteConfirmOpen && !isQuickReplyModalOpen && !isReminderModalOpen && !isChatPhoneModalOpen && !isCourseSelectionModalOpen && !isCourseProgramModalOpen && !isCourseAttachmentModalOpen && !isFilePreviewOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isFilePreviewOpen) {
          closeFilePreview();
          return;
        }

        if (isDeleteConfirmOpen && deletingMessageId === null) {
          setDeleteConfirmMessage(null);
          return;
        }

        if (isChatPhoneCountryMenuOpen) {
          setIsChatPhoneCountryMenuOpen(false);
          return;
        }

        if (isCourseActionPopoverOpen) {
          setIsCourseActionPopoverOpen(false);
          return;
        }

        if (isCourseProgramModalOpen && !isCourseProgramLoading && !isApplyingChatCourseProgram) {
          closeCourseProgramModal();
          return;
        }

        if (isCourseAttachmentModalOpen && !isCourseAttachmentFilesLoading && !isAttachingCourseFiles) {
          closeCourseAttachmentModal();
          return;
        }

        if (isCourseSelectionModalOpen && !isCourseSelectionLoading) {
          closeCourseSelectionModal();
          return;
        }

        if (isChatPhoneModalOpen && !isApplyingChatPhone) {
          closeChatPhoneModal();
          return;
        }

        if (isQuickReplyModalOpen && !sendMessagePending) {
          setIsQuickReplyModalOpen(false);
          return;
        }

        if (isReminderModalOpen && !createReminderPending) {
          closeReminderModal();
          return;
        }

        if (isLogoutConfirmOpen && !logoutPending) {
          setIsLogoutConfirmOpen(false);
          return;
        }

        if (isUisFilterPanelOpen) {
          closeUisFilterPanel();
          return;
        }

        if (isMenuOpen) {
          setIsMenuOpen(false);
        }
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeChatPhoneModal, closeCourseAttachmentModal, closeCourseProgramModal, closeCourseSelectionModal, closeUisFilterPanel, createReminderPending, deletingMessageId, isApplyingChatCourseProgram, isApplyingChatPhone, isAttachingCourseFiles, isChatPhoneCountryMenuOpen, isChatPhoneModalOpen, isCourseActionPopoverOpen, isCourseAttachmentFilesLoading, isCourseAttachmentModalOpen, isCourseProgramLoading, isCourseProgramModalOpen, isCourseSelectionLoading, isCourseSelectionModalOpen, isDeleteConfirmOpen, isFilePreviewOpen, isLogoutConfirmOpen, isMenuOpen, isQuickReplyModalOpen, isReminderModalOpen, isUisFilterPanelOpen, logoutPending, sendMessagePending]);

  const refreshSession = async (showExpiredMessage: boolean): Promise<boolean> => {
    if (refreshRequestRef.current) {
      return refreshRequestRef.current;
    }

    const requestPromise = (async () => {
      const hadActiveSession = authSessionRef.current !== null;

      try {
        const response = await requestSessionRefresh();
        const payload = await readJson<AuthSuccessResponse | ErrorResponse>(response);

        if (!response.ok || !payload || isErrorResponse(payload)) {
          const errorMessage = payload && isErrorResponse(payload)
            ? payload.error
            : 'Не удалось восстановить сессию оператора.';
          const shouldResetAuthenticatedUi = !hadActiveSession || response.status === 401 || response.status === 403;

          if (shouldResetAuthenticatedUi) {
            const terminalRefreshError = Object.assign(new Error(errorMessage), { terminalAuthFailure: true });
            throw terminalRefreshError;
          }

          return false;
        }

        applySession(payload);
        return true;
      } catch (error) {
        if (!hadActiveSession) {
          if (showExpiredMessage) {
            showNotification({
              tone: 'error',
              title: 'Сессия завершена',
              message: toErrorMessage(error, 'Сессия завершена. Выполните вход повторно.')
            });
          }

          resetSession();
          return false;
        }

        const isAuthTermination = typeof error === 'object'
          && error !== null
          && 'terminalAuthFailure' in error
          && Boolean((error as { terminalAuthFailure?: boolean }).terminalAuthFailure);

        if (isAuthTermination) {
          if (showExpiredMessage) {
            showNotification({
              tone: 'error',
              title: 'Сессия завершена',
              message: toErrorMessage(error, 'Сессия завершена. Выполните вход повторно.')
            });
          }

          resetSession();
          return false;
        }

        if (showExpiredMessage) {
          showNotification({
            tone: 'error',
            title: 'Связь с сервером нестабильна',
            message: 'Не удалось тихо обновить сессию, но вход пока сохранён. Попробуйте повторить действие ещё раз.'
          });
        }

        return false;
      } finally {
        refreshRequestRef.current = null;
      }
    })();

    refreshRequestRef.current = requestPromise;
    return requestPromise;
  };

  refreshSessionRef.current = refreshSession;

  useEffect(() => {
    void refreshSessionRef.current(false);
  }, []);

  useEffect(() => {
    if (authViewState !== 'authenticated' || !authSession?.accessTokenExpiresAt) {
      return;
    }

    const expiresAt = new Date(authSession.accessTokenExpiresAt).getTime();
    const refreshDelay = Math.max(expiresAt - Date.now() - ACCESS_TOKEN_REFRESH_LEAD_MS, 0);
    const timerId = window.setTimeout(() => {
      void refreshSessionRef.current(false);
    }, refreshDelay);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [authSession?.accessTokenExpiresAt, authViewState]);

  useEffect(() => {
    if (authViewState !== 'authenticated') {
      return;
    }

    const refreshIfNeeded = () => {
      const expiresAtValue = authSessionRef.current?.accessTokenExpiresAt;

      if (!expiresAtValue) {
        return;
      }

      const expiresAt = new Date(expiresAtValue).getTime();

      if (!Number.isFinite(expiresAt)) {
        return;
      }

      if (expiresAt - Date.now() <= ACCESS_TOKEN_RECHECK_THRESHOLD_MS) {
        void refreshSessionRef.current(false);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfNeeded();
      }
    };

    window.addEventListener('focus', refreshIfNeeded);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshIfNeeded);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authViewState, authSession?.accessTokenExpiresAt]);

  const buildAuthorizedHeaders = (headers?: HeadersInit): Headers => {
    const nextHeaders = new Headers(headers);
    const accessToken = authSessionRef.current?.accessToken;

    if (accessToken) {
      nextHeaders.set('Authorization', `Bearer ${accessToken}`);
    }

    return nextHeaders;
  };

  const performAuthorizedRequest = async (path: string, init: RequestInit = {}, allowRefresh = true): Promise<Response> => {
    const response = await fetch(buildApiUrl(path), {
      ...init,
      credentials: init.credentials ?? 'include',
      headers: buildAuthorizedHeaders(init.headers)
    });

    if (!allowRefresh || response.status !== 401 || !authSessionRef.current) {
      return response;
    }

    const restored = await refreshSession(true);

    if (!restored) {
      return response;
    }

    return fetch(buildApiUrl(path), {
      ...init,
      credentials: init.credentials ?? 'include',
      headers: buildAuthorizedHeaders(init.headers)
    });
  };

  const fetchAuthorizedJson = async <T,>(path: string): Promise<T> => {
    const response = await performAuthorizedRequest(path);
    const payload = await readJson<T | ErrorResponse>(response);

    if (!response.ok || !payload || isErrorResponse(payload)) {
      throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось загрузить данные сервиса.');
    }

    return payload;
  };

  fetchAuthorizedJsonRef.current = fetchAuthorizedJson;

  const fetchAuthorizedBlob = async (path: string): Promise<Blob> => {
    const response = await performAuthorizedRequest(path);

    if (!response.ok) {
      const payload = await readJson<ErrorResponse>(response);
      throw new Error(payload?.error ?? 'Не удалось загрузить файл.');
    }

    const blob = await response.blob();
    const contentType = response.headers.get('Content-Type')?.split(';')[0]?.trim();

    if (!contentType || blob.type === contentType) {
      return blob;
    }

    return blob.slice(0, blob.size, contentType);
  };

  fetchAuthorizedBlobRef.current = fetchAuthorizedBlob;

  const {
    overview: uisOverview,
    isLoading: isUisOverviewLoading,
    errorMessage: uisOverviewErrorMessage
  } = useUisOverviewData(fetchAuthorizedJson, uisRefreshRequestVersion, authViewState === 'authenticated');

  const uisCallGroups = useMemo(
    () => buildUisCallGroups(uisOverview?.calls ?? []),
    [uisOverview?.calls]
  );

  useEffect(() => {
    if (
      authViewState !== 'authenticated'
      || !isQuickReplyModalOpen
      || composerCourseSourceId === null
      || composerCourseProgramId === null
    ) {
      setQuickReplyCoursePrograms([]);
      setIsQuickReplyCourseProgramsLoading(false);
      setQuickReplyCourseProgramsError('');
      return;
    }

    let cancelled = false;
    setIsQuickReplyCourseProgramsLoading(true);
    setQuickReplyCourseProgramsError('');

    const loadQuickReplyCoursePrograms = async () => {
      try {
        const programs = await getChatCoursePrograms(fetchAuthorizedJsonRef.current, composerCourseSourceId);

        if (!cancelled) {
          setQuickReplyCoursePrograms(programs);
        }
      } catch (error) {
        if (!cancelled) {
          setQuickReplyCoursePrograms([]);
          setQuickReplyCourseProgramsError(toDisplayErrorMessage(error, 'Не удалось загрузить варианты программы для быстрого ответа.'));
        }
      } finally {
        if (!cancelled) {
          setIsQuickReplyCourseProgramsLoading(false);
        }
      }
    };

    void loadQuickReplyCoursePrograms();

    return () => {
      cancelled = true;
    };
  }, [composerCourseProgramId, composerCourseSourceId, authViewState, isQuickReplyModalOpen]);

  useEffect(() => {
    if (authViewState !== 'authenticated') {
      return;
    }

    if (uisOverview === null || isUisOverviewLoading) {
      return;
    }

    if (activeUisGroup !== null || requestedUisGroupId === null) {
      return;
    }

    const matchedGroup = uisCallGroups.find((group) => group.id === requestedUisGroupId) ?? null;

    if (matchedGroup) {
      setActiveUisGroup(matchedGroup);
    } else {
      setRequestedUisGroupId(null);
      syncUisGroupIdInLocation(null);
    }
  }, [authViewState, uisOverview, isUisOverviewLoading, uisCallGroups, activeUisGroup, requestedUisGroupId]);

  useEffect(() => {
    if (authViewState !== 'authenticated') {
      setAiAutoresponderSettings(null);
      setIsAiAutoresponderSettingsLoading(false);
      setAiAutoresponderUpdatePending(false);
      setChatAiAutoresponderUpdatePendingIds([]);
      return;
    }

    let cancelled = false;

    const syncAiAutoresponderSettings = async (silent: boolean) => {
      if (!silent) {
        setIsAiAutoresponderSettingsLoading(true);
      }

      try {
        const nextSettings = await getAiAutoresponderSettings(fetchAuthorizedJsonRef.current);

        if (!cancelled) {
          setAiAutoresponderSettings(nextSettings);
        }
      } catch (error) {
        if (!silent && !cancelled && authSessionRef.current !== null) {
          showNotificationRef.current({
            tone: 'error',
            title: 'Не удалось загрузить Автоответчик AI',
            message: toErrorMessage(error, 'Глобальная настройка автоответчика временно недоступна.')
          });
        }
      } finally {
        if (!silent && !cancelled) {
          setIsAiAutoresponderSettingsLoading(false);
        }
      }
    };

    void syncAiAutoresponderSettings(false);
    const intervalId = window.setInterval(() => {
      void syncAiAutoresponderSettings(true);
    }, AI_AUTORESPONDER_SETTINGS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [authViewState]);

  const refreshIncomingMessageState = async (chatId: number): Promise<void> => {
    pendingIncomingMessageRefreshChatIdsRef.current.add(chatId);

    if (incomingMessageRefreshRequestRef.current) {
      return incomingMessageRefreshRequestRef.current;
    }

    const requestPromise = (async () => {
      while (pendingIncomingMessageRefreshChatIdsRef.current.size > 0) {
        const activeChatIdValue = activeChatIdRef.current;
        const pendingChatIds = Array.from(pendingIncomingMessageRefreshChatIdsRef.current);

        pendingIncomingMessageRefreshChatIdsRef.current.clear();

        const nextChats = await getOperatorChats(fetchAuthorizedJsonRef.current);
        const nextChatThreads = buildChatThreads(nextChats);
        const activeThread = findChatThreadByChatId(nextChatThreads, activeChatIdValue);
        const shouldRefreshActiveChat = activeThread !== null && pendingChatIds.some((pendingChatId) => activeThread.chatIds.includes(pendingChatId));

        setChats(nextChats);
        chatsSnapshotRef.current = nextChats;
        hasHydratedChatsRef.current = true;

        if (shouldRefreshActiveChat && activeThread) {
          const nextMessages = await fetchChatThreadMessages(fetchAuthorizedJsonRef.current, activeThread.chatIds);

          if (resolveChatThreadId(nextChatThreads, activeChatIdRef.current) === activeThread.chatId) {
            setActiveChatMessages(nextMessages);
            activeChatMessagesSnapshotRef.current = nextMessages;
            hasHydratedActiveChatMessagesRef.current = true;
          }
        }
      }
    })().finally(() => {
      incomingMessageRefreshRequestRef.current = null;
    });

    incomingMessageRefreshRequestRef.current = requestPromise;
    return requestPromise;
  };

  const refreshChatCollections = async (chatId: number) => {
    const nextChats = await getOperatorChats(fetchAuthorizedJson);
    const nextChatThreads = buildChatThreads(nextChats);
    const activeThread = findChatThreadByChatId(nextChatThreads, activeChatIdRef.current) ?? findChatThreadByChatId(nextChatThreads, chatId);
    const nextMessages = activeThread
      ? await fetchChatThreadMessages(fetchAuthorizedJsonRef.current, activeThread.chatIds)
      : [];

    setChats(nextChats);
    chatsSnapshotRef.current = nextChats;
    hasHydratedChatsRef.current = true;

    if (activeThread && resolveChatThreadId(nextChatThreads, activeChatIdRef.current ?? chatId) === activeThread.chatId) {
      setActiveChatMessages(nextMessages);
      activeChatMessagesSnapshotRef.current = nextMessages;
      hasHydratedActiveChatMessagesRef.current = true;
    }
  };

  const refreshWidgetChatCollections = async (chatId: number) => {
    const nextWidgetChats = await getWidgetChats(fetchAuthorizedJson);
    const nextMessages = nextWidgetChats.some((chat) => chat.chatId === chatId)
      ? await getWidgetChatMessages(fetchAuthorizedJson, chatId)
      : [];

    setWidgetChats(nextWidgetChats);
    if (activeWidgetChatIdRef.current === chatId) {
      setActiveWidgetChatMessages(nextMessages);
    }
  };

  const handleAiAutoresponderToggle = async (nextEnabled: boolean) => {
    if (aiAutoresponderUpdatePending || isAiAutoresponderSettingsLoading) {
      return;
    }

    if ((aiAutoresponderSettings?.enabled ?? true) === nextEnabled) {
      return;
    }

    setAiAutoresponderUpdatePending(true);

    try {
      const response = await updateAiAutoresponderSettings(performAuthorizedRequest, {
        enabled: nextEnabled
      });
      const payload = await readJson<AiAutoresponderSettings | ErrorResponse>(response);

      if (!response.ok || !payload || isErrorResponse(payload)) {
        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось изменить настройку Автоответчика AI.');
      }

      setAiAutoresponderSettings(payload);
      showNotification({
        tone: 'success',
        title: nextEnabled ? 'Автоответчик AI включён' : 'Автоответчик AI выключен',
        message: 'Настройка применена для всего сервиса.'
      });
    } catch (error) {
      showNotification({
        tone: 'error',
        title: 'Не удалось изменить Автоответчик AI',
        message: toErrorMessage(error, 'Глобальная настройка не изменилась.')
      });
    } finally {
      setAiAutoresponderUpdatePending(false);
    }
  };

  const handleChatAiAutoresponderToggle = async (event: ChangeEvent<HTMLInputElement>, chat: ChatThreadSummary) => {
    event.stopPropagation();

    if (!isAiAutoresponderEnabled) {
      return;
    }

    const targetChatIds = chat.chatIds;

    if (targetChatIds.some((chatId) => chatAiAutoresponderUpdatePendingIds.includes(chatId))) {
      return;
    }

    const nextEnabled = event.target.checked;
    const previousChats = chatsSnapshotRef.current.length > 0 ? chatsSnapshotRef.current : chats;

    setChatAiAutoresponderUpdatePendingIds((current) => Array.from(new Set([...current, ...targetChatIds])));

    setChats((current) => {
      const nextChats = current.map((item) => (
        targetChatIds.includes(item.chatId)
          ? { ...item, aiAutoresponderEnabled: nextEnabled }
          : item
      ));

      chatsSnapshotRef.current = nextChats;
      return nextChats;
    });

    try {
      await Promise.all(targetChatIds.map(async (chatId) => {
        const response = await updateChatAiAutoresponderSettings(performAuthorizedRequest, chatId, {
          enabled: nextEnabled
        });
        const payload = await readJson<ApiMutationSuccess | ErrorResponse>(response);

        if (!response.ok || !payload || isErrorResponse(payload)) {
          throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось изменить настройку AI для чата.');
        }
      }));

      await refreshChatCollections(chat.chatId);

      showNotification({
        tone: 'success',
        title: nextEnabled ? 'AI включён для чата' : 'AI выключен для чата',
        message: nextEnabled
          ? 'Индивидуальная настройка сохранена. При включённом общем AI автоответчик снова будет работать в этом чате.'
          : 'Индивидуальная настройка сохранена. Общий переключатель AI не включит этот чат автоматически.'
      });
    } catch (error) {
      setChats(previousChats);
      chatsSnapshotRef.current = previousChats;
      showNotification({
        tone: 'error',
        title: 'Не удалось изменить AI для чата',
        message: toErrorMessage(error, 'Индивидуальная настройка чата не изменилась.')
      });
    } finally {
      setChatAiAutoresponderUpdatePendingIds((current) => current.filter((chatId) => !targetChatIds.includes(chatId)));
    }
  };

  useEffect(() => {
    if (authViewState !== 'authenticated' || !authSession?.accessToken || typeof window === 'undefined') {
      incomingMessageEventSourceRef.current?.close();
      incomingMessageEventSourceRef.current = null;
      pendingIncomingMessageRefreshChatIdsRef.current.clear();
      incomingMessageRefreshRequestRef.current = null;
      return;
    }

    const eventSource = new EventSource(buildIncomingMessageEventSourceUrl(authSession.accessToken), { withCredentials: true });
    incomingMessageEventSourceRef.current = eventSource;

    const handleIncomingMessageEvent = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data) as IncomingMessageStreamEvent;

        if (!payload || typeof payload.chatId !== 'number' || payload.chatId <= 0) {
          return;
        }

        showIncomingMessageSystemNotification(payload);
        void playIncomingMessageAlertRef.current?.();
        void refreshIncomingMessageState(payload.chatId);
      } catch {
        return;
      }
    };

    const handleEventSourceError = () => {
      if (eventSource.readyState === EventSource.CLOSED) {
        incomingMessageEventSourceRef.current = null;
        void refreshSessionRef.current(false);
      }
    };

    eventSource.addEventListener('incoming-message', handleIncomingMessageEvent as EventListener);
    eventSource.addEventListener('error', handleEventSourceError as EventListener);

    return () => {
      eventSource.removeEventListener('incoming-message', handleIncomingMessageEvent as EventListener);
      eventSource.removeEventListener('error', handleEventSourceError as EventListener);
      eventSource.close();

      if (incomingMessageEventSourceRef.current === eventSource) {
        incomingMessageEventSourceRef.current = null;
      }
    };
  }, [authSession?.accessToken, authViewState]);

  const handleCreateReminder = async () => {
    if (composerTargetChatId === null || createReminderPending) {
      return;
    }

    const reminderIdToUpdate = editingReminderId;
    const isEditingReminder = reminderIdToUpdate !== null;
    const remindAt = reminderFormState.remindAt.trim();
    const comment = reminderFormState.comment.trim();
    const remindAtDate = parseReminderDateTimeValue(remindAt);

    if (!remindAt) {
      showNotification({
        tone: 'error',
        title: 'Не указано время уведомления',
        message: 'Выберите дату и время, когда нужно поднять этот чат наверх.'
      });
      return;
    }

    if (!remindAtDate) {
      showNotification({
        tone: 'error',
        title: 'Некорректная дата уведомления',
        message: 'Проверьте дату и время уведомления и попробуйте снова.'
      });
      return;
    }

    if (remindAtDate.getTime() <= Date.now()) {
      showNotification({
        tone: 'error',
        title: isEditingReminder ? 'Нельзя обновить уведомление' : 'Нельзя создать уведомление',
        message: 'Дата и время уведомления уже прошли. Укажите будущее время.'
      });
      return;
    }

    setCreateReminderPending(true);

    try {
      const response = isEditingReminder
        ? await updateChatReminder(performAuthorizedRequest, composerTargetChatId, reminderIdToUpdate, {
            remindAt,
            comment
          }, composerApiScope)
        : await createChatReminder(performAuthorizedRequest, composerTargetChatId, {
            remindAt,
            comment
          }, composerApiScope);
      const payload = await readJson<ApiMutationSuccess | ErrorResponse>(response);

      if (!response.ok || (payload && isErrorResponse(payload))) {
        throw new Error(
          payload && isErrorResponse(payload)
            ? payload.error
            : isEditingReminder
              ? 'Не удалось обновить уведомление.'
              : 'Не удалось создать уведомление.'
        );
      }

      await (composerApiScope === 'wds/chats' ? refreshWidgetChatCollections(composerTargetChatId) : refreshChatCollections(composerTargetChatId));
      if (composerTargetKeyRef.current !== composerTargetKey) {
        return;
      }
      setIsReminderModalOpen(false);
      setEditingReminderId(null);
      resetReminderForm();
      showNotification({
        tone: 'success',
        title: isEditingReminder ? 'Уведомление обновлено' : 'Уведомление создано',
        message: isEditingReminder
          ? 'Дата, время и комментарий уведомления обновлены.'
          : 'Напоминание сохранено внутри чата и не будет отправлено клиенту.'
      });
    } catch (error) {
      showNotification({
        tone: 'error',
        title: isEditingReminder ? 'Не удалось обновить уведомление' : 'Не удалось создать уведомление',
        message: toErrorMessage(
          error,
          isEditingReminder
            ? 'Редактирование уведомления временно недоступно.'
            : 'Создание уведомления временно недоступно.'
        )
      });
    } finally {
      setCreateReminderPending(false);
    }
  };

  const handleDismissReminder = async (message: OperatorChatMessage) => {
    if (!isOperatorReminderMessage(message) || dismissingReminderId !== null) {
      return;
    }

    const reminderId = message.reminderId;

    if (typeof reminderId !== 'number') {
      return;
    }

    setDismissingReminderId(reminderId);

    try {
      const response = await dismissChatReminder(performAuthorizedRequest, message.chatId, reminderId, message.platform === 'widget' ? 'wds/chats' : 'chats');
      const payload = await readJson<ApiMutationSuccess | ErrorResponse>(response);

      if (!response.ok || (payload && isErrorResponse(payload))) {
        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось отключить уведомление.');
      }

      await (message.platform === 'widget' ? refreshWidgetChatCollections(message.chatId) : refreshChatCollections(message.chatId));
      showNotification({
        tone: 'success',
        title: 'Уведомление отключено',
        message: 'Подсветка чата остановлена, напоминание сохранено в истории как выполненное.'
      });
    } catch (error) {
      showNotification({
        tone: 'error',
        title: 'Не удалось отключить уведомление',
        message: toErrorMessage(error, 'Сейчас не получилось отключить уведомление.')
      });
    } finally {
      setDismissingReminderId(null);
    }
  };

  useEffect(() => {
    if (authViewState !== 'authenticated') {
      chatsSnapshotRef.current = [];
      hasHydratedChatsRef.current = false;
      activeChatMessagesSnapshotRef.current = [];
      hasHydratedActiveChatMessagesRef.current = false;
      pendingIncomingMessageAlertRef.current = false;
      return;
    }

    let cancelled = false;

    const syncChats = async (silent: boolean) => {
      if (!silent) {
        setIsChatsLoading(true);
      }

      try {
        const nextChats = await getOperatorChats(fetchAuthorizedJsonRef.current);
        const nextChatThreads = buildChatThreads(nextChats);
        const activeThreadChatIds = new Set(findChatThreadByChatId(nextChatThreads, activeChatIdRef.current)?.chatIds ?? []);

        if (cancelled) {
          return;
        }

        const currentChatState = new Map(
          chatsSnapshotRef.current.map((chat) => [
            chat.chatId,
            {
              unreadCount: Math.max(0, chat.unreadCount),
              lastIncomingMessageAtMs: chat.lastIncomingMessageAt ? Date.parse(chat.lastIncomingMessageAt) : 0
            }
          ])
        );
        const shouldPlayIncomingMessageAlert = hasHydratedChatsRef.current && nextChats.some((chat) => (
          (() => {
            if (activeThreadChatIds.has(chat.chatId)) {
              return false;
            }

            const previousChatState = currentChatState.get(chat.chatId);
            const nextUnreadCount = Math.max(0, chat.unreadCount);
            const nextLastIncomingMessageAtMs = chat.lastIncomingMessageAt ? Date.parse(chat.lastIncomingMessageAt) : 0;

            return nextUnreadCount > (previousChatState?.unreadCount ?? 0)
              || nextLastIncomingMessageAtMs > (previousChatState?.lastIncomingMessageAtMs ?? 0);
          })()
        ));

        setChats(nextChats);
        chatsSnapshotRef.current = nextChats;
        hasHydratedChatsRef.current = true;

        if (shouldPlayIncomingMessageAlert) {
          void playIncomingMessageAlert();
        }

        setActiveChatId((current) => {
          return resolveChatThreadId(nextChatThreads, current)
            ?? resolveChatThreadId(nextChatThreads, requestedChatIdRef.current)
            ?? null;
        });
      } catch (error) {
        if (!silent && !cancelled && authSessionRef.current !== null) {
          showNotificationRef.current({
            tone: 'error',
            title: 'Не удалось загрузить чаты',
            message: toErrorMessage(error, 'Список чатов временно недоступен.')
          });
        }
      } finally {
        if (!silent && !cancelled) {
          setIsChatsLoading(false);
        }
      }
    };

    void syncChats(false);
    const intervalId = window.setInterval(() => {
      void syncChats(true);
    }, CHAT_LIST_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [authViewState]);

  useEffect(() => {
    if (authViewState !== 'authenticated') {
      setWidgetChats([]);
      setIsWidgetChatsLoading(false);
      widgetChatsSnapshotRef.current = [];
      hasHydratedWidgetChatsRef.current = false;
      return;
    }

    let cancelled = false;

    const syncWidgetChats = async (silent: boolean) => {
      if (!silent) {
        setIsWidgetChatsLoading(true);
      }

      try {
        const nextWidgetChats = await getWidgetChats(fetchAuthorizedJsonRef.current);

        if (cancelled) {
          return;
        }

        const currentWidgetChatState = new Map(
          widgetChatsSnapshotRef.current.map((chat) => [
            chat.chatId,
            {
              unreadCount: Math.max(0, chat.unreadCount),
              lastIncomingMessageAtMs: chat.lastIncomingMessageAt ? Date.parse(chat.lastIncomingMessageAt) : 0
            }
          ])
        );
        const shouldPlayIncomingMessageAlert = hasHydratedWidgetChatsRef.current && nextWidgetChats.some((chat) => {
          if (chat.chatId === activeWidgetChatId) {
            return false;
          }

          const previousChatState = currentWidgetChatState.get(chat.chatId);
          const nextUnreadCount = Math.max(0, chat.unreadCount);
          const nextLastIncomingMessageAtMs = chat.lastIncomingMessageAt ? Date.parse(chat.lastIncomingMessageAt) : 0;

          return nextUnreadCount > (previousChatState?.unreadCount ?? 0)
            || nextLastIncomingMessageAtMs > (previousChatState?.lastIncomingMessageAtMs ?? 0);
        });

        setWidgetChats(nextWidgetChats);
        widgetChatsSnapshotRef.current = nextWidgetChats;
        hasHydratedWidgetChatsRef.current = true;

        if (shouldPlayIncomingMessageAlert) {
          void playIncomingMessageAlert('whistle');
        }

        if (activeWidgetChatId !== null && !nextWidgetChats.some((chat) => chat.chatId === activeWidgetChatId)) {
          setActiveWidgetChatId(null);
          setActiveWidgetChatMessages([]);
        }
      } catch (error) {
        if (!silent && !cancelled && authSessionRef.current !== null) {
          showNotificationRef.current({
            tone: 'error',
            title: 'Не удалось загрузить WDS-чаты',
            message: toErrorMessage(error, 'Список WDS-чатов временно недоступен.')
          });
        }
      } finally {
        if (!silent && !cancelled) {
          setIsWidgetChatsLoading(false);
        }
      }
    };

    void syncWidgetChats(false);
    const intervalId = window.setInterval(() => {
      void syncWidgetChats(true);
    }, CHAT_LIST_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeWidgetChatId, authViewState]);

  useEffect(() => {
    if (authViewState !== 'authenticated') {
      return;
    }

    if (requestedChatId === null) {
      setActiveChatId((current) => (current === null ? current : null));
      return;
    }

    const requestedChatThread = findChatThreadByChatId(chatThreads, requestedChatId);

    if (!requestedChatThread) {
      return;
    }

    if (requestedChatThread.chatId !== requestedChatId) {
      setRequestedChatId(requestedChatThread.chatId);
      syncChatIdInLocation(requestedChatThread.chatId);
    }

    if (activeChatId === requestedChatThread.chatId) {
      return;
    }

    shouldStickToBottomRef.current = true;
    setActiveChatId(requestedChatThread.chatId);
  }, [activeChatId, authViewState, chatThreads, requestedChatId]);

  useEffect(() => {
    if (authViewState !== 'authenticated' || !activeChatThreadKey) {
      activeChatMessagesSnapshotRef.current = [];
      hasHydratedActiveChatMessagesRef.current = false;
      setActiveChatMessages([]);
      setIsMessagesLoading(false);
      return;
    }

    const activeThreadChatIds = activeChatThreadKey
      .split(':')
      .map((value) => Number.parseInt(value, 10))
      .filter((value) => Number.isSafeInteger(value) && value > 0);

    if (activeThreadChatIds.length === 0) {
      activeChatMessagesSnapshotRef.current = [];
      hasHydratedActiveChatMessagesRef.current = false;
      setActiveChatMessages([]);
      setIsMessagesLoading(false);
      return;
    }

    let cancelled = false;
    shouldStickToBottomRef.current = true;
    activeChatMessagesSnapshotRef.current = [];
    hasHydratedActiveChatMessagesRef.current = false;
    setActiveChatMessages([]);

    const syncMessages = async (silent: boolean) => {
      if (!silent) {
        setIsMessagesLoading(true);
      }

      try {
        const nextMessages = await fetchChatThreadMessages(fetchAuthorizedJsonRef.current, activeThreadChatIds);

        if (cancelled) {
          return;
        }

        const currentIncomingMessageIds = new Set(
          activeChatMessagesSnapshotRef.current
            .filter((message) => message.direction === 'incoming')
            .map((message) => message.messageId)
        );
        const shouldPlayIncomingMessageAlert = hasHydratedActiveChatMessagesRef.current && nextMessages.some((message) => (
          message.direction === 'incoming' && !currentIncomingMessageIds.has(message.messageId)
        ));

        setActiveChatMessages(nextMessages);
        activeChatMessagesSnapshotRef.current = nextMessages;
        hasHydratedActiveChatMessagesRef.current = true;

        if (shouldPlayIncomingMessageAlert) {
          void playIncomingMessageAlert();
        }
      } catch (error) {
        if (!silent && !cancelled && authSessionRef.current !== null) {
          showNotificationRef.current({
            tone: 'error',
            title: 'Не удалось загрузить сообщения',
            message: toDisplayErrorMessage(error, 'История переписки временно недоступна.')
          });
        }
      } finally {
        if (!silent && !cancelled) {
          setIsMessagesLoading(false);
        }
      }
    };

    void syncMessages(false);
    const intervalId = window.setInterval(() => {
      void syncMessages(true);
    }, CHAT_MESSAGES_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeChatId, activeChatThreadKey, authViewState]);

  useEffect(() => {
    if (
      authViewState !== 'authenticated'
      || !isChatHistoryModalOpen
      || activeSidebarSection === 'wds'
      || !activeChatThreadKey
    ) {
      setActiveChatHistoryEntries([]);
      setIsChatHistoryLoading(false);

      if (isChatHistoryModalOpen && activeSidebarSection === 'wds') {
        setIsChatHistoryModalOpen(false);
      }

      return;
    }

    const activeThreadChatIds = activeChatThreadKey
      .split(':')
      .map((value) => Number.parseInt(value, 10))
      .filter((value) => Number.isSafeInteger(value) && value > 0);

    if (activeThreadChatIds.length === 0) {
      setActiveChatHistoryEntries([]);
      setIsChatHistoryLoading(false);
      return;
    }

    let cancelled = false;
    setIsChatHistoryLoading(true);

    const syncChatHistory = async () => {
      try {
        const nextHistoryEntries = await fetchChatThreadHistory(fetchAuthorizedJsonRef.current, activeThreadChatIds);

        if (!cancelled) {
          setActiveChatHistoryEntries(nextHistoryEntries);
        }
      } catch (error) {
        if (!cancelled && authSessionRef.current !== null) {
          showNotificationRef.current({
            tone: 'error',
            title: 'Не удалось загрузить историю чата',
            message: toDisplayErrorMessage(error, 'История действий по чату временно недоступна.')
          });
        }
      } finally {
        if (!cancelled) {
          setIsChatHistoryLoading(false);
        }
      }
    };

    void syncChatHistory();

    return () => {
      cancelled = true;
    };
  }, [activeChatThreadKey, activeSidebarSection, authSession?.operator, authViewState, isChatHistoryModalOpen]);

  useEffect(() => {
    if (authViewState !== 'authenticated' || activeWidgetChatId === null) {
      setActiveWidgetChatMessages([]);
      setIsWidgetMessagesLoading(false);
      return;
    }

    let cancelled = false;
    shouldStickToBottomRef.current = true;
    setActiveWidgetChatMessages([]);

    const syncWidgetMessages = async (silent: boolean) => {
      if (!silent) {
        setIsWidgetMessagesLoading(true);
      }

      try {
        const nextMessages = await getWidgetChatMessages(fetchAuthorizedJsonRef.current, activeWidgetChatId);

        if (cancelled) {
          return;
        }

        setActiveWidgetChatMessages(nextMessages);
      } catch (error) {
        if (!silent && !cancelled && authSessionRef.current !== null) {
          showNotificationRef.current({
            tone: 'error',
            title: 'Не удалось загрузить WDS-переписку',
            message: toErrorMessage(error, 'История сообщений виджета временно недоступна.')
          });
        }
      } finally {
        if (!silent && !cancelled) {
          setIsWidgetMessagesLoading(false);
        }
      }
    };

    void syncWidgetMessages(false);
    const intervalId = window.setInterval(() => {
      void syncWidgetMessages(true);
    }, CHAT_MESSAGES_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeWidgetChatId, authViewState]);

  useEffect(() => {
    if (!activeWidgetChat) {
      setWidgetLinkChatId('');
      return;
    }

    const linkedChatIds = new Set(activeWidgetChat.linkedChats.map((chat) => chat.chatId));
    const nextDefaultTarget = chats.find((chat) => !linkedChatIds.has(chat.chatId));

    setWidgetLinkChatId((current) => {
      if (current) {
        const currentId = Number(current);

        if (Number.isInteger(currentId) && chats.some((chat) => chat.chatId === currentId && !linkedChatIds.has(chat.chatId))) {
          return current;
        }
      }

      return nextDefaultTarget ? String(nextDefaultTarget.chatId) : '';
    });
  }, [activeWidgetChat, chats]);

  useEffect(() => {
    setChatDraft('');
    setSendMessagePending(false);
    setDeleteConfirmMessage(null);
    setReadingMessageId(null);
    setIsReminderModalOpen(false);
    setIsQuickReplyModalOpen(false);
    setCreateReminderPending(false);
    setEditingReminderId(null);
    setDismissingReminderId(null);
    setIsAttachmentMenuOpen(false);
    resetCourseAttachmentModal();
    setAudioPlaybackStates({});
    resetReminderForm();
    setIsConversationDragActive(false);
    conversationDragDepthRef.current = 0;
    audioElementRefs.current = {};
  }, [activeChatId, activeWidgetChatId, activeSidebarSection]);

  useEffect(() => {
    if (authViewState !== 'authenticated') {
      return;
    }

    const messages = activeSidebarSection === 'wds' ? activeWidgetChatMessages : activeChatMessages;
    const previewableFiles = messages.flatMap((message) => message.files).filter((file) => (
      isPreviewableFile(file)
      && !filePreviewUrlsRef.current[getMessageFileKey(file)]
      && !failedPreviewFileIdsRef.current.has(getMessageFileKey(file))
    ));

    if (previewableFiles.length === 0) {
      return;
    }

    let cancelled = false;

    const loadPreviewUrls = async () => {
      const settledResults = await Promise.allSettled(
        previewableFiles.map(async (file) => {
          const blob = await fetchChatFileBlob(fetchAuthorizedBlobRef.current, file.fileId, file.platform);
          return {
            fileKey: getMessageFileKey(file),
            objectUrl: URL.createObjectURL(blob)
          };
        })
      );

      if (cancelled) {
        settledResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            URL.revokeObjectURL(result.value.objectUrl);
          }
        });

        return;
      }

      const nextUrls: Record<string, string> = {};

      settledResults.forEach((result, index) => {
        const file = previewableFiles[index];

        if (result.status === 'fulfilled') {
          nextUrls[result.value.fileKey] = result.value.objectUrl;
          return;
        }

        if (file) {
          failedPreviewFileIdsRef.current.add(getMessageFileKey(file));
          console.error(result.reason);
        }
      });

      if (Object.keys(nextUrls).length === 0) {
        return;
      }

      filePreviewUrlsRef.current = {
        ...filePreviewUrlsRef.current,
        ...nextUrls
      };

      setFilePreviewUrls((current) => ({
        ...current,
        ...nextUrls
      }));
    };

    void loadPreviewUrls();

    return () => {
      cancelled = true;
    };
  }, [activeChatMessages, activeWidgetChatMessages, activeSidebarSection, authViewState]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const viewport = messageViewportRef.current;

      if (!viewport) {
        setShowScrollToBottomButton(false);
        return;
      }

      if (shouldStickToBottomRef.current) {
        viewport.scrollTop = viewport.scrollHeight;
      }

      const distanceToBottom = Math.max(0, viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight);
      const isNearBottom = distanceToBottom < 120;

      shouldStickToBottomRef.current = isNearBottom;
      setShowScrollToBottomButton(distanceToBottom > 160);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [activeChatId, activeWidgetChatId, activeChatMessages, activeWidgetChatMessages, filePreviewUrls, isMessagesLoading, isWidgetMessagesLoading]);

  const handleLoginFieldChange = (field: keyof LoginFormState) => (event: ChangeEvent<HTMLInputElement>) => {
    setLoginFormState((current) => ({
      ...current,
      [field]: event.target.value
    }));
  };

  const handleLoginSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginPending(true);

    try {
      const response = await loginOperator({
        login: loginFormState.login.trim(),
        password: loginFormState.password
      });
      const payload = await readJson<AuthSuccessResponse | ErrorResponse>(response);

      if (!response.ok || !payload || isErrorResponse(payload)) {
        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось выполнить вход в сервис.');
      }

      applySession(payload);
      setLoginFormState(initialLoginFormState);
    } catch (error) {
      resetSession();
      showNotification({
        tone: 'error',
        title: 'Ошибка авторизации',
        message: toErrorMessage(error, 'Не удалось выполнить вход в сервис.')
      });
      setLoginFormState((current) => ({
        ...current,
        password: ''
      }));
    } finally {
      setLoginPending(false);
    }
  };

  const handleLogout = async () => {
    setLogoutPending(true);

    try {
      const response = await logoutOperator(performAuthorizedRequest);

      if (!response.ok) {
        const payload = await readJson<ErrorResponse>(response);
        throw new Error(payload?.error ?? 'Не удалось корректно завершить текущую сессию на сервере.');
      }

      setLoginFormState(initialLoginFormState);
      resetSession();
    } catch (error) {
      showNotification({
        tone: 'error',
        title: 'Выход завершился с ошибкой',
        message: toErrorMessage(error, 'Не удалось корректно завершить текущую сессию на сервере.')
      });
    } finally {
      setLogoutPending(false);
    }
  };

  const handleChatSelect = (chatId: number) => {
    shouldStickToBottomRef.current = true;
    setRequestedChatId(chatId);
    setActiveChatId(chatId);
    syncChatIdInLocation(chatId);
  };

  const handleWidgetChatSelect = (chatId: number) => {
    shouldStickToBottomRef.current = true;
    setActiveWidgetChatId(chatId);
    syncWidgetChatIdInLocation(chatId);
  };

  const handleUisCallSelect = useCallback((call: UisCallGroup | null) => {
    setActiveUisGroup(call);
    setRequestedUisGroupId(call?.id ?? null);
    syncUisGroupIdInLocation(call?.id ?? null);
  }, []);

  const handleUisFilterOptionsChange = useCallback((options: UisFilterOption[]) => {
    setUisFilterOptions(options);
  }, []);

  const handleUisRefresh = useCallback(() => {
    setUisRefreshRequestVersion((current) => current + 1);
  }, []);

  const handleLinkedChatOpen = (event: ReactMouseEvent<HTMLButtonElement>, chatId: number) => {
    event.stopPropagation();
    setActiveSidebarSection('chats');
    setActiveWidgetChatId(null);
    handleChatSelect(chatId);
  };

  const handleLinkedWidgetChatOpen = (event: ReactMouseEvent<HTMLButtonElement>, chatId: number) => {
    event.stopPropagation();
    shouldStickToBottomRef.current = true;
    setActiveSidebarSection('wds');
    setActiveWidgetChatId(chatId);
  };

  const handleMobileConversationClose = () => {
    shouldStickToBottomRef.current = true;
    setRequestedChatId(null);
    setActiveChatId(null);
    setActiveWidgetChatId(null);
    setActiveUisGroup(null);
    setRequestedUisGroupId(null);
    applyLocationParams({
      chatId: null,
      wdsChatId: null,
      uisGroupId: null
    });
  };

  const syncMessageViewportState = (viewport: HTMLDivElement | null) => {
    if (!viewport) {
      setShowScrollToBottomButton(false);
      return;
    }

    const distanceToBottom = Math.max(0, viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight);
    const isNearBottom = distanceToBottom < 120;

    shouldStickToBottomRef.current = isNearBottom;
    setShowScrollToBottomButton(distanceToBottom > 160);
  };

  const scrollMessageViewportToBottomNow = () => {
    const viewport = messageViewportRef.current;

    if (!viewport) {
      return;
    }

    viewport.scrollTop = viewport.scrollHeight;
    setShowScrollToBottomButton(false);
  };

  const scheduleMessageViewportBottomSync = () => {
    shouldStickToBottomRef.current = true;
    scrollMessageViewportToBottomNow();

    window.requestAnimationFrame(() => {
      scrollMessageViewportToBottomNow();

      window.requestAnimationFrame(() => {
        scrollMessageViewportToBottomNow();
      });
    });
  };

  const scrollMessageViewportToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const viewport = messageViewportRef.current;

    if (!viewport) {
      return;
    }

    shouldStickToBottomRef.current = true;
    viewport.scrollTo({
      top: viewport.scrollHeight,
      behavior
    });
    setShowScrollToBottomButton(false);
  };

  const handleMessageViewportScroll = (event: UIEvent<HTMLDivElement>) => {
    syncMessageViewportState(event.currentTarget);
  };

  const syncComposerTextareaHeight = () => {
    const textarea = composerTextareaRef.current;

    if (!textarea) {
      return;
    }

    const maxHeight = 196;
    const minHeight = window.matchMedia('(max-width: 760px)').matches ? 24 : 50;

    textarea.style.height = '0px';
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      syncComposerTextareaHeight();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [activeChatId, activeWidgetChatId, chatDraft]);

  useEffect(() => {
    resetComposerFiles();
  }, [activeChatId, activeWidgetChatId, activeSidebarSection]);

  useEffect(() => {
    closeFilePreview();
  }, [activeChatId, activeWidgetChatId, activeSidebarSection]);

  useEffect(() => {
    if (!isChatPhoneCountryMenuOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (chatPhoneCountryMenuRef.current && !chatPhoneCountryMenuRef.current.contains(event.target as Node)) {
        setIsChatPhoneCountryMenuOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isChatPhoneCountryMenuOpen]);

  useEffect(() => {
    if (!isCourseActionPopoverOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (courseActionPopoverRef.current && !courseActionPopoverRef.current.contains(event.target as Node)) {
        setIsCourseActionPopoverOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isCourseActionPopoverOpen]);

  useEffect(() => {
    if (!isAttachmentMenuOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(event.target as Node)) {
        setIsAttachmentMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAttachmentMenuOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAttachmentMenuOpen]);

  useEffect(() => {
    setIsCourseActionPopoverOpen(false);
    setDemoAccessDialogMode(null);
    resetCourseSelectionModal();
    resetCourseProgramModal();
  }, [activeChatId, activeWidgetChatId, activeSidebarSection]);

  const ensureAttachmentObjectUrl = async (file: OperatorChatMessageFile): Promise<string> => {
    const fileKey = getMessageFileKey(file);
    const existingObjectUrl = filePreviewUrlsRef.current[fileKey];

    if (existingObjectUrl) {
      return existingObjectUrl;
    }

    const blob = await fetchChatFileBlob(fetchAuthorizedBlob, file.fileId, file.platform);
    const normalizedBlob = normalizeAttachmentPreviewBlob(file, blob);
    const nextObjectUrl = URL.createObjectURL(normalizedBlob);
    const concurrentObjectUrl = filePreviewUrlsRef.current[fileKey];

    if (concurrentObjectUrl) {
      URL.revokeObjectURL(nextObjectUrl);
      return concurrentObjectUrl;
    }

    filePreviewUrlsRef.current = {
      ...filePreviewUrlsRef.current,
      [fileKey]: nextObjectUrl
    };
    setFilePreviewUrls((current) => ({
      ...current,
      [fileKey]: nextObjectUrl
    }));

    return nextObjectUrl;
  };

  const buildAuthorizedAttachmentContentUrl = (file: OperatorChatMessageFile): string | null => {
    const accessToken = authSessionRef.current?.accessToken;

    if (!accessToken) {
      return null;
    }

    return buildChatFileContentUrl(file.fileId, accessToken, file.platform);
  };

  const handleAttachmentPreview = async (file: OperatorChatMessageFile) => {
    const fileKey = getMessageFileKey(file);
    const previewMode = getFilePreviewMode(file);
    const shouldRenderDocxPreview = previewMode === 'document' && isDocxFile(file);
    const shouldLoadStructuredPreview = previewMode === 'document' && !shouldRenderDocxPreview && supportsStructuredDocumentPreview(file);
    const shouldOpenPdfInline = previewMode === 'document' && isPdfFile(file);
    const cachedBlob = shouldRenderDocxPreview ? filePreviewBlobCacheRef.current[fileKey] ?? null : null;
    const cachedDocumentPreview = shouldLoadStructuredPreview ? fileDocumentPreviewCacheRef.current[fileKey] ?? null : null;
    const existingObjectUrl = shouldLoadStructuredPreview || shouldRenderDocxPreview ? null : filePreviewUrlsRef.current[fileKey] ?? null;
    const directPdfUrl = shouldOpenPdfInline ? buildAuthorizedAttachmentContentUrl(file) : null;

    setActiveFilePreview({
      file,
      objectUrl: directPdfUrl ?? existingObjectUrl,
      sourceBlob: cachedBlob,
      documentPreview: cachedDocumentPreview,
      isLoading: shouldLoadStructuredPreview
        ? cachedDocumentPreview === null
        : shouldRenderDocxPreview
          ? cachedBlob === null
          : shouldOpenPdfInline
            ? directPdfUrl === null
            : existingObjectUrl === null,
      errorMessage: null
    });

    if (shouldOpenPdfInline && directPdfUrl) {
      return;
    }

    if (shouldRenderDocxPreview && cachedBlob) {
      return;
    }

    if (shouldLoadStructuredPreview && cachedDocumentPreview) {
      return;
    }

    if (!shouldLoadStructuredPreview && !shouldRenderDocxPreview && existingObjectUrl) {
      return;
    }

    try {
      if (shouldRenderDocxPreview) {
        const blob = await fetchChatFileBlob(fetchAuthorizedBlobRef.current, file.fileId, file.platform);

        filePreviewBlobCacheRef.current = {
          ...filePreviewBlobCacheRef.current,
          [fileKey]: blob
        };

        setActiveFilePreview((current) => {
          if (!current || getMessageFileKey(current.file) !== fileKey) {
            return current;
          }

          return {
            ...current,
            sourceBlob: blob,
            isLoading: false,
            errorMessage: null
          };
        });

        return;
      }

      if (shouldLoadStructuredPreview) {
        const documentPreview = await fetchChatFilePreview(fetchAuthorizedJsonRef.current, file.fileId, file.platform);

        fileDocumentPreviewCacheRef.current = {
          ...fileDocumentPreviewCacheRef.current,
          [fileKey]: documentPreview
        };

        setActiveFilePreview((current) => {
          if (!current || getMessageFileKey(current.file) !== fileKey) {
            return current;
          }

          return {
            ...current,
            documentPreview,
            sourceBlob: null,
            isLoading: false,
            errorMessage: null
          };
        });

        return;
      }

      const objectUrl = await ensureAttachmentObjectUrl(file);

      setActiveFilePreview((current) => {
        if (!current || getMessageFileKey(current.file) !== fileKey) {
          return current;
        }

        return {
          ...current,
          objectUrl,
          sourceBlob: null,
          documentPreview: null,
          isLoading: false,
          errorMessage: null
        };
      });
    } catch (error) {
      setActiveFilePreview((current) => {
        if (!current || getMessageFileKey(current.file) !== fileKey) {
          return current;
        }

        return {
          ...current,
          isLoading: false,
          errorMessage: toErrorMessage(error, 'Предпросмотр файла временно недоступен.')
        };
      });
    }
  };

  const handleAttachmentDownload = async (file: OperatorChatMessageFile) => {
    try {
      let objectUrl = filePreviewUrlsRef.current[getMessageFileKey(file)];
      let shouldRevokeAfterDownload = false;

      if (!objectUrl) {
        const blob = await fetchChatFileBlob(fetchAuthorizedBlob, file.fileId, file.platform);
        objectUrl = URL.createObjectURL(blob);
        shouldRevokeAfterDownload = true;
      }

      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = file.originalFileName ?? file.fileName;
      anchor.rel = 'noopener';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();

      if (shouldRevokeAfterDownload) {
        window.setTimeout(() => {
          URL.revokeObjectURL(objectUrl);
        }, 1_000);
      }
    } catch (error) {
      showNotification({
        tone: 'error',
        title: 'Не удалось скачать файл',
        message: toErrorMessage(error, 'Скачивание файла временно недоступно.')
      });
    }
  };

  const handleComposerKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  };

  const handleComposerAttachmentClick = () => {
    if (!hasComposerAttachmentMenu) {
      composerFileInputRef.current?.click();
      return;
    }

    setIsAttachmentMenuOpen((current) => !current);
  };

  const handleOpenNativeAttachmentPicker = () => {
    setIsAttachmentMenuOpen(false);
    composerFileInputRef.current?.click();
  };

  const openCourseAttachmentModal = async (selectedCourse?: ChatCourseCatalogCourse) => {
    if (!composerTargetChatId || sendMessagePending || isCourseAttachmentFilesLoading) {
      return;
    }

    const requestId = ++courseAttachmentRequestRef.current;
    const isCurrentRequest = () => composerTargetKeyRef.current === composerTargetKey && courseAttachmentRequestRef.current === requestId;
    const isWidgetTarget = activeSidebarSection === 'wds';

    setIsAttachmentMenuOpen(false);
    setIsCourseAttachmentModalOpen(!isWidgetTarget || Boolean(selectedCourse));
    setCourseAttachmentFiles([]);
    setWidgetAttachmentCourse(selectedCourse ?? null);
    setSelectedCourseAttachmentIds([]);

    setCourseAttachmentError('');
    setIsCourseAttachmentFilesLoading(true);

    try {
      if (isWidgetTarget) {
        const result = await getWidgetCourseAttachmentFiles(fetchAuthorizedJson, composerTargetChatId, selectedCourse?.courseId);

        if (!isCurrentRequest()) {
          return;
        }

        if (result.courseId === null) {
          setIsCourseAttachmentFilesLoading(false);
          await openCourseSelectionModal();
          return;
        }

        setWidgetAttachmentCourse({ courseId: result.courseId, courseName: result.courseName ?? selectedCourse?.courseName ?? `Курс #${result.courseId}` });
        setCourseAttachmentFiles(result.files);
        setIsCourseAttachmentModalOpen(true);
      } else {
        if (!composerCourseSourceId || composerCourseProgramId === null) {
          throw new Error('У клиента не выбраны курс и программа часов.');
        }

        const files = await getChatCourseAttachmentFiles(fetchAuthorizedJson, composerCourseSourceId);
        if (isCurrentRequest()) {
          setCourseAttachmentFiles(files);
        }
      }
    } catch (error) {
      if (isCurrentRequest()) {
        setIsCourseAttachmentModalOpen(true);
        setCourseAttachmentError(toErrorMessage(error, 'Не удалось загрузить файлы курса.'));
      }
    } finally {
      if (isCurrentRequest()) {
        setIsCourseAttachmentFilesLoading(false);
      }
    }
  };

  const addFilesToComposer = (incomingFiles: File[]) => {
    if (incomingFiles.length === 0) {
      return;
    }

    const acceptedFiles = incomingFiles.filter((file) => file.size <= COMPOSER_MAX_FILE_SIZE_BYTES);
    const currentFileKeys = new Set(composerFiles.map((file) => buildComposerFileKey(file)));
    const uniqueFiles = acceptedFiles.filter((file) => !currentFileKeys.has(buildComposerFileKey(file)));
    const nextFiles = [...composerFiles, ...uniqueFiles].slice(0, COMPOSER_MAX_FILES);

    setComposerFiles(nextFiles);

    if (acceptedFiles.length !== incomingFiles.length) {
      showNotification({
        tone: 'error',
        title: 'Файл слишком большой',
        message: 'Можно отправлять файлы размером не более 25 МБ.'
      });
    }

    if (uniqueFiles.length !== acceptedFiles.length) {
      showNotification({
        tone: 'info',
        title: 'Повторные файлы пропущены',
        message: 'Одинаковые файлы не будут добавлены в сообщение повторно.'
      });
    }

    if (composerFiles.length + uniqueFiles.length > COMPOSER_MAX_FILES) {
      showNotification({
        tone: 'info',
        title: 'Достигнут лимит файлов',
        message: 'За одно сообщение можно прикрепить не более 10 файлов.'
      });
    }
  };

  const handleCourseAttachmentSelectionChange = (attachmentId: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedCourseAttachmentIds((current) => event.target.checked
      ? current.includes(attachmentId)
        ? current
        : [...current, attachmentId]
      : current.filter((selectedId) => selectedId !== attachmentId));
  };

  const handleAttachSelectedCourseFiles = async () => {
    const isWidgetTarget = activeSidebarSection === 'wds';
    const sourceChatId = isWidgetTarget ? composerTargetChatId : composerCourseSourceId;
    const selectedWidgetCourseId = widgetAttachmentCourse?.courseId;

    if (!sourceChatId || (isWidgetTarget && selectedWidgetCourseId === undefined) || selectedCourseAttachmentIds.length === 0 || isAttachingCourseFiles) {
      return;
    }

    const requestId = courseAttachmentRequestRef.current;
    const isCurrentRequest = () => composerTargetKeyRef.current === composerTargetKey && courseAttachmentRequestRef.current === requestId;
    const selectedFiles = courseAttachmentFiles.filter((file) => selectedCourseAttachmentIds.includes(file.attachmentId));

    if (selectedFiles.length === 0) {
      return;
    }

    setIsAttachingCourseFiles(true);
    setCourseAttachmentError('');

    try {
      const downloadedFiles = await Promise.all(selectedFiles.map(async (attachment) => {
        const blob = isWidgetTarget && selectedWidgetCourseId !== undefined
          ? await fetchWidgetCourseAttachmentFileBlob(fetchAuthorizedBlob, sourceChatId, attachment.attachmentId, selectedWidgetCourseId)
          : await fetchChatCourseAttachmentFileBlob(fetchAuthorizedBlob, sourceChatId, attachment.attachmentId);
        const fileType = attachment.mimeType || blob.type || 'application/octet-stream';

        return new File([blob], formatCourseAttachmentDisplayFileName(attachment.fileName), {
          type: fileType,
          lastModified: Date.now()
        });
      }));

      if (isCurrentRequest()) {
        addFilesToComposer(downloadedFiles);
        resetCourseAttachmentModal();
      }
    } catch (error) {
      if (isCurrentRequest()) {
        setCourseAttachmentError(toErrorMessage(error, 'Не удалось прикрепить выбранные файлы.'));
      }
    } finally {
      if (isCurrentRequest()) {
        setIsAttachingCourseFiles(false);
      }
    }
  };

  const handleComposerFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);

    event.target.value = '';

    if (selectedFiles.length === 0) {
      return;
    }

    addFilesToComposer(selectedFiles);
  };

  const handleConversationDragEnter = (event: ReactDragEvent<HTMLElement>) => {
    if (!composerTargetChatId || sendMessagePending || !hasTransferredFiles(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    conversationDragDepthRef.current += 1;
    setIsConversationDragActive(true);
  };

  const handleConversationDragOver = (event: ReactDragEvent<HTMLElement>) => {
    if (!composerTargetChatId || sendMessagePending || !hasTransferredFiles(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';

    if (!isConversationDragActive) {
      setIsConversationDragActive(true);
    }
  };

  const handleConversationDragLeave = (event: ReactDragEvent<HTMLElement>) => {
    if (!hasTransferredFiles(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    conversationDragDepthRef.current = Math.max(0, conversationDragDepthRef.current - 1);

    if (conversationDragDepthRef.current === 0) {
      setIsConversationDragActive(false);
    }
  };

  const handleConversationDrop = (event: ReactDragEvent<HTMLElement>) => {
    if (!hasTransferredFiles(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    conversationDragDepthRef.current = 0;
    setIsConversationDragActive(false);

    if (!composerTargetChatId || sendMessagePending) {
      return;
    }

    addFilesToComposer(Array.from(event.dataTransfer.files ?? []));
  };

  const handleComposerFileRemove = (fileKey: string) => {
    setComposerFiles((current) => current.filter((file) => buildComposerFileKey(file) !== fileKey));
  };

  const handleSendMessageSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isWidgetSection = activeSidebarSection === 'wds';
    const targetChatId = isWidgetSection ? activeWidgetChatId : activeChatId;

    if (!targetChatId || sendMessagePending) {
      return;
    }

    const text = chatDraft.trim();
    const hasFiles = composerFiles.length > 0;

    if (!text && !hasFiles) {
      return;
    }

    setSendMessagePending(true);
    scheduleMessageViewportBottomSync();

    try {
      if (isWidgetSection) {
        const response = await sendWidgetMessage(performAuthorizedRequest, targetChatId, text, composerFiles);
        const payload = await readJson<ApiMutationSuccess | ErrorResponse>(response);

        if (!response.ok || (payload && isErrorResponse(payload))) {
          throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось отправить сообщение в WDS-чат.');
        }

        if (composerTargetKeyRef.current !== composerTargetKey) {
          return;
        }
        setChatDraft('');
        resetComposerFiles();
        await refreshWidgetChatCollections(targetChatId);
        scheduleMessageViewportBottomSync();
        return;
      }

      const response = await sendChatMessage(performAuthorizedRequest, targetChatId, text, composerFiles);
      const payload = await readJson<ApiMutationSuccess | ErrorResponse>(response);

      if (!response.ok || (payload && isErrorResponse(payload))) {
        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось отправить сообщение.');
      }

      setChatDraft('');
      resetComposerFiles();
      await refreshChatCollections(targetChatId);
      scheduleMessageViewportBottomSync();
    } catch (error) {
      showNotification({
        tone: 'error',
        title: 'Не удалось отправить сообщение',
        message: toErrorMessage(error, 'Сообщение не было отправлено в чат.')
      });
    } finally {
      setSendMessagePending(false);
    }
  };

  const handleDeleteMessage = (message: DisplayedChatMessage) => {
    if (!composerTargetChatId || deletingMessageId !== null) {
      return;
    }

    if (message.direction !== 'outgoing' || isOperatorReminderMessage(message)) {
      return;
    }

    setDeleteConfirmMessage(message);
  };

  const confirmDeleteMessage = async () => {
    if (deletingMessageId !== null || !deleteConfirmMessage) {
      return;
    }

    const targetMessage = deleteConfirmMessage;
    setDeletingMessageId(targetMessage.messageId);

    try {
      const targetChatId = targetMessage.chatId;
      for (const groupedMessageId of targetMessage.groupedMessageIds) {
        const response = await deleteChatMessage(performAuthorizedRequest, targetChatId, groupedMessageId, targetMessage.platform === 'widget' ? 'wds/chats' : 'chats');
        const payload = await readJson<ApiMutationSuccess | ErrorResponse>(response);

        if (!response.ok || (payload && isErrorResponse(payload))) {
          throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось удалить сообщение.');
        }
      }

      await (targetMessage.platform === 'widget' ? refreshWidgetChatCollections(targetChatId) : refreshChatCollections(targetChatId));
      setDeleteConfirmMessage(null);
    } catch (error) {
      showNotification({
        tone: 'error',
        title: 'Не удалось удалить сообщение',
        message: toErrorMessage(error, 'Сообщение осталось в чате.')
      });
    } finally {
      setDeletingMessageId(null);
    }
  };

  const handleMarkMessageAsRead = async (message: OperatorChatMessage) => {
    const isWidgetMessage = message.platform === 'widget';
    const targetChatId = isWidgetMessage ? activeWidgetChatId : activeChatId;

    if (!targetChatId || readingMessageId !== null) {
      return;
    }

    if (message.direction !== 'incoming' || message.operatorReadAt !== null) {
      return;
    }

    setReadingMessageId(message.messageId);

    try {
      const response = isWidgetMessage
        ? await markWidgetMessageAsRead(performAuthorizedRequest, message.chatId, message.messageId)
        : await markChatMessageAsRead(performAuthorizedRequest, message.chatId, message.messageId);
      const payload = await readJson<ApiMutationSuccess | ErrorResponse>(response);

      if (!response.ok || (payload && isErrorResponse(payload))) {
        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось отметить сообщение как прочитанное.');
      }

      const readAt = new Date().toISOString();

      if (isWidgetMessage) {
        setActiveWidgetChatMessages((current) => current.map((item) => (
          item.messageId === message.messageId
            ? { ...item, operatorReadAt: readAt }
            : item
        )));
        setWidgetChats((current) => current.map((chat) => (
          chat.chatId === message.chatId
            ? { ...chat, unreadCount: Math.max(0, chat.unreadCount - 1) }
            : chat
        )));
      } else {
        setActiveChatMessages((current) => current.map((item) => (
          item.messageId === message.messageId
            ? { ...item, operatorReadAt: readAt }
            : item
        )));
        setChats((current) => current.map((chat) => (
          chat.chatId === message.chatId
            ? { ...chat, unreadCount: Math.max(0, chat.unreadCount - 1) }
            : chat
        )));
      }
    } catch (error) {
      showNotification({
        tone: 'error',
        title: 'Не удалось отметить сообщение',
        message: toErrorMessage(error, 'Статус сообщения не изменился.')
      });
    } finally {
      setReadingMessageId(null);
    }
  };

  const handleWidgetLinkFieldChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setWidgetLinkChatId(event.target.value);
  };

  const handleCreateWidgetLink = async () => {
    if (!activeWidgetChatId || !widgetLinkChatId || createWidgetLinkPending) {
      return;
    }

    const linkedChatId = Number(widgetLinkChatId);

    if (!Number.isInteger(linkedChatId)) {
      return;
    }

    setCreateWidgetLinkPending(true);

    try {
      const response = await linkWidgetChat(performAuthorizedRequest, activeWidgetChatId, linkedChatId);
      const payload = await readJson<ApiMutationSuccess | ErrorResponse>(response);

      if (!response.ok || (payload && isErrorResponse(payload))) {
        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось связать WDS-чат с диалогом клиента.');
      }

      await refreshWidgetChatCollections(activeWidgetChatId);
      showNotification({
        tone: 'success',
        title: 'Связь сохранена',
        message: 'WDS-чат связан с выбранным Telegram/MAX-диалогом.'
      });
    } catch (error) {
      showNotification({
        tone: 'error',
        title: 'Не удалось связать чаты',
        message: toErrorMessage(error, 'Связь между WDS и Telegram/MAX не была сохранена.')
      });
    } finally {
      setCreateWidgetLinkPending(false);
    }
  };

  const handleApplyChatPhone = async () => {
    if (!activeChatId || !activeChat || isApplyingChatPhone) {
      return;
    }

    const currentPhoneCountry = getPhoneCountryConfig(chatPhoneCountry);
    const phoneNumber = buildPhoneValue(chatPhoneInput, chatPhoneCountry);
    const phoneLabel = formatPhoneValue(chatPhoneInput, chatPhoneCountry);

    if (!isValidPhoneForCountry(phoneNumber, chatPhoneCountry)) {
      setChatPhoneError(`Укажите корректный номер для страны «${currentPhoneCountry.label}».`);
      return;
    }

    setIsApplyingChatPhone(true);
    setChatPhoneError('');

    try {
      const response = await applyChatPhone(performAuthorizedRequest, activeChatId, {
        phoneNumber,
        phoneLabel
      });
      const payload = await readJson<ChatPhoneApplicationResult | ErrorResponse>(response);

      if (!response.ok || !payload || isErrorResponse(payload)) {
        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось сохранить номер телефона клиента.');
      }

      await refreshChatCollections(activeChatId);
      resetChatPhoneModal();
      showNotification({
        tone: 'success',
        title: 'Телефон сохранён',
        message: `Номер ${payload.phoneNumber} сохранён. Заявка ${payload.applicationId} привязана к текущему чату.`
      });
    } catch (error) {
      const errorMessage = toErrorMessage(error, 'Номер телефона не удалось сохранить.');

      setChatPhoneError(errorMessage);
      showNotification({
        tone: 'error',
        title: 'Не удалось сохранить телефон',
        message: errorMessage
      });
    } finally {
      setIsApplyingChatPhone(false);
    }
  };

  const handleCourseSelectionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedCourseId(event.target.value);
    setCourseSelectionError('');
  };

  const handleCourseSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCourseSearchQuery(event.target.value);
  };

  const handleCourseProgramSelectionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedCourseProgramId(event.target.value);
    setCourseProgramSelectionError('');
  };

  const openCourseSelectionModal = async () => {
    if (!composerTargetChatId || (activeSidebarSection === 'chats' && !activeChat) || isCourseSelectionLoading || isCourseProgramLoading || isApplyingChatCourseProgram) {
      return;
    }

    const requestId = ++courseSelectionRequestRef.current;
    const isCurrentRequest = () => composerTargetKeyRef.current === composerTargetKey && courseSelectionRequestRef.current === requestId;
    const currentCourseLabel = activeSidebarSection === 'wds' ? null : activeChat?.courseName ?? activeChat?.courseTitle ?? null;

    setIsCourseActionPopoverOpen(false);
    setIsCourseAttachmentModalOpen(false);
    setIsCourseSelectionModalOpen(true);
    setCourseCatalogOptions([]);
    setSelectedCourseId('');
    setCourseSearchQuery('');
    setCourseSelectionError('');
    setIsCourseSelectionLoading(true);

    try {
      const courses = await getChatCourses(fetchAuthorizedJsonRef.current);

      if (!isCurrentRequest()) {
        return;
      }

      const matchedCourse = courses.find((course) => normalizeCourseOptionLabel(course.courseName) === normalizeCourseOptionLabel(currentCourseLabel)) ?? null;

      setCourseCatalogOptions(courses);
      setSelectedCourseId(matchedCourse ? String(matchedCourse.courseId) : '');

      if (courses.length === 0) {
        setCourseSelectionError('Список доступных курсов пока пуст.');
      }
    } catch (error) {
      if (!isCurrentRequest()) {
        return;
      }

      const errorMessage = toErrorMessage(error, 'Не удалось загрузить список курсов.');

      setCourseSelectionError(errorMessage);
      showNotification({
        tone: 'error',
        title: 'Не удалось загрузить курсы',
        message: errorMessage
      });
    } finally {
      if (isCurrentRequest()) {
        setIsCourseSelectionLoading(false);
      }
    }
  };

  const openActiveChatCourseProgramModal = async () => {
    if (activeSidebarSection !== 'chats' || !activeChatId || !activeChat || activeChat.volumeHours === null || activeChat.courseProgramId === null || isCourseProgramLoading || isApplyingChatCourseProgram) {
      return;
    }

    const requestedChatId = activeChatId;
    const currentCourseProgramId = activeChat.courseProgramId;

    setIsCourseActionPopoverOpen(false);
    setIsCourseProgramModalOpen(true);
    setCourseProgramOptions([]);
    setSelectedCourseProgramId(String(currentCourseProgramId));
    setCourseProgramSelectionError('');
    setCourseProgramCourseName(activeChat.courseName ?? activeChat.courseTitle ?? null);
    setIsCourseProgramLoading(true);

    try {
      const programs = await getChatCoursePrograms(fetchAuthorizedJsonRef.current, requestedChatId);

      if (activeChatIdRef.current !== requestedChatId) {
        return;
      }

      setCourseProgramOptions(programs);
      setSelectedCourseProgramId(
        programs.some((program) => program.courseProgramId === currentCourseProgramId)
          ? String(currentCourseProgramId)
          : ''
      );

      if (!activeChat.courseName && programs[0]?.courseName) {
        setCourseProgramCourseName(programs[0].courseName);
      }

      if (programs.length === 0) {
        setCourseProgramSelectionError('Для выбранного курса нет активных вариантов часов.');
      }
    } catch (error) {
      if (activeChatIdRef.current !== requestedChatId) {
        return;
      }

      const errorMessage = toErrorMessage(error, 'Не удалось загрузить варианты часов.');

      setCourseProgramSelectionError(errorMessage);
      showNotification({
        tone: 'error',
        title: 'Не удалось загрузить часы',
        message: errorMessage
      });
    } finally {
      if (activeChatIdRef.current === requestedChatId) {
        setIsCourseProgramLoading(false);
      }
    }
  };

  const handleCourseBadgeClick = () => {
    if (activeSidebarSection !== 'chats' || !activeChatId || !activeChat || isCourseSelectionLoading || isCourseProgramLoading || isApplyingChatCourseProgram) {
      return;
    }

    if (!normalizeCourseOptionLabel(activeChat.courseName)) {
      void openCourseSelectionModal();
      return;
    }

    setIsCourseActionPopoverOpen((current) => !current);
  };

  const handleGrantChatDemoAccess = async () => {
    if (!activeChatId || !activeChat || isGrantingChatDemoAccess) {
      return;
    }

    if (!hasActiveChatDemoAccessPrerequisites) {
      setDemoAccessDialogMode('requirements');
      return;
    }

    setIsGrantingChatDemoAccess(true);

    try {
      const response = await grantChatDemoAccess(performAuthorizedRequest, activeChatId, {
        courseName: activeChatCourseDisplayName
      });
      const payload = await readJson<ApiMutationSuccess | ErrorResponse>(response);

      if (!response.ok || (payload && isErrorResponse(payload))) {
        if (payload && isErrorResponse(payload) && payload.code === 'CHAT_DEMO_PREREQUISITES_MISSING') {
          setDemoAccessDialogMode('requirements');
          return;
        }

        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось предоставить демо-доступ.');
      }

      setDemoAccessDialogMode(null);

      try {
        await refreshChatCollections(activeChatId);
        showNotification({
          tone: 'success',
          title: 'Демо-доступ предоставлен',
          message: 'Клиенту отправлено сообщение с кнопкой для перехода в личный кабинет.'
        });
      } catch {
        showNotification({
          tone: 'success',
          title: 'Демо-доступ предоставлен',
          message: 'Клиенту отправлено сообщение с кнопкой для перехода в личный кабинет. Данные в чате обновятся при следующей синхронизации.'
        });
      }
    } catch (error) {
      showNotification({
        tone: 'error',
        title: 'Не удалось предоставить демо-доступ',
        message: toErrorMessage(error, 'Демо-доступ не был предоставлен.')
      });
    } finally {
      setIsGrantingChatDemoAccess(false);
    }
  };

  const handleOpenActiveChatCourseLanding = () => {
    if (!activeChat) {
      return;
    }

    const courseHref = buildCourseHref(activeChat.pageCanonical);

    if (!courseHref) {
      return;
    }

    setIsCourseActionPopoverOpen(false);
    window.open(courseHref, '_blank', 'noopener,noreferrer');
  };

  const handleApplySelectedCourse = async () => {
    if (!composerTargetChatId || isCourseSelectionLoading) {
      return;
    }

    if (!selectedCourseId) {
      setCourseSelectionError('Выберите курс из списка.');
      return;
    }

    const selectedCourse = courseCatalogOptions.find((course) => String(course.courseId) === selectedCourseId) ?? null;

    if (!selectedCourse) {
      setCourseSelectionError('Выберите курс из списка.');
      return;
    }

    if (activeSidebarSection === 'wds') {
      resetCourseSelectionModal();
      await openCourseAttachmentModal(selectedCourse);
      return;
    }

    if (!activeChatId || !activeChat) {
      return;
    }

    const requestedChatId = activeChatId;
    const currentCourseProgramId = activeChat.courseProgramId;
    const sameCourseSelected = normalizeCourseOptionLabel(activeChat.courseName) === normalizeCourseOptionLabel(selectedCourse.courseName);

    setCourseSelectionError('');
    setIsCourseSelectionLoading(true);

    try {
      const programs = await getCoursePrograms(fetchAuthorizedJsonRef.current, selectedCourse.courseId);

      if (activeChatIdRef.current !== requestedChatId) {
        return;
      }

      if (programs.length === 0) {
        setCourseSelectionError('Для выбранного курса нет активных вариантов часов.');
        return;
      }

      setCourseProgramOptions(programs);
      setSelectedCourseProgramId(
        sameCourseSelected && currentCourseProgramId !== null && programs.some((program) => program.courseProgramId === currentCourseProgramId)
          ? String(currentCourseProgramId)
          : ''
      );
      setCourseProgramSelectionError('');
      setCourseProgramCourseName(selectedCourse.courseName);
      setIsCourseSelectionModalOpen(false);
      setIsCourseProgramModalOpen(true);
    } catch (error) {
      if (activeChatIdRef.current !== requestedChatId) {
        return;
      }

      const errorMessage = toErrorMessage(error, 'Не удалось загрузить варианты часов для выбранного курса.');

      setCourseSelectionError(errorMessage);
      showNotification({
        tone: 'error',
        title: 'Не удалось открыть выбор часов',
        message: errorMessage
      });
    } finally {
      if (activeChatIdRef.current === requestedChatId) {
        setIsCourseSelectionLoading(false);
      }
    }
  };

  const handleApplySelectedCourseProgram = async () => {
    if (!activeChatId || isApplyingChatCourseProgram) {
      return;
    }

    if (!selectedCourseProgramId) {
      setCourseProgramSelectionError('Выберите часы из списка.');
      return;
    }

    const selectedProgram = courseProgramOptions.find((program) => String(program.courseProgramId) === selectedCourseProgramId) ?? null;

    if (!selectedProgram) {
      setCourseProgramSelectionError('Выберите часы из списка.');
      return;
    }

    setIsApplyingChatCourseProgram(true);
    setCourseProgramSelectionError('');

    try {
      const response = await applyChatCourseProgram(performAuthorizedRequest, activeChatId, {
        courseProgramId: selectedProgram.courseProgramId
      });
      const payload = await readJson<ChatCourseProgramApplyResult | ErrorResponse>(response);

      if (!response.ok || !payload || isErrorResponse(payload)) {
        throw new Error(payload && isErrorResponse(payload) ? payload.error : 'Не удалось сохранить выбранный курс и часы.');
      }

      await refreshChatCollections(activeChatId);
      resetCourseSelectionModal();
      resetCourseProgramModal();
      setIsCourseActionPopoverOpen(false);

      const selectionLabel = `${selectedProgram.courseName}, ${formatCourseVolumeHours(selectedProgram.volumeHours)}`;

      showNotification({
        tone: 'success',
        title: 'Курс обновлён',
        message: payload.applicationId !== null
          ? `${selectionLabel}. Заявка ${payload.applicationId} обновлена.`
          : `${selectionLabel}. Данные сохранены для текущего чата.`
      });
    } catch (error) {
      const errorMessage = toErrorMessage(error, 'Не удалось сохранить выбранный курс.');

      setCourseProgramSelectionError(errorMessage);
      showNotification({
        tone: 'error',
        title: 'Не удалось обновить курс',
        message: errorMessage
      });
    } finally {
      setIsApplyingChatCourseProgram(false);
    }
  };

  const openChatHistoryModal = () => {
    if (activeSidebarSection === 'wds' || !activeChatThreadKey) {
      return;
    }

    setIsChatHistoryModalOpen(true);
  };

  const closeChatHistoryModal = () => {
    setIsChatHistoryModalOpen(false);
  };

  const normalizedSearch = chatSearch.trim().toLowerCase();
  const isChatsSection = activeSidebarSection === 'chats';
  const isWidgetSection = activeSidebarSection === 'wds';
  const isUisSection = activeSidebarSection === 'uis';
  const isChatToolbarEnabled = isChatsSection
    ? chatThreads.length > 0
    : isWidgetSection
      ? widgetChats.length > 0
      : false;
  const isSidebarSearchEnabled = isChatToolbarEnabled;
  const isSidebarFilterEnabled = isChatToolbarEnabled || isUisSection;
  const isSidebarFilterActive = isUisSection
    ? isUisFilterPanelOpen || uisActiveFilter !== 'all'
    : isChatToolbarEnabled && isUnreadFilterEnabled;
  const sidebarFilterAriaLabel = isUisSection
    ? isUisFilterPanelOpen
      ? 'Скрыть фильтры звонков UIS'
      : 'Показать фильтры звонков UIS'
    : isUnreadFilterEnabled
      ? 'Отключить фильтр непрочитанных чатов'
      : 'Включить фильтр непрочитанных чатов';
  const currentSidebarSectionLabel = SIDEBAR_SECTIONS.find((section) => section.id === activeSidebarSection)?.label ?? 'ЧАТЫ';
  const currentSidebarSectionPlaceholderText = activeSidebarSection === 'chats' || activeSidebarSection === 'wds'
    ? null
    : SIDEBAR_SECTION_PLACEHOLDER_TEXT[activeSidebarSection];
  const totalUnreadMessages = chatThreads.reduce((total, chat) => total + Math.max(0, chat.unreadCount), 0);
  const totalUnreadMessagesBadge = totalUnreadMessages > 99 ? '99+' : String(totalUnreadMessages);
  const totalUnreadWidgetMessages = widgetChats.reduce((total, chat) => total + Math.max(0, chat.unreadCount), 0);
  const totalUnreadWidgetMessagesBadge = totalUnreadWidgetMessages > 99 ? '99+' : String(totalUnreadWidgetMessages);
  const totalPendingUisMissedCalls = useMemo(
    () => countPendingMissedUisGroups(uisOverview?.calls ?? []),
    [uisOverview?.calls]
  );
  const totalPendingUisMissedCallsBadge = totalPendingUisMissedCalls > 99 ? '99+' : String(totalPendingUisMissedCalls);
  const isAiAutoresponderEnabled = aiAutoresponderSettings?.enabled ?? true;
  const isReminderEditMode = editingReminderId !== null;
  const reminderModalTitle = isReminderEditMode ? 'Редактировать уведомление' : 'Создать уведомление';
  const reminderModalDescription = isReminderEditMode
    ? 'Изменения сохранятся только в истории этого чата и не будут отправлены клиенту.'
    : 'Уведомление появится только в истории этого чата и не будет отправлено клиенту.';
  const reminderModalSubmitLabel = createReminderPending
    ? isReminderEditMode
      ? 'Сохраняем...'
      : 'Создаём...'
    : isReminderEditMode
      ? 'Сохранить изменения'
      : 'Создать уведомление';

  useEffect(() => {
    if (!isUisSection) {
      setIsUisFilterPanelOpen(false);
    }
  }, [isUisSection]);
  const canViewActiveChatHistory = !isWidgetSection && activeChat !== null;
  const filteredChatThreads = chatThreads.filter((chatThread) => {
    if (isUnreadFilterEnabled && chatThread.unreadCount < 1) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    return buildChatThreadSearchTarget(chatThread).includes(normalizedSearch);
  });
  const filteredWidgetChats = widgetChats.filter((chat) => {
    if (isUnreadFilterEnabled && chat.unreadCount < 1) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    return buildWidgetChatSearchTarget(chat).includes(normalizedSearch);
  });
  const activeChatListThreadId = resolveChatThreadId(filteredChatThreads, activeChatId);

  useEffect(() => {
    if (authViewState !== 'authenticated' || !isChatsSection || activeChatListThreadId === null) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      activeChatListRowRef.current?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      });
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [activeChatListThreadId, authViewState, isChatsSection]);

  const canSubmitComposer = Boolean(chatDraft.trim() || composerFiles.length > 0);
  const composerPlaceholder = composerFiles.length > 0
    ? 'Добавьте подпись к файлам'
    : isWidgetSection ? 'Введите сообщение посетителю сайта' : 'Введите сообщение клиенту';
  const selectedCourseAttachmentFiles = courseAttachmentFiles.filter((file) => selectedCourseAttachmentIds.includes(file.attachmentId));
  const selectedCourseAttachmentCount = selectedCourseAttachmentFiles.length;
  const selectedCourseAttachmentIdSet = new Set(selectedCourseAttachmentIds);
  const isCourseAttachmentStudyPlanFile = (file: ChatCourseAttachmentFile) => {
    const attachmentType = file.attachmentType.trim().toLowerCase();
    const fileName = file.fileName.trim().toLowerCase();

    return file.attachmentId.endsWith(':doc_path') || attachmentType.includes('учебный план') || fileName.includes('учебный_план');
  };
  const getCourseAttachmentGroupTitle = (file: ChatCourseAttachmentFile) => isCourseAttachmentStudyPlanFile(file)
    ? 'Учебный план'
    : file.attachmentType || 'Файлы курса';
  const getCourseAttachmentGroupSortOrder = (title: string) => {
    const normalizedTitle = title.trim().toLowerCase();

    if (normalizedTitle === 'образец удостоверения') {
      return 10;
    }

    if (normalizedTitle === 'образец свидетельства') {
      return 20;
    }

    if (normalizedTitle === 'образец диплома') {
      return 30;
    }

    if (normalizedTitle === 'лицензия на образовательную деятельность') {
      return 40;
    }

    if (normalizedTitle === 'учебный план') {
      return 1000;
    }

    return 100;
  };
  const courseAttachmentGroups = courseAttachmentFiles.reduce<Array<{ groupKey: string; title: string; files: ChatCourseAttachmentFile[]; orderIndex: number }>>((groups, file, fileIndex) => {
    const title = getCourseAttachmentGroupTitle(file);
    const existingGroup = groups.find((group) => group.groupKey === title);

    if (existingGroup) {
      existingGroup.files.push(file);
      return groups;
    }

    groups.push({
      groupKey: title,
      title,
      files: [file],
      orderIndex: fileIndex
    });

    return groups;
  }, []).sort((firstGroup, secondGroup) => {
    const orderDelta = getCourseAttachmentGroupSortOrder(firstGroup.title) - getCourseAttachmentGroupSortOrder(secondGroup.title);

    return orderDelta || firstGroup.orderIndex - secondGroup.orderIndex;
  });

  const renderCourseAttachmentOption = (file: ChatCourseAttachmentFile) => {
    const isSelected = selectedCourseAttachmentIdSet.has(file.attachmentId);
    const displayFileName = formatCourseAttachmentDisplayFileName(file.fileName);
    const fileSizeLabel = formatFileSize(file.fileSize);
    const volumeLabel = formatCourseVolumeHours(file.volumeHours);
    const isTooLarge = file.fileSize !== null && file.fileSize > COMPOSER_MAX_FILE_SIZE_BYTES;
    const metaParts = [file.attachmentType, volumeLabel, file.programName, fileSizeLabel].filter(Boolean);

    return (
      <label className="telegram-course-attachment-option" key={file.attachmentId} title={isTooLarge ? 'Файл больше лимита 25 МБ' : displayFileName}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleCourseAttachmentSelectionChange(file.attachmentId)}
          disabled={isCourseAttachmentFilesLoading || isAttachingCourseFiles || isTooLarge}
        />

        <span className="telegram-course-attachment-option__card">
          <span className="telegram-course-attachment-option__check" aria-hidden="true" />
          <span className="telegram-course-attachment-option__body">
            <span className="telegram-course-attachment-option__title">{displayFileName}</span>
            <span className="telegram-course-attachment-option__meta">
              {metaParts.join(' · ')}{isTooLarge ? ' · больше 25 МБ' : ''}
            </span>
          </span>
        </span>
      </label>
    );
  };

  const renderCourseAttachmentGroup = (group: { groupKey: string; title: string; files: ChatCourseAttachmentFile[] }, groupIndex: number) => {
    if (group.files.length === 1) {
      return renderCourseAttachmentOption(group.files[0]);
    }

    const groupStateId = `telegram-course-attachment-group-state-${groupIndex}`;

    return (
      <div className="telegram-course-attachment-collapse-block" key={group.groupKey}>
        <input className="telegram-course-attachment-collapse__state" id={groupStateId} type="checkbox" />

        <label className="telegram-course-attachment-collapse" htmlFor={groupStateId}>
          <span className="telegram-course-attachment-collapse__title">{group.title}</span>
          <span className="telegram-course-attachment-collapse__count">{group.files.length}</span>
          <span className="telegram-course-attachment-collapse__chevron" aria-hidden="true" />
        </label>

        <div className="telegram-course-attachment-collapse__items">
          {group.files.map((file) => renderCourseAttachmentOption(file))}
        </div>
      </div>
    );
  };

  const stopNestedAction = (event: ReactMouseEvent<HTMLElement> | ReactKeyboardEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const handleChatRowKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>, chatId: number) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleChatSelect(chatId);
  };

  const handleWidgetChatRowKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>, chatId: number) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleWidgetChatSelect(chatId);
  };

  const handleAvatarLoadError = (identityKey: string, avatarUrl: string | null) => {
    if (!avatarUrl) {
      return;
    }

    const avatarStateKey = buildAvatarStateKey(identityKey, avatarUrl);

    setAvatarLoadAttempts((current) => {
      const currentAttempt = current[avatarStateKey] ?? 0;

      if (currentAttempt >= 2) {
        return current;
      }

      return {
        ...current,
        [avatarStateKey]: (currentAttempt + 1) as AvatarLoadAttempt
      };
    });
  };

  const renderChatAvatar = (
    identityKey: string,
    avatarUrl: string | null,
    title: string,
    fallbackLabel: string,
    imageClassName: string,
    fallbackClassName: string
  ) => {
    if (!avatarUrl) {
      return <span className={fallbackClassName}>{fallbackLabel}</span>;
    }

    const avatarSrc = resolveAvatarImageSrc(identityKey, avatarUrl, avatarLoadAttempts);

    if (!avatarSrc) {
      return <span className={fallbackClassName}>{fallbackLabel}</span>;
    }

    return (
      <img
        className={imageClassName}
        src={avatarSrc}
        alt={title}
        onError={() => handleAvatarLoadError(identityKey, avatarUrl)}
      />
    );
  };

  const openActiveChatAvatarPreview = () => {
    if (!activeChat) {
      return;
    }

    const avatarSrc = resolveAvatarImageSrc(activeChat.key, activeChat.avatarUrl, avatarLoadAttempts);

    if (!avatarSrc) {
      return;
    }

    setActiveAvatarPreview({
      title: activeChat.title,
      imageUrl: avatarSrc
    });
  };

  const renderChatRow = (chat: ChatThreadSummary) => {
    const phoneLabel = formatDisplayPhoneNumber(chat.phoneNumber);
    const phoneDisplayLabel = phoneLabel ?? '----';
    const courseDisplayLabel = chat.courseName ?? chat.courseTitle ?? '----';
    const hasUnreadMessages = chat.unreadCount > 0;
    const applicationDisplayLabel = formatChatCardApplicationLabel(chat.crmApplicationId);
    const linkedWidgetChat = chat.linkedWidgetChats[0] ?? null;
    const isChatAiTogglePending = chat.chatIds.some((chatId) => chatAiAutoresponderUpdatePendingIds.includes(chatId));
    const isChatAiToggleChecked = isAiAutoresponderEnabled && chat.aiAutoresponderEnabled;
    const isChatAiToggleDisabled = !isAiAutoresponderEnabled || isChatAiTogglePending;
    const chatAiToggleTitle = !isAiAutoresponderEnabled
      ? `Глобальный Автоответчик AI выключен. Индивидуально для этого чата: ${chat.aiAutoresponderEnabled ? 'включён' : 'выключен'}.`
      : chat.aiAutoresponderEnabled
        ? 'AI-автоответчик включён для этого чата'
        : 'AI-автоответчик выключен для этого чата';

    return (
      <div
        key={chat.key}
        ref={chat.chatId === activeChatListThreadId ? activeChatListRowRef : undefined}
        className={`telegram-chat-row${chat.chatId === activeChatId ? ' telegram-chat-row--active' : ''}${chat.hasTriggeredReminder ? ' telegram-chat-row--reminder-due' : ''}`}
        role="button"
        tabIndex={0}
        aria-pressed={chat.chatId === activeChatId}
        onClick={() => handleChatSelect(chat.chatId)}
        onKeyDown={(event) => handleChatRowKeyDown(event, chat.chatId)}
      >
        <span className="telegram-chat-row__sidebar">
          {renderChatAvatar(
            chat.key,
            chat.avatarUrl,
            chat.title,
            getTitleInitials(chat.title),
            `telegram-chat-row__avatar telegram-chat-row__avatar--${chat.platform}`,
            'telegram-chat-row__avatar telegram-chat-row__avatar--fallback'
          )}
        </span>

        <span className="telegram-chat-row__content">
          <span className="telegram-chat-row__head">
            <strong>{chat.title}</strong>
            <span className="telegram-chat-row__head-side">
              {hasUnreadMessages ? (
                <span className="telegram-chat-row__unread-badge">
                  <span className="telegram-chat-row__unread-badge-text">{formatUnreadCount(chat.unreadCount)}</span>
                </span>
              ) : null}
              <time>{formatChatListTimestamp(chat.lastMessageAt ?? chat.createdAt)}</time>
            </span>
          </span>

          <span className="telegram-chat-row__meta">
            {phoneLabel ? (
              <span className="telegram-info-pill telegram-info-pill--phone">{phoneLabel}</span>
            ) : (
              <span className="telegram-info-pill telegram-info-pill--phone">{phoneDisplayLabel}</span>
            )}
            <span className="telegram-info-pill telegram-info-pill--application telegram-chat-row__application-pill">{applicationDisplayLabel}</span>
            {chat.email ? <span>{chat.email}</span> : null}
          </span>
        </span>

        <span className="telegram-chat-row__chips telegram-chat-row__chips--course">
          {chat.hasMaxChat ? <span className="telegram-platform-badge telegram-platform-badge--max">MAX</span> : null}
          {chat.hasTelegramChat ? <span className="telegram-platform-badge telegram-platform-badge--telegram">TG</span> : null}
          {linkedWidgetChat ? (
            <button
              type="button"
              className="telegram-platform-badge telegram-platform-badge--widget telegram-platform-badge--link"
              onClick={(event) => handleLinkedWidgetChatOpen(event, linkedWidgetChat.chatId)}
              onKeyDown={stopNestedAction}
              title={`${formatWidgetPageLabel(linkedWidgetChat)}${formatWidgetPageCaption(linkedWidgetChat) ? ` • ${formatWidgetPageCaption(linkedWidgetChat)}` : ''}`}
            >
              WDS
            </button>
          ) : null}
          <span className="telegram-info-pill telegram-info-pill--course" title={courseDisplayLabel}>
            <span className="telegram-chat-row__course-pill-text">{courseDisplayLabel}</span>
          </span>
          <label
            className={`telegram-chat-ai-toggle${isChatAiToggleChecked ? ' is-active' : ''}${isChatAiToggleDisabled ? ' is-disabled' : ''}${isChatAiTogglePending ? ' is-pending' : ''}`}
            title={chatAiToggleTitle}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={stopNestedAction}
          >
            <span className="telegram-chat-ai-toggle__label">AI</span>
            <input
              type="checkbox"
              checked={isChatAiToggleChecked}
              disabled={isChatAiToggleDisabled}
              onChange={(event) => void handleChatAiAutoresponderToggle(event, chat)}
              aria-label={chat.aiAutoresponderEnabled ? 'Выключить AI для этого чата' : 'Включить AI для этого чата'}
            />
            <span className="telegram-chat-ai-toggle__switch" aria-hidden="true">
              <span className="telegram-chat-ai-toggle__thumb" />
            </span>
          </label>
        </span>
      </div>
    );
  };

  const renderWidgetChatRow = (chat: WidgetChatSummary) => {
    const hasUnreadMessages = chat.unreadCount > 0;
    const currentPageLabel = formatWidgetPageLabel(chat);

    return (
      <div
        key={chat.chatId}
        className={`telegram-chat-row${chat.chatId === activeWidgetChatId ? ' telegram-chat-row--active' : ''}${chat.hasTriggeredReminder ? ' telegram-chat-row--reminder-due' : ''}`}
        role="button"
        tabIndex={0}
        aria-pressed={chat.chatId === activeWidgetChatId}
        onClick={() => handleWidgetChatSelect(chat.chatId)}
        onKeyDown={(event) => handleWidgetChatRowKeyDown(event, chat.chatId)}
      >
        <span className="telegram-chat-row__sidebar">
          <img className="telegram-chat-row__avatar telegram-chat-row__avatar--widget" src={widgetChatAvatarUrl} alt={chat.title} />
        </span>

        <span className="telegram-chat-row__content">
          <span className="telegram-chat-row__head">
            <strong>{chat.title}</strong>
            <span className="telegram-chat-row__head-side">
              {hasUnreadMessages ? (
                <span className="telegram-chat-row__unread-badge telegram-chat-row__unread-badge--widget">
                  <span className="telegram-chat-row__unread-badge-text">{formatUnreadCount(chat.unreadCount)}</span>
                </span>
              ) : null}
              <time>{formatChatListTimestamp(chat.lastMessageAt ?? chat.createdAt)}</time>
            </span>
          </span>

          <span className="telegram-chat-row__meta telegram-chat-row__meta--widget">
            <span className="telegram-info-pill telegram-info-pill--course telegram-chat-row__widget-page-pill" title={currentPageLabel}>
              <span className="telegram-chat-row__pill-text">{currentPageLabel}</span>
            </span>
          </span>
        </span>
      </div>
    );
  };

  const renderMessageMetadata = (message: OperatorChatMessage) => {
    const metadata = getRecord(message.metadata);

    if (!metadata) {
      return null;
    }

    if (message.messageType === 'contact') {
      const phoneNumber = getRecordString(metadata, 'phoneNumber');
      const phoneHref = buildPhoneHref(phoneNumber);
      const firstName = getRecordString(metadata, 'firstName');
      const lastName = getRecordString(metadata, 'lastName');
      const fullName = [firstName, lastName].filter(Boolean).join(' ');

      return (
        <div className="telegram-message__info-card">
          <strong>{fullName || 'Клиент поделился контактом'}</strong>
          {phoneNumber ? (
            phoneHref ? <a href={phoneHref} target="_blank" rel="noreferrer">{phoneNumber}</a> : <span>{phoneNumber}</span>
          ) : null}
        </div>
      );
    }

    if (message.messageType === 'location') {
      const latitude = getRecordNumber(metadata, 'latitude');
      const longitude = getRecordNumber(metadata, 'longitude');

      if (latitude === null || longitude === null) {
        return null;
      }

      const mapUrl = `https://maps.google.com/?q=${latitude},${longitude}`;

      return (
        <div className="telegram-message__info-card">
          <strong>Геопозиция</strong>
          <a href={mapUrl} target="_blank" rel="noreferrer">{`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`}</a>
        </div>
      );
    }

    if (message.messageType === 'share') {
      const url = getRecordString(metadata, 'url');
      const title = getRecordString(metadata, 'title');

      if (!url) {
        return null;
      }

      return (
        <div className="telegram-message__info-card">
          <strong>{title || 'Ссылка'}</strong>
          <a href={url} target="_blank" rel="noreferrer">{url}</a>
        </div>
      );
    }

    const actionButton = getRecord(metadata.actionButton);
    const actionButtonText = getRecordString(actionButton, 'text');
    const actionButtonHref = buildMessageActionHref(getRecordString(actionButton, 'url'));

    if (actionButtonText && actionButtonHref) {
      return (
        <a className="telegram-message__action-button" href={actionButtonHref} target="_blank" rel="noreferrer">
          {actionButtonText}
        </a>
      );
    }

    return null;
  };

  const renderReminderMessage = (message: OperatorChatMessage) => {
    if (!isOperatorReminderMessage(message)) {
      return null;
    }

    const isDismissingReminder = dismissingReminderId === message.reminderId;
    const isReminderEditable = !message.isReminderTriggered && !message.isReminderDismissed;
    const reminderCreatorName = getReminderCreatorName(message);
    const reminderTitle = message.isReminderDismissed
      ? 'Уведомление отключено'
      : message.isReminderTriggered
        ? 'Уведомление сработало'
        : 'Напоминание для оператора';
    const reminderCaption = message.isReminderDismissed
      ? 'Подсветка остановлена. Запись сохранена в истории чата.'
      : message.isReminderTriggered
        ? 'Чат поднят наверх списка и ждёт реакции оператора.'
        : null;
    const reminderTime = message.reminderAt ?? message.sentAt;
    const formattedReminderTimestamp = formatMessagePanelTimestamp(reminderTime, 'в');
    const reminderChipLabel = reminderCreatorName ?? (message.isReminderDismissed ? 'Отключено' : message.isReminderTriggered ? 'Сработало' : 'Запланировано');

    return (
      <article
        key={message.messageId}
        className={`telegram-message telegram-message--system telegram-message--reminder${message.isReminderTriggered ? ' telegram-message--reminder-due' : ''}${message.isReminderDismissed ? ' telegram-message--reminder-dismissed' : ''}`}
      >
        <div className="telegram-message__meta telegram-message__meta--reminder">
          <strong>{reminderTitle}</strong>
          <span className="telegram-message__reminder-chip">{reminderChipLabel}</span>
        </div>

        <div className="telegram-message__reminder-panel">
          {reminderCaption ? <p className="telegram-message__reminder-caption">{reminderCaption}</p> : null}

          {message.textContent?.trim() ? (
            <p className="telegram-message__text">{message.textContent}</p>
          ) : (
            <p className="telegram-message__text telegram-message__text--muted">Комментарий не указан.</p>
          )}
        </div>

        <div className="telegram-message__footer">
          {message.isReminderDismissed ? (
            <span className="telegram-message__read-badge telegram-message__read-badge--read">Уведомление отключено</span>
          ) : (
            <div className="telegram-message__reminder-actions">
              {isReminderEditable ? (
                <button
                  type="button"
                  className="telegram-message__reminder-action telegram-message__reminder-action--edit"
                  onClick={() => openReminderEditModal(message)}
                  disabled={createReminderPending || isDismissingReminder}
                >
                  Редактировать
                </button>
              ) : null}

              <button
                type="button"
                className="telegram-message__reminder-action"
                onClick={() => void handleDismissReminder(message)}
                disabled={isDismissingReminder}
              >
                {isDismissingReminder ? 'Отключаем...' : 'Отключить уведомление'}
              </button>
            </div>
          )}

          <time className="telegram-message__footer-timestamp" dateTime={reminderTime}>{formattedReminderTimestamp}</time>
        </div>
      </article>
    );
  };

  const renderFilePreviewContent = (preview: FilePreviewModalState) => {
    if (preview.isLoading) {
      return (
        <div className="telegram-file-preview-dialog__placeholder">
          <span className="telegram-chat-list__pulse" aria-hidden="true" />
          <strong>Подготавливаем файл</strong>
          <p>Предпросмотр откроется сразу после загрузки содержимого.</p>
        </div>
      );
    }

    if (preview.errorMessage) {
      return (
        <div className="telegram-file-preview-dialog__placeholder telegram-file-preview-dialog__placeholder--error">
          <strong>Не удалось открыть файл</strong>
          <p>{preview.errorMessage}</p>
        </div>
      );
    }

    const previewMode = getFilePreviewMode(preview.file);
    const fileName = getAttachmentFileName(preview.file);

    if (previewMode === 'document' && isDocxFile(preview.file)) {
      if (!preview.sourceBlob) {
        return (
          <div className="telegram-file-preview-dialog__placeholder telegram-file-preview-dialog__placeholder--error">
            <strong>Предпросмотр недоступен</strong>
            <p>Содержимое документа Word пока не удалось подготовить для просмотра.</p>
          </div>
        );
      }

      return (
        <div className="telegram-file-preview-dialog__document telegram-file-preview-dialog__document--docx">
          <DocxPreviewRenderer blob={preview.sourceBlob} />
        </div>
      );
    }

    if (previewMode === 'document' && preview.documentPreview) {
      if (preview.documentPreview.kind === 'html') {
        return (
          <div className="telegram-file-preview-dialog__document telegram-file-preview-dialog__document--html">
            <iframe
              className="telegram-file-preview-dialog__html-frame"
              srcDoc={buildDocumentPreviewHtmlFrame(preview.documentPreview.html)}
              title={fileName}
              sandbox=""
            />
            {preview.documentPreview.truncated ? (
              <p className="telegram-file-preview-dialog__document-note">Показана только часть содержимого документа.</p>
            ) : null}
          </div>
        );
      }

      if (preview.documentPreview.kind === 'text') {
        return (
          <div className="telegram-file-preview-dialog__document telegram-file-preview-dialog__document--text">
            <pre>{preview.documentPreview.text || 'В документе не найдено доступного для показа текста.'}</pre>
            {preview.documentPreview.truncated ? (
              <p className="telegram-file-preview-dialog__document-note">Показана только часть содержимого документа.</p>
            ) : null}
          </div>
        );
      }

      return (
        <div className="telegram-file-preview-dialog__document telegram-file-preview-dialog__document--sheets">
          {preview.documentPreview.sheets.length === 0 ? (
            <div className="telegram-file-preview-dialog__placeholder">
              <strong>Листы не найдены</strong>
              <p>В таблице нет данных, доступных для отображения в модальном окне.</p>
            </div>
          ) : preview.documentPreview.sheets.map((sheet) => (
            <section key={sheet.name} className="telegram-file-preview-dialog__sheet">
              <div className="telegram-file-preview-dialog__sheet-header">
                <strong>{sheet.name}</strong>
                <span>{sheet.rows.length > 0 ? `${sheet.rows.length} строк` : 'Пустой лист'}</span>
              </div>

              {sheet.rows.length > 0 ? (
                <div className="telegram-file-preview-dialog__sheet-table-wrap">
                  <table className="telegram-file-preview-dialog__sheet-table">
                    <tbody>
                      {sheet.rows.map((row, rowIndex) => (
                        <tr key={`${sheet.name}:${rowIndex}`}>
                          {row.map((cell, cellIndex) => (
                            <td key={`${sheet.name}:${rowIndex}:${cellIndex}`}>{cell || ' '}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="telegram-file-preview-dialog__sheet-empty">На этом листе нет данных для показа.</p>
              )}
            </section>
          ))}

          {preview.documentPreview.truncated ? (
            <p className="telegram-file-preview-dialog__document-note">Предпросмотр сокращён: показана только часть листов, строк или столбцов.</p>
          ) : null}
        </div>
      );
    }

    if (!preview.objectUrl) {
      return (
        <div className="telegram-file-preview-dialog__placeholder telegram-file-preview-dialog__placeholder--error">
          <strong>Предпросмотр недоступен</strong>
          <p>Содержимое файла пока не удалось подготовить для просмотра.</p>
        </div>
      );
    }

    if (previewMode === 'image') {
      return <img className="telegram-file-preview-dialog__image" src={preview.objectUrl} alt={fileName} />;
    }

    if (previewMode === 'video') {
      return <video className="telegram-file-preview-dialog__video" controls preload="metadata" src={preview.objectUrl} />;
    }

    if (previewMode === 'audio') {
      return <audio className="telegram-file-preview-dialog__audio" controls preload="metadata" src={preview.objectUrl} />;
    }

    if (previewMode === 'document' && isPdfFile(preview.file)) {
      return (
        <object
          className="telegram-file-preview-dialog__frame"
          data={preview.objectUrl}
          type={getAttachmentMimeType(preview.file) ?? 'application/pdf'}
          aria-label={fileName}
        >
          <div className="telegram-file-preview-dialog__placeholder">
            <strong>Предпросмотр PDF недоступен</strong>
            <p>Браузер не смог встроенно открыть документ. Попробуйте открыть файл в новой вкладке или скачать его.</p>
            <div className="telegram-file-preview-dialog__actions">
              <a className="telegram-file-preview-dialog__link" href={preview.objectUrl} target="_blank" rel="noreferrer">
                Открыть PDF
              </a>
            </div>
          </div>
        </object>
      );
    }

    return (
      <object
        className="telegram-file-preview-dialog__frame"
        data={preview.objectUrl}
        type={getAttachmentMimeType(preview.file) ?? (isPdfFile(preview.file) ? 'application/pdf' : undefined)}
        aria-label={fileName}
      >
        <div className="telegram-file-preview-dialog__placeholder">
          <strong>Предпросмотр в браузере недоступен</strong>
          <p>Откройте файл в новой вкладке или скачайте его на устройство.</p>
          <div className="telegram-file-preview-dialog__actions">
            <a className="telegram-file-preview-dialog__link" href={preview.objectUrl} target="_blank" rel="noreferrer">
              Открыть файл
            </a>
          </div>
        </div>
      </object>
    );
  };

  const renderMessageFile = (file: OperatorChatMessageFile, message: DisplayedChatMessage) => {
    const fileKey = getMessageFileKey(file);
    const fileMeta = buildAttachmentMetaLabel(file);
    const fileName = getAttachmentFileName(file);

    if (shouldRenderAttachmentAsFileCard(file)) {

      return (
        <div
          key={file.fileId}
          className="telegram-attachment telegram-attachment--file-card"
          role="button"
          tabIndex={0}
          onClick={() => void handleAttachmentPreview(file)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              void handleAttachmentPreview(file);
            }
          }}
          aria-label={`Открыть файл ${fileName}`}
        >
          <div className="telegram-attachment__caption telegram-attachment__caption--file-card">
            <strong title={fileName}>{fileName}</strong>
            {fileMeta ? <span>{fileMeta}</span> : null}
          </div>
          <button
            type="button"
            className="telegram-attachment__action telegram-attachment__action--icon telegram-attachment__action--card"
            onClick={(event) => {
              event.stopPropagation();
              void handleAttachmentDownload(file);
            }}
            aria-label="Скачать файл"
            title="Скачать файл"
          >
            <DownloadIcon width="16px" height="16px" color="currentColor" opacity="1" />
          </button>
        </div>
      );
    }

    if (isAudioFile(file)) {
      const objectUrl = filePreviewUrls[fileKey] ?? null;
      const playbackState = audioPlaybackStates[fileKey] ?? {
        currentTimeSeconds: 0,
        durationSeconds: file.durationSeconds,
        isPlaying: false
      };
      const remainingDurationLabel = formatAudioRemainingDuration(playbackState.durationSeconds ?? file.durationSeconds, playbackState.currentTimeSeconds);
      const audioMetaLabel = [
        formatFileSize(file.fileSize) ? `Размер: ${formatFileSize(file.fileSize)}` : null,
        `${playbackState.isPlaying ? 'Осталось' : 'Длительность'}: ${remainingDurationLabel}`
      ].filter(Boolean).join(' • ');

      return (
        <div key={file.fileId} className="telegram-attachment telegram-attachment--audio">
          {objectUrl ? (
            <audio
              ref={(element) => {
                if (element) {
                  audioElementRefs.current[fileKey] = element;
                } else {
                  delete audioElementRefs.current[fileKey];
                }
              }}
              className="telegram-attachment__audio-element"
              preload="metadata"
              src={objectUrl}
              onLoadedMetadata={(event) => {
                const audioElement = event.currentTarget;
                const durationSeconds = Number.isFinite(audioElement.duration) && audioElement.duration > 0
                  ? Math.ceil(audioElement.duration)
                  : file.durationSeconds;

                setAudioPlaybackStates((current) => ({
                  ...current,
                  [fileKey]: {
                    currentTimeSeconds: Math.max(0, audioElement.currentTime),
                    durationSeconds,
                    isPlaying: !audioElement.paused && !audioElement.ended
                  }
                }));
              }}
              onTimeUpdate={(event) => {
                const audioElement = event.currentTarget;
                const durationSeconds = Number.isFinite(audioElement.duration) && audioElement.duration > 0
                  ? Math.ceil(audioElement.duration)
                  : file.durationSeconds;

                setAudioPlaybackStates((current) => ({
                  ...current,
                  [fileKey]: {
                    currentTimeSeconds: Math.max(0, audioElement.currentTime),
                    durationSeconds,
                    isPlaying: !audioElement.paused && !audioElement.ended
                  }
                }));
              }}
              onPlay={(event) => {
                const audioElement = event.currentTarget;
                const durationSeconds = Number.isFinite(audioElement.duration) && audioElement.duration > 0
                  ? Math.ceil(audioElement.duration)
                  : file.durationSeconds;

                setAudioPlaybackStates((current) => ({
                  ...current,
                  [fileKey]: {
                    currentTimeSeconds: Math.max(0, audioElement.currentTime),
                    durationSeconds,
                    isPlaying: true
                  }
                }));
              }}
              onPause={(event) => {
                const audioElement = event.currentTarget;
                const durationSeconds = Number.isFinite(audioElement.duration) && audioElement.duration > 0
                  ? Math.ceil(audioElement.duration)
                  : file.durationSeconds;

                setAudioPlaybackStates((current) => ({
                  ...current,
                  [fileKey]: {
                    currentTimeSeconds: Math.max(0, audioElement.currentTime),
                    durationSeconds,
                    isPlaying: false
                  }
                }));
              }}
              onEnded={(event) => {
                const audioElement = event.currentTarget;
                const durationSeconds = Number.isFinite(audioElement.duration) && audioElement.duration > 0
                  ? Math.ceil(audioElement.duration)
                  : file.durationSeconds;

                audioElement.currentTime = 0;

                setAudioPlaybackStates((current) => ({
                  ...current,
                  [fileKey]: {
                    currentTimeSeconds: 0,
                    durationSeconds,
                    isPlaying: false
                  }
                }));
              }}
            />
          ) : null}

          <button
            type="button"
            className={`telegram-attachment__player-button${playbackState.isPlaying ? ' is-playing' : ''}`}
            onClick={() => {
              const audioElement = audioElementRefs.current[fileKey];

              if (!audioElement) {
                return;
              }

              if (!audioElement.paused) {
                audioElement.pause();
                return;
              }

              Object.entries(audioElementRefs.current).forEach(([storedFileId, element]) => {
                if (storedFileId !== fileKey && element && !element.paused) {
                  element.pause();
                }
              });

              void audioElement.play().catch((error) => {
                console.error(error);
              });
            }}
            disabled={!objectUrl}
            aria-label={playbackState.isPlaying ? 'Пауза' : 'Воспроизвести аудио'}
            title={playbackState.isPlaying ? 'Пауза' : 'Воспроизвести аудио'}
          >
            {playbackState.isPlaying ? (
              <PauseIcon width="18px" height="18px" color="currentColor" opacity="1" />
            ) : (
              <PlayIcon width="18px" height="18px" color="currentColor" opacity="1" />
            )}
          </button>

          <div className="telegram-attachment__caption">
            <strong>{buildAudioAttachmentTitle(file)}</strong>
            {audioMetaLabel ? <span>{audioMetaLabel}</span> : null}
          </div>
          <button
            type="button"
            className="telegram-attachment__action telegram-attachment__action--icon"
            onClick={() => handleAttachmentDownload(file)}
            aria-label="Скачать аудио"
            title="Скачать аудио"
          >
            <DownloadIcon width="18px" height="18px" color="currentColor" opacity="1" />
          </button>
        </div>
      );
    }

    return null;
  };

  const deleteConfirmPreviewText = deleteConfirmMessage
    ? (deleteConfirmMessage.textContent?.trim() || buildMessageFallback(deleteConfirmMessage) || 'Сообщение без текста')
    : '';

  const notificationStack = notifications.length ? (
    <div className="notification-stack" aria-live="polite" aria-atomic="true">
      {notifications.map((notification) => (
        <Notification key={notification.id} {...notification} onClose={dismissNotification} />
      ))}
    </div>
  ) : null;

  if (authViewState === 'booting') {
    return (
      <LoginContainer
        theme={theme}
        stageBackground={stageBackground}
        serviceIconUrl={serviceIconUrl}
        isBooting
        loginValue={loginFormState.login}
        passwordValue={loginFormState.password}
        loginPending={loginPending}
        notificationStack={notificationStack}
        onSubmit={handleLoginSubmit}
        onLoginChange={handleLoginFieldChange('login')}
        onPasswordChange={handleLoginFieldChange('password')}
      />
    );
  }

  if (authViewState !== 'authenticated' || !authSession) {
    return (
      <LoginContainer
        theme={theme}
        stageBackground={stageBackground}
        serviceIconUrl={serviceIconUrl}
        isBooting={false}
        loginValue={loginFormState.login}
        passwordValue={loginFormState.password}
        loginPending={loginPending}
        notificationStack={notificationStack}
        onSubmit={handleLoginSubmit}
        onLoginChange={handleLoginFieldChange('login')}
        onPasswordChange={handleLoginFieldChange('password')}
      />
    );
  }

  const operator = authSession.operator;
  const operatorInitials = getOperatorInitials(operator);
  const activeChatPhoneHref = activeChat ? buildPhoneHref(activeChat.phoneNumber) : null;
  const activeChatApplicationHref = activeChat ? buildApplicationHref(activeChat.crmApplicationId) : null;
  const activeChatCourseHref = activeChat ? buildCourseHref(activeChat.pageCanonical) : null;
  const activeChatHasCourse = Boolean(activeChat?.courseName?.trim());
  const activeChatHasSelectableHours = Boolean(activeChat && activeChat.volumeHours !== null && activeChat.courseProgramId !== null);
  const isCourseActionBusy = isCourseSelectionLoading || isCourseProgramLoading || isApplyingChatCourseProgram;
  const activeWidgetPageHref = activeWidgetChat ? buildWidgetPageHref(activeWidgetChat.currentPageUrl) : null;
  const currentChatPhoneCountry = getPhoneCountryConfig(chatPhoneCountry);
  const chatPhonePreviewLabel = formatPhoneValue(chatPhoneInput, chatPhoneCountry);
  const normalizedCourseSearchQuery = normalizeCourseOptionLabel(courseSearchQuery);
  const filteredCourseCatalogOptions = normalizedCourseSearchQuery
    ? courseCatalogOptions.filter((course) => normalizeCourseOptionLabel(course.courseName).includes(normalizedCourseSearchQuery))
    : courseCatalogOptions;
  const isSelectedCourseVisible = selectedCourseId.length > 0
    && filteredCourseCatalogOptions.some((course) => String(course.courseId) === selectedCourseId);
  const reminderMinDateTimeValue = buildReminderMinDateTimeValue();

  const renderActiveChatPhoneBadge = (className: string) => {
    const phoneLabel = formatDisplayPhoneNumber(activeChat?.phoneNumber) ?? 'Телефон не указан';
    const badgeClassName = `telegram-info-pill telegram-info-pill--phone ${className}`;

    if (activeChat?.phoneNumber && activeChatPhoneHref) {
      return (
        <a className={`${badgeClassName} telegram-info-pill--link`} href={activeChatPhoneHref} target="_blank" rel="noreferrer">
          {phoneLabel}
        </a>
      );
    }

    if (!activeChat) {
      return <span className={badgeClassName}>{phoneLabel}</span>;
    }

    return (
      <button
        className={`${badgeClassName} telegram-info-pill--button telegram-info-pill--link`}
        type="button"
        onClick={openChatPhoneModal}
        title="Указать номер телефона клиента"
      >
        {phoneLabel}
      </button>
    );
  };

  const renderActiveChatDemoBadge = (className: string) => {
    if (!activeChat) {
      return null;
    }

    const isMobileBadge = className.includes('mobile');
    const demoAccessLabel = activeChat.demoAccessGranted
      ? 'Демо предоставлен'
      : isMobileBadge
        ? 'Демо нет'
        : 'Демо не предоставлен';
    const badgeClassName = `telegram-info-pill telegram-info-pill--demo-access ${activeChat.demoAccessGranted ? 'telegram-info-pill--demo-access-granted' : 'telegram-info-pill--demo-access-missing'} ${className}`;

    if (activeChat.demoAccessGranted) {
      return (
        <span
          className={badgeClassName}
          title={demoAccessLabel}
        >
          {demoAccessLabel}
        </span>
      );
    }

    return (
      <button
        className={`${badgeClassName} telegram-info-pill--button telegram-info-pill--link`}
        type="button"
        onClick={handleDemoAccessBadgeClick}
        title="Предоставить демо-доступ"
        disabled={isGrantingChatDemoAccess}
      >
        {demoAccessLabel}
      </button>
    );
  };

  const renderActiveChatStartEducationBadge = (className: string) => {
    if (!activeChat) {
      return null;
    }

    const formattedStartEducationDate = formatStartEducationDateValue(activeChat.startEducationDate);
    const startEducationDateLabel = formatStartEducationDateLabel(activeChat.startEducationDate);

    if (!formattedStartEducationDate || !startEducationDateLabel) {
      return null;
    }

    return (
      <span className={`telegram-info-pill ${className}`} title={`Дата начала обучения: ${formattedStartEducationDate}`}>
        {startEducationDateLabel}
      </span>
    );
  };

  const renderActiveChatPlatformBadges = (className: string) => (
    <>
      {activeChat?.hasMaxChat ? (
        <span className={`telegram-platform-badge telegram-platform-badge--max ${className}`}>MAX</span>
      ) : null}
      {activeChat?.hasTelegramChat ? (
        <span className={`telegram-platform-badge telegram-platform-badge--telegram ${className}`}>TG</span>
      ) : null}
    </>
  );

  const renderActiveChatWidgetBadge = (className: string) => {
    const linkedWidgetChat = activeChat?.linkedWidgetChats[0] ?? null;

    if (!linkedWidgetChat) {
      return null;
    }

    return (
      <button
        type="button"
        className={`telegram-platform-badge telegram-platform-badge--widget telegram-platform-badge--link ${className}`}
        onClick={(event) => handleLinkedWidgetChatOpen(event, linkedWidgetChat.chatId)}
        onKeyDown={stopNestedAction}
        title={`${formatWidgetPageLabel(linkedWidgetChat)}${formatWidgetPageCaption(linkedWidgetChat) ? ` • ${formatWidgetPageCaption(linkedWidgetChat)}` : ''}`}
      >
        WDS
      </button>
    );
  };

  const renderActiveWidgetLinkBadges = (className: string) => (
    (() => {
      const linkedMaxChat = activeWidgetChat?.linkedChats.find((linkedChat) => linkedChat.platform === 'max') ?? null;
      const linkedTelegramChat = activeWidgetChat?.linkedChats.find((linkedChat) => linkedChat.platform === 'telegram') ?? null;

      return (
        <>
          <span className={`telegram-platform-badge telegram-platform-badge--widget ${className}`}>WDS</span>
          {linkedMaxChat ? (
            <button
              type="button"
              className={`telegram-platform-badge telegram-platform-badge--max telegram-platform-badge--link ${className}`}
              onClick={(event) => handleLinkedChatOpen(event, linkedMaxChat.chatId)}
              onKeyDown={stopNestedAction}
              title={formatWidgetLinkedChatLabel(linkedMaxChat)}
            >
              MAX
            </button>
          ) : null}
          {linkedTelegramChat ? (
            <button
              type="button"
              className={`telegram-platform-badge telegram-platform-badge--telegram telegram-platform-badge--link ${className}`}
              onClick={(event) => handleLinkedChatOpen(event, linkedTelegramChat.chatId)}
              onKeyDown={stopNestedAction}
              title={formatWidgetLinkedChatLabel(linkedTelegramChat)}
            >
              TG
            </button>
          ) : null}
        </>
      );
    })()
  );

  const hasActiveConversation = isUisSection ? activeUisGroup !== null : isWidgetSection ? activeWidgetChat !== null : activeChat !== null;
  const currentMessages = isWidgetSection ? activeWidgetChatMessages : activeChatMessages;
  const displayedCurrentMessages = isWidgetSection ? displayedActiveWidgetChatMessages : displayedActiveChatMessages;
  const isCurrentMessagesLoading = isWidgetSection ? isWidgetMessagesLoading : isMessagesLoading;

  return (
    <div className={`telegram-shell${isMenuOpen ? ' telegram-shell--menu-open' : ''}${hasActiveConversation ? ' telegram-shell--mobile-chat-open' : ''}`} data-theme={theme}>
      <aside className="telegram-sidebar">
        <div className="telegram-sidebar__top">
          <button
            className={`telegram-icon-button telegram-icon-button--menu${isMenuOpen ? ' is-active' : ''}`}
            type="button"
            aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
            aria-expanded={isMenuOpen}
            aria-controls="operator-menu"
            onClick={() => setIsMenuOpen((current) => !current)}
          >
            <MenuIcon isOpen={isMenuOpen} />
          </button>

          <label className={`telegram-search${isSidebarSearchEnabled ? '' : ' is-disabled'}`}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Поиск"
              aria-label="Поиск"
              value={chatSearch}
              onChange={(event) => setChatSearch(event.target.value)}
              disabled={!isSidebarSearchEnabled}
            />
          </label>

          <button
            type="button"
            className={`telegram-sidebar__filter${isSidebarFilterActive ? ' is-active' : ''}`}
            aria-label={sidebarFilterAriaLabel}
            aria-pressed={isSidebarFilterActive}
            aria-expanded={isUisSection ? isUisFilterPanelOpen : undefined}
            aria-controls={isUisSection ? 'uis-sidebar-filter-panel' : undefined}
            onClick={() => {
              if (isUisSection) {
                setIsUisFilterPanelOpen((current) => !current);
                return;
              }

              setIsUnreadFilterEnabled((current) => !current);
            }}
            disabled={!isSidebarFilterEnabled}
          >
            <IconFilter width="19px" height="19px" color="currentColor" />
          </button>
        </div>

        <div className="telegram-sidebar__toolbar">
          <div className="telegram-sidebar__switcher" role="tablist" aria-label="Разделы сервиса">
            {SIDEBAR_SECTIONS.map((section) => (
              <button
                key={section.id}
                type="button"
                role="tab"
                aria-selected={activeSidebarSection === section.id}
                aria-label={section.id === 'chats' && totalUnreadMessages > 0
                  ? `${section.label}, ${totalUnreadMessages} новых сообщений`
                  : section.id === 'wds' && totalUnreadWidgetMessages > 0
                    ? `${section.label}, ${totalUnreadWidgetMessages} новых сообщений`
                    : section.id === 'uis' && totalPendingUisMissedCalls > 0
                      ? `${section.label}, ${formatUisCallCount(totalPendingUisMissedCalls)} требуют обратного звонка`
                    : section.label}
                className={`telegram-sidebar__switch${activeSidebarSection === section.id ? ' is-active' : ''}`}
                onClick={() => setActiveSidebarSection(section.id)}
              >
                <span>{section.label}</span>
                {section.id === 'chats' && totalUnreadMessages > 0 ? (
                  <span className="telegram-sidebar__switch-badge" aria-hidden="true">{totalUnreadMessagesBadge}</span>
                ) : null}
                {section.id === 'wds' && totalUnreadWidgetMessages > 0 ? (
                  <span className="telegram-sidebar__switch-badge" aria-hidden="true">{totalUnreadWidgetMessagesBadge}</span>
                ) : null}
                {section.id === 'uis' && totalPendingUisMissedCalls > 0 ? (
                  <span className="telegram-sidebar__switch-badge" aria-hidden="true">{totalPendingUisMissedCallsBadge}</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        <div className="telegram-chat-list" aria-label={isChatsSection ? 'Список клиентских чатов' : `Раздел ${currentSidebarSectionLabel}`}>
          {isChatsSection ? (
            <ChatsSection
              isLoading={isChatsLoading}
              hasChats={chatThreads.length > 0}
              hasVisibleChats={filteredChatThreads.length > 0}
              isUnreadFilterEnabled={isUnreadFilterEnabled}
            >
              {filteredChatThreads.map((chatThread) => renderChatRow(chatThread))}
            </ChatsSection>
          ) : null}

          {isWidgetSection ? (
            <WdsSection
              isLoading={isWidgetChatsLoading}
              hasChats={widgetChats.length > 0}
              hasVisibleChats={filteredWidgetChats.length > 0}
              isUnreadFilterEnabled={isUnreadFilterEnabled}
            >
              {filteredWidgetChats.map((chat) => renderWidgetChatRow(chat))}
            </WdsSection>
          ) : null}

          {activeSidebarSection === 'lms' && currentSidebarSectionPlaceholderText ? (
            <LmsSection title={currentSidebarSectionLabel} placeholderText={currentSidebarSectionPlaceholderText} />
          ) : null}

          {activeSidebarSection === 'uis' && currentSidebarSectionPlaceholderText ? (
            <UisSection
              title={currentSidebarSectionLabel}
              placeholderText={currentSidebarSectionPlaceholderText}
              overview={uisOverview}
              isLoading={isUisOverviewLoading}
              errorMessage={uisOverviewErrorMessage}
              activeCallId={activeUisGroup?.id ?? null}
              activeFilter={uisActiveFilter}
              onActiveCallChange={handleUisCallSelect}
              onFilterOptionsChange={handleUisFilterOptionsChange}
              onRefresh={handleUisRefresh}
            />
          ) : null}
        </div>
      </aside>

      <main className="telegram-stage" style={stageBackground} aria-label="Область диалога">
        <div className="telegram-stage__ambient" aria-hidden="true">
          <span className="telegram-stage__orb telegram-stage__orb--one" />
          <span className="telegram-stage__orb telegram-stage__orb--two" />
          <span className="telegram-stage__orb telegram-stage__orb--three" />
        </div>

        {!hasActiveConversation ? (
          <div className="telegram-stage__empty">
            <div className="telegram-stage__logo" aria-hidden="true">
              <img src={serviceIconUrl} alt="" />
            </div>

            <div className="telegram-stage__copy">
              <h2>{operator.firstName ? `Здравствуйте, ${operator.firstName}` : 'Сервис уведомлений'}</h2>
              <p>
                {isUisSection
                  ? 'Слева список номеров UIS, справа откроется история статусов выбранного номера.'
                  : isWidgetSection
                    ? 'Слева список обращений из виджета сайта, справа область переписки.'
                    : 'Слева список чатов, справа область переписки.'}
              </p>
            </div>
          </div>
        ) : isUisSection && activeUisGroup ? (
          <section className="uis-stage-shell" aria-label="История звонков по номеру">
            <header className="telegram-conversation__header uis-stage-shell__header">
              <button
                className="telegram-conversation__back-button"
                type="button"
                onClick={handleMobileConversationClose}
                aria-label="Вернуться к списку звонков UIS"
              >
                <ArrowLeftIcon width="22px" height="22px" color="currentColor" />
              </button>

              <div className="telegram-conversation__identity telegram-conversation__identity--uis">
                <div className="telegram-conversation__identity-content telegram-conversation__identity-content--uis">
                  <div className="telegram-conversation__title-row telegram-conversation__title-row--uis">
                    <strong className="telegram-conversation__title-text">История номера</strong>
                    <span className="telegram-info-pill telegram-info-pill--phone telegram-conversation__title-phone telegram-conversation__title-phone--uis">
                      {formatCallCardPhoneNumber(activeUisGroup.phoneNumber ?? activeUisGroup.latestCall.contactPhoneNumber)}
                    </span>
                  </div>

                  <div className="telegram-conversation__subtitle telegram-conversation__subtitle--uis">
                    <span className="telegram-conversation__subtitle-summary">
                      {`Всего ${formatUisCallCount(activeUisGroup.calls.length)} • Пропущено ${activeUisGroup.missedCalls} • С записями ${activeUisGroup.recordedCalls}`}
                    </span>
                  </div>
                </div>
              </div>
            </header>

            <div className="uis-stage-shell__body">
              <UisCallDetailsPanel group={activeUisGroup} />
            </div>
          </section>
        ) : (
          <section
            className={`telegram-conversation${isConversationDragActive ? ' telegram-conversation--drag-active' : ''}`}
            onDragEnter={handleConversationDragEnter}
            onDragOver={handleConversationDragOver}
            onDragLeave={handleConversationDragLeave}
            onDrop={handleConversationDrop}
          >
            {isConversationDragActive ? (
              <div className="telegram-conversation__dropzone" aria-hidden="true">
                <div className="telegram-conversation__dropzone-card">
                  <strong>Перетащите файлы сюда</strong>
                  <p>Файлы добавятся в сообщение, а текст внизу можно отправить как подпись.</p>
                </div>
              </div>
            ) : null}

            <header className="telegram-conversation__header">
              <button
                className="telegram-conversation__back-button"
                type="button"
                onClick={handleMobileConversationClose}
                aria-label="Вернуться к списку чатов"
              >
                <ArrowLeftIcon width="22px" height="22px" color="currentColor" />
              </button>

              <div className="telegram-conversation__identity">
                {isWidgetSection && activeWidgetChat ? (
                  <>
                    <div className="telegram-conversation__identity-media">
                      <img className="telegram-conversation__avatar telegram-conversation__avatar--widget" src={widgetChatAvatarUrl} alt={activeWidgetChat.title} />
                    </div>

                    <div className="telegram-conversation__identity-content">
                      <div className="telegram-conversation__title-row">
                        <strong className="telegram-conversation__title-text">{activeWidgetChat.title}</strong>
                        {renderActiveWidgetLinkBadges('telegram-conversation__title-platform-badge')}
                      </div>

                      <div className="telegram-conversation__subtitle">
                        {activeWidgetPageHref ? (
                          <a
                            className="telegram-info-pill telegram-info-pill--course telegram-info-pill--link telegram-conversation__subtitle-item telegram-conversation__subtitle-item--course"
                            href={activeWidgetPageHref}
                            target="_blank"
                            rel="noreferrer"
                            title={formatWidgetPageLabel(activeWidgetChat)}
                          >
                            <span className="telegram-conversation__subtitle-course-text">{formatWidgetPageLabel(activeWidgetChat)}</span>
                          </a>
                        ) : (
                          <span
                            className="telegram-info-pill telegram-info-pill--course telegram-conversation__subtitle-item telegram-conversation__subtitle-item--course"
                            title={formatWidgetPageLabel(activeWidgetChat)}
                          >
                            <span className="telegram-conversation__subtitle-course-text">{formatWidgetPageLabel(activeWidgetChat)}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                ) : activeChat ? (
                  <>
                    <div className="telegram-conversation__identity-media">
                      {resolveAvatarImageSrc(activeChat.key, activeChat.avatarUrl, avatarLoadAttempts) ? (
                        <button
                          type="button"
                          className="telegram-conversation__avatar-button"
                          onClick={openActiveChatAvatarPreview}
                          aria-label={`Открыть фото клиента ${activeChat.title}`}
                          title="Открыть фото клиента"
                        >
                          {renderChatAvatar(
                            activeChat.key,
                            activeChat.avatarUrl,
                            activeChat.title,
                            getTitleInitials(activeChat.title),
                            `telegram-conversation__avatar telegram-conversation__avatar--${activeChat.platform}`,
                            'telegram-conversation__avatar telegram-conversation__avatar--fallback'
                          )}
                        </button>
                      ) : renderChatAvatar(
                        activeChat.key,
                        activeChat.avatarUrl,
                        activeChat.title,
                        getTitleInitials(activeChat.title),
                        `telegram-conversation__avatar telegram-conversation__avatar--${activeChat.platform}`,
                        'telegram-conversation__avatar telegram-conversation__avatar--fallback'
                      )}
                    </div>

                    <div className="telegram-conversation__identity-content">
                      <div className="telegram-conversation__title-row">
                        <strong className="telegram-conversation__title-text">{activeChat.title}</strong>
                        {renderActiveChatPlatformBadges('telegram-conversation__title-platform-badge')}
                        {renderActiveChatWidgetBadge('telegram-conversation__title-platform-badge')}
                        {renderActiveChatPhoneBadge('telegram-conversation__title-phone')}
                        {renderActiveChatDemoBadge('telegram-conversation__title-demo-access')}
                      </div>

                      <div className="telegram-conversation__subtitle">
                        {activeChatApplicationHref ? (
                          <a
                            className="telegram-info-pill telegram-info-pill--application telegram-info-pill--link telegram-conversation__subtitle-item telegram-conversation__subtitle-item--application"
                            href={activeChatApplicationHref}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <span className="telegram-conversation__detail--desktop">{formatChatApplicationLabel(activeChat.crmApplicationId)}</span>
                            <span className="telegram-conversation__detail--mobile">{formatChatApplicationNumberLabel(activeChat.crmApplicationId)}</span>
                          </a>
                        ) : (
                          <span className="telegram-info-pill telegram-info-pill--application telegram-conversation__subtitle-item telegram-conversation__subtitle-item--application">
                            <span className="telegram-conversation__detail--desktop">{formatChatApplicationLabel(activeChat.crmApplicationId)}</span>
                            <span className="telegram-conversation__detail--mobile">{formatChatApplicationNumberLabel(activeChat.crmApplicationId)}</span>
                          </span>
                        )}

                        <div className="telegram-conversation__subtitle-action-shell" ref={courseActionPopoverRef}>
                          <button
                            className="telegram-info-pill telegram-info-pill--course telegram-info-pill--button telegram-conversation__subtitle-item telegram-conversation__subtitle-item--course telegram-conversation__subtitle-item--interactive"
                            type="button"
                            title={activeChat.courseName ?? 'Курс не указан'}
                            aria-haspopup={activeChatHasCourse ? 'menu' : 'dialog'}
                            aria-expanded={activeChatHasCourse ? isCourseActionPopoverOpen : isCourseSelectionModalOpen}
                            onClick={handleCourseBadgeClick}
                            disabled={isCourseActionBusy}
                          >
                            <span className="telegram-conversation__subtitle-course-text">{activeChat.courseName ?? 'Курс не указан'}</span>
                          </button>

                          {isCourseActionPopoverOpen ? (
                            <div className="telegram-course-actions-popover" role="menu" aria-label="Действия с курсом">
                              <button
                                className="telegram-course-actions-popover__action"
                                type="button"
                                role="menuitem"
                                onClick={handleOpenActiveChatCourseLanding}
                                disabled={!activeChatCourseHref}
                              >
                                Перейти на лэндинг
                              </button>

                              <button
                                className="telegram-course-actions-popover__action"
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  void openCourseSelectionModal();
                                }}
                                disabled={isCourseActionBusy}
                              >
                                Изменить курс
                              </button>
                            </div>
                          ) : null}
                        </div>

                        {activeChatHasSelectableHours ? (
                          <button
                            className="telegram-info-pill telegram-info-pill--button telegram-conversation__subtitle-item telegram-conversation__subtitle-item--hours telegram-conversation__subtitle-item--interactive"
                            type="button"
                            onClick={() => {
                              void openActiveChatCourseProgramModal();
                            }}
                            title="Изменить часы курса"
                            disabled={isCourseActionBusy}
                          >
                            {formatCourseVolumeHours(activeChat.volumeHours)}
                          </button>
                        ) : (
                          <span className="telegram-info-pill telegram-conversation__subtitle-item telegram-conversation__subtitle-item--hours">
                            {formatCourseVolumeHours(activeChat.volumeHours)}
                          </span>
                        )}

                        {renderActiveChatStartEducationBadge('telegram-conversation__subtitle-item telegram-conversation__subtitle-item--start-education')}

                        {renderActiveChatPlatformBadges('telegram-conversation__subtitle-item telegram-conversation__subtitle-item--platform telegram-conversation__subtitle-platform-badge')}

                        {renderActiveChatPhoneBadge('telegram-conversation__subtitle-item telegram-conversation__subtitle-item--phone telegram-conversation__subtitle-item--phone-mobile')}

                        {activeChat.email ? <span className="telegram-conversation__detail--desktop">{activeChat.email}</span> : null}
                      </div>
                    </div>

                    <div className="telegram-conversation__mobile-badges">
                      {renderActiveChatPlatformBadges('telegram-conversation__mobile-badges-item')}
                      {renderActiveChatDemoBadge('telegram-conversation__mobile-badges-item telegram-conversation__mobile-badges-item--demo-access')}
                      {renderActiveChatStartEducationBadge('telegram-conversation__mobile-badges-item telegram-conversation__mobile-badges-item--start-education')}
                      {renderActiveChatWidgetBadge('telegram-conversation__mobile-badges-item')}
                    </div>
                  </>
                ) : null}
              </div>

              {canViewActiveChatHistory ? (
                <button
                  className="telegram-conversation__history-button"
                  type="button"
                  onClick={openChatHistoryModal}
                  aria-label="Открыть историю"
                  title="Открыть историю"
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M4 12a8 8 0 1 0 2.343-5.657"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 4v4h4"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 8.25v4.2l2.8 1.8"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : null}
            </header>

            <div className="telegram-conversation__body" ref={messageViewportRef} onScroll={handleMessageViewportScroll}>
              {isCurrentMessagesLoading && currentMessages.length === 0 ? (
                <div className="telegram-conversation__placeholder">
                  <span className="telegram-chat-list__pulse" aria-hidden="true" />
                  <strong>Загружаем переписку</strong>
                  <p>История сообщений будет показана здесь.</p>
                </div>
              ) : null}

              {!isCurrentMessagesLoading && currentMessages.length === 0 ? (
                <div className="telegram-conversation__placeholder">
                  <strong>Сообщений пока нет</strong>
                  <p>{isWidgetSection ? 'Как только посетитель напишет в виджет сайта, сообщение появится здесь автоматически.' : 'Как только клиент напишет в этот чат, сообщение появится здесь автоматически.'}</p>
                </div>
              ) : null}

              {displayedCurrentMessages.length > 0 ? (
                <div className="telegram-message-list">
                  {displayedCurrentMessages.map((message) => {
                    const reminderMessage = renderReminderMessage(message);

                    if (reminderMessage) {
                      return reminderMessage;
                    }

                    const fallbackText = buildMessageFallback(message);
                    const hasAttachmentStorageWarning = getAttachmentStorageWarning(message) !== null;
                    const canDeleteMessage = message.direction === 'outgoing';
                    const isDeletingMessage = deletingMessageId === message.messageId;
                    const isIncomingMessage = message.direction === 'incoming';
                    const isIncomingMessageUnread = isIncomingMessage && message.operatorReadAt === null;
                    const isMarkingMessageRead = readingMessageId === message.messageId;
                    const isEditedMessage = message.editedAt !== null;
                    const footerTimestamp = message.sentAt;
                    const messageSourceChat = isWidgetSection
                      ? { title: activeWidgetChat?.title ?? 'Посетитель сайта' }
                      : activeChat
                        ? activeChat.chats.find((chat) => chat.chatId === message.chatId) ?? activeChat
                        : { title: 'Клиент' };
                    const messageRoleLabel = getMessageRoleLabel(message);
                    const messageActorLabel = getMessageActorLabel(message, messageSourceChat.title);
                    const isAiMessage = isAiAutoresponderMessage(message);
                    const messagePlatformLabel = getPlatformBadgeLabel(message.platform);
                    const formattedFooterTimestamp = formatMessagePanelTimestamp(footerTimestamp);

                    const shouldRenderTextAboveAttachments = Boolean(message.textContent) && !message.renderTextBelowAttachments;
                    const shouldRenderTextBelowAttachments = Boolean(message.textContent) && message.renderTextBelowAttachments;

                    return (
                      <article key={`${message.chatId}:${message.messageId}`} className={`telegram-message telegram-message--${message.direction}`}>
                        <div className="telegram-message__meta">
                          <span className={`telegram-message__meta-card telegram-message__meta-card--platform telegram-message__meta-card--platform-${message.platform}`}>{messagePlatformLabel}</span>
                          <span className={`telegram-message__meta-card telegram-message__meta-card--role${isAiMessage ? ' telegram-message__meta-card--role-ai' : ''}`}>{messageRoleLabel}</span>
                          <span className="telegram-message__meta-card telegram-message__meta-card--actor" title={messageActorLabel}>{messageActorLabel}</span>
                        </div>

                        {shouldRenderTextAboveAttachments ? (
                          <div className="telegram-message__content">
                            <p className="telegram-message__text">{message.textContent}</p>
                          </div>
                        ) : null}
                        {!message.textContent && fallbackText && message.messageType !== 'contact' ? (
                          <div className={`telegram-message__content telegram-message__content--muted${hasAttachmentStorageWarning ? ' telegram-message__content--attachment-warning' : ''}`}>
                            <p className="telegram-message__text telegram-message__text--muted">{fallbackText}</p>
                          </div>
                        ) : null}
                        {renderMessageMetadata(message)}

                        {message.files.length > 0 ? (
                          <div className="telegram-message__attachments">
                            {message.files.map((file) => renderMessageFile(file, message))}
                          </div>
                        ) : null}

                        {shouldRenderTextBelowAttachments ? (
                          <div className="telegram-message__content">
                            <p className="telegram-message__text">{message.textContent}</p>
                          </div>
                        ) : null}

                        <div className="telegram-message__footer">
                          <div className="telegram-message__footer-main">
                            {canDeleteMessage ? (
                              <button
                                type="button"
                                className="telegram-message__delete-button"
                                onClick={() => void handleDeleteMessage(message)}
                                disabled={isDeletingMessage}
                                aria-label={isDeletingMessage ? 'Удаляем сообщение' : 'Удалить сообщение'}
                                title={isDeletingMessage ? 'Удаляем сообщение' : 'Удалить сообщение'}
                              >
                                <IconDelete width="16px" height="16px" color="currentColor" opacity="1" />
                              </button>
                            ) : null}

                            <div className="telegram-message__footer-statuses">
                              {isIncomingMessage ? (
                                isIncomingMessageUnread ? (
                                  <button
                                    type="button"
                                    className="telegram-message__read-badge telegram-message__read-badge--new"
                                    onClick={() => void handleMarkMessageAsRead(message)}
                                    disabled={isMarkingMessageRead}
                                  >
                                    {isMarkingMessageRead ? 'Читаем...' : 'Новое'}
                                  </button>
                                ) : (
                                  <span
                                    className="telegram-message__read-badge telegram-message__read-badge--read telegram-message__read-badge--icon"
                                    aria-label="Прочитано"
                                    title="Прочитано"
                                  >
                                    <IconDoneAll width="16px" height="16px" color="currentColor" opacity="1" />
                                  </span>
                                )
                              ) : null}

                              {isEditedMessage ? <span className="telegram-message__edited-label">Изменено</span> : null}
                            </div>
                          </div>

                          <time className="telegram-message__footer-timestamp" dateTime={footerTimestamp}>{formattedFooterTimestamp}</time>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {displayedCurrentMessages.length > 0 && showScrollToBottomButton ? (
              <button
                type="button"
                className="telegram-conversation__scroll-bottom"
                onClick={() => scrollMessageViewportToBottom()}
                aria-label="Перейти к последним сообщениям"
                title="Перейти вниз"
              >
                <IconArrowDown width="20px" height="20px" color="currentColor" opacity="1" />
              </button>
            ) : null}

            <form className="telegram-conversation__composer" onSubmit={handleSendMessageSubmit}>
                <input
                  ref={composerFileInputRef}
                  className="telegram-composer__file-input"
                  type="file"
                  multiple
                  onChange={handleComposerFilesChange}
                  disabled={sendMessagePending}
                />
              {composerFiles.length > 0 ? (
                <div className="telegram-composer__attachments" aria-label="Выбранные файлы">
                  {composerFiles.map((file) => {
                    const fileKey = buildComposerFileKey(file);
                    const fileSizeLabel = formatFileSize(file.size);

                    return (
                      <div key={fileKey} className="telegram-composer-chip">
                        <div className="telegram-composer-chip__meta">
                          <strong title={file.name}>{file.name}</strong>
                          {fileSizeLabel ? <span>{fileSizeLabel}</span> : null}
                        </div>

                        <button
                          type="button"
                          className="telegram-composer-chip__remove"
                          onClick={() => handleComposerFileRemove(fileKey)}
                          disabled={sendMessagePending}
                          aria-label={`Убрать файл ${file.name}`}
                        >
                          x
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {composerFiles.length > 0 ? (
                <p className="telegram-composer__hint">Можно добавить текст и отправить его вместе с файлами одним сообщением.</p>
              ) : null}

              <div className="telegram-composer-field telegram-composer-field--with-ai">
                  <div className="telegram-composer-field__attachment-shell" ref={attachmentMenuRef}>
                    <button
                      className="telegram-composer-field__action telegram-composer-field__action--attach"
                      type="button"
                      onClick={handleComposerAttachmentClick}
                      disabled={sendMessagePending || isCourseAttachmentFilesLoading}
                      aria-label="Прикрепить файл"
                      title="Прикрепить файл"
                      aria-haspopup={hasComposerAttachmentMenu ? 'menu' : undefined}
                      aria-expanded={hasComposerAttachmentMenu ? isAttachmentMenuOpen : undefined}
                    >
                      <AttachmentIcon />
                    </button>

                    {isAttachmentMenuOpen && hasComposerAttachmentMenu ? (
                      <div className="telegram-composer-attachment-menu" role="menu" aria-label="Способы прикрепления файла" onPointerDown={(event) => event.stopPropagation()}>
                        <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={handleOpenNativeAttachmentPicker} disabled={sendMessagePending}>
                          Прикрепить файл
                        </button>
                        <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => void openCourseAttachmentModal()} disabled={sendMessagePending}>
                          Выбрать файл из БД
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <button
                    className="telegram-composer-field__action telegram-composer-field__action--bell"
                    type="button"
                    onClick={openReminderModal}
                    disabled={sendMessagePending}
                    aria-label="Уведомление"
                    title="Уведомление"
                  >
                    <BellIcon />
                  </button>
                  <button
                    className="telegram-composer-field__action telegram-composer-field__action--sms-tracking"
                    type="button"
                    onClick={openQuickReplyModal}
                    disabled={sendMessagePending || !composerTargetChatId}
                    aria-label="Варианты ответов"
                    title="Варианты ответов"
                  >
                    <IconSmsTracking />
                  </button>

                <textarea
                  ref={composerTextareaRef}
                  value={chatDraft}
                  onChange={(event) => setChatDraft(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  placeholder={composerPlaceholder}
                  aria-label="Текст сообщения клиенту"
                  rows={1}
                  disabled={sendMessagePending}
                />

                <AiReplyAssistant
                  chatId={composerTargetChatId}
                  chatTitle={(activeSidebarSection === 'wds' ? activeWidgetChat?.title : activeChat?.title) || 'Клиент'}
                  scope={composerApiScope}
                  performAuthorizedRequest={performAuthorizedRequest}
                  draft={chatDraft}
                  disabled={sendMessagePending}
                  onInsert={(text) => {
                    setChatDraft(text);
                    window.requestAnimationFrame(() => {
                      const textarea = composerTextareaRef.current;
                      textarea?.focus();
                      textarea?.setSelectionRange(text.length, text.length);
                    });
                  }}
                />
                <button
                  className="telegram-composer-field__action telegram-composer-field__action--send"
                  type="submit"
                  disabled={sendMessagePending || !canSubmitComposer}
                  aria-label={sendMessagePending ? 'Отправка сообщения' : 'Отправить сообщение или файлы'}
                >
                  <TelegramSendIcon />
                </button>
              </div>
            </form>
          </section>
        )}
      </main>

      <div className={`telegram-filter-window${isUisSection && isUisFilterPanelOpen ? ' telegram-filter-window--open' : ''}`} aria-hidden={!isUisSection || !isUisFilterPanelOpen}>
        <button className="telegram-filter-window__backdrop" type="button" aria-label="Закрыть фильтры UIS" onClick={closeUisFilterPanel} />

        <section className="telegram-filter-window__panel" id="uis-sidebar-filter-panel" role="dialog" aria-modal="true" aria-label="Фильтры звонков UIS">
          <div className="telegram-filter-window__glow" aria-hidden="true" />

          <div className="telegram-filter-window__header">
            <div className="telegram-filter-window__identity">
              <span>Переключатели списка звонков</span>
              <strong>Фильтры UIS</strong>
            </div>

            <div className="telegram-filter-window__actions">
              <button type="button" className="uis-section__action" onClick={handleUisRefresh}>
                Обновить
              </button>

              <button className="telegram-filter-window__close" type="button" onClick={closeUisFilterPanel} aria-label="Закрыть фильтры UIS">
                <CloseIcon />
              </button>
            </div>
          </div>

          {uisFilterOptions.length > 0 ? (
            <div className="telegram-filter-window__grid" role="tablist" aria-label="Переключатели фильтра UIS">
              {uisFilterOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`uis-section__filter${option.id === uisActiveFilter ? ' is-active' : ''}`}
                  onClick={() => {
                    setUisActiveFilter(option.id);
                    closeUisFilterPanel();
                  }}
                >
                  <span>{option.label}</span>
                  <strong>{option.count}</strong>
                </button>
              ))}
            </div>
          ) : (
            <p className="telegram-filter-window__empty">Фильтры появятся после загрузки звонков UIS.</p>
          )}
        </section>
      </div>

      <div className={`telegram-menu${isMenuOpen ? ' telegram-menu--open' : ''}`} aria-hidden={!isMenuOpen}>
        <button className="telegram-menu__backdrop" type="button" aria-label="Закрыть меню" onClick={closeMenu} />

        <section className="telegram-menu__panel" id="operator-menu" role="dialog" aria-modal="true" aria-label="Меню">
          <div className="telegram-menu__glow" aria-hidden="true" />

          <div className="telegram-menu__header">
            <div className="telegram-menu__avatar">{operatorInitials}</div>

            <div className="telegram-menu__identity">
              <span>{operator.accessLevelName}</span>
              <strong>{operator.displayName}</strong>
            </div>

            <button className="telegram-menu__close" type="button" onClick={closeMenu} aria-label="Закрыть меню">
              <CloseIcon />
            </button>
          </div>

          <div className="telegram-menu__meta">
            <article className="telegram-menu__meta-item telegram-menu__meta-item--ai">
              <span className="telegram-menu__theme-title">Автоответчик AI</span>

              <div className="telegram-menu__theme-switcher" role="group" aria-label="Переключатель Автоответчика AI">
                <button
                  className={`telegram-menu__theme-button${isAiAutoresponderEnabled ? ' is-active' : ''}`}
                  type="button"
                  onClick={() => void handleAiAutoresponderToggle(true)}
                  aria-pressed={isAiAutoresponderEnabled}
                  disabled={isAiAutoresponderSettingsLoading || aiAutoresponderUpdatePending}
                >
                  Включён
                </button>

                <button
                  className={`telegram-menu__theme-button${!isAiAutoresponderEnabled ? ' is-active' : ''}`}
                  type="button"
                  onClick={() => void handleAiAutoresponderToggle(false)}
                  aria-pressed={!isAiAutoresponderEnabled}
                  disabled={isAiAutoresponderSettingsLoading || aiAutoresponderUpdatePending}
                >
                  Выключен
                </button>
              </div>
            </article>

            <article className="telegram-menu__meta-item telegram-menu__meta-item--theme">
              <span className="telegram-menu__theme-title">Тема интерфейса</span>

              <div className="telegram-menu__theme-switcher" role="group" aria-label="Переключатель темы интерфейса">
                <button
                  className={`telegram-menu__theme-button${theme === 'dark' ? ' is-active' : ''}`}
                  type="button"
                  onClick={() => setThemePreference('dark')}
                  aria-pressed={theme === 'dark'}
                >
                  Тёмная
                </button>

                <button
                  className={`telegram-menu__theme-button${theme === 'light' ? ' is-active' : ''}`}
                  type="button"
                  onClick={() => setThemePreference('light')}
                  aria-pressed={theme === 'light'}
                >
                  Светлая
                </button>
              </div>
            </article>
          </div>

          <button className="telegram-menu__logout" type="button" onClick={openLogoutConfirm} disabled={logoutPending}>
            <IconExitApp />
            <span>Выйти из сервиса</span>
          </button>
        </section>
      </div>

      <ModalConfirm
        isOpen={isDemoAccessDialogOpen}
        onClose={closeDemoAccessDialog}
        closeAriaLabel="Закрыть окно выдачи демо-доступа"
        dialogAriaLabel={demoAccessDialogMode === 'confirm' ? 'Подтверждение выдачи демо-доступа' : 'Информация о требованиях для выдачи демо-доступа'}
        title={demoAccessDialogMode === 'confirm' ? 'Предоставить демо-доступ' : 'Недостаточно данных'}
        showHeaderCloseButton
        actions={demoAccessDialogMode === 'confirm'
          ? (
            <>
              <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeDemoAccessDialog} disabled={isGrantingChatDemoAccess}>
                Отмена
              </button>
              <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={() => void handleGrantChatDemoAccess()} disabled={isGrantingChatDemoAccess}>
                {isGrantingChatDemoAccess ? 'Продолжаем...' : 'Продолжить'}
              </button>
            </>
          )
          : (
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeDemoAccessDialog}>
              Понятно
            </button>
          )}
      >
        <div className="telegram-confirm__content">
          <p>
            {demoAccessDialogMode === 'confirm'
              ? 'Вы действительно хотите предоставить демо-доступ для данного клиента?'
              : 'Прежде чем приступить к выдаче демо-доступа, необходимо указать номер телефона клиента и выбрать программу курса.'}
          </p>
        </div>
      </ModalConfirm>

      <ModalConfirm
        isOpen={isChatPhoneModalOpen}
        onClose={closeChatPhoneModal}
        closeAriaLabel="Закрыть окно ввода телефона"
        dialogAriaLabel={activeChat ? `Указать номер телефона для чата ${activeChat.title}` : 'Указать номер телефона'}
        dialogClassName="telegram-chat-phone-dialog"
        title="Указать номер телефона"
        icon={<PhoneCallIcon width="24px" height="24px" color="currentColor" opacity="1" />}
        showHeaderCloseButton
        actions={(
          <>
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeChatPhoneModal} disabled={isApplyingChatPhone}>
              Отмена
            </button>
            <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={() => void handleApplyChatPhone()} disabled={isApplyingChatPhone}>
              {isApplyingChatPhone ? 'Применяем...' : 'Применить'}
            </button>
          </>
        )}
      >
        <div className="telegram-confirm__content telegram-chat-phone-dialog__content">
          <p>У клиента ещё не указан номер телефона. Введите его, чтобы создать заявку для текущего чата.</p>

          <div className={`telegram-chat-phone-form__field${chatPhoneError ? ' telegram-chat-phone-form__field--invalid' : ''}`}>
            <span className="telegram-chat-phone-form__label">Номер телефона</span>

            <div className="telegram-chat-phone-form__shell">
              <div className="telegram-chat-phone-form__country-menu" ref={chatPhoneCountryMenuRef}>
                <button
                  type="button"
                  className="telegram-chat-phone-form__country-pick"
                  onClick={() => setIsChatPhoneCountryMenuOpen((current) => !current)}
                  aria-haspopup="listbox"
                  aria-expanded={isChatPhoneCountryMenuOpen}
                  aria-label={`Выбрать страну: ${currentChatPhoneCountry.label}`}
                  title={currentChatPhoneCountry.label}
                  disabled={isApplyingChatPhone}
                >
                  <img src={currentChatPhoneCountry.iconSrc} alt="" width={24} height={24} className="telegram-chat-phone-form__country-icon" />
                </button>

                {isChatPhoneCountryMenuOpen ? (
                  <div className="telegram-chat-phone-form__country-dropdown" role="listbox" aria-label="Страны для номера телефона">
                    {PHONE_COUNTRY_LIST.map((phoneCountry) => (
                      <button
                        key={phoneCountry.code}
                        type="button"
                        role="option"
                        aria-selected={phoneCountry.code === chatPhoneCountry}
                        aria-label={phoneCountry.label}
                        title={phoneCountry.label}
                        className={`telegram-chat-phone-form__country-option${phoneCountry.code === chatPhoneCountry ? ' telegram-chat-phone-form__country-option--active' : ''}`}
                        onClick={() => handleChatPhoneCountryChange(phoneCountry.code)}
                      >
                        <img src={phoneCountry.iconSrc} alt="" width={26} height={26} className="telegram-chat-phone-form__country-icon telegram-chat-phone-form__country-icon--option" />
                        <span className="telegram-chat-phone-form__country-label">{phoneCountry.label}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <span className="telegram-chat-phone-form__divider" aria-hidden="true" />
              <span className="telegram-chat-phone-form__code">+{currentChatPhoneCountry.dialCode}</span>

              <input
                ref={chatPhoneInputRef}
                className="telegram-chat-phone-form__input"
                type="tel"
                inputMode="tel"
                placeholder={currentChatPhoneCountry.placeholder}
                value={chatPhoneInput}
                onChange={handleChatPhoneInputChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleApplyChatPhone();
                  }
                }}
                disabled={isApplyingChatPhone}
                aria-invalid={chatPhoneError.length > 0}
              />
            </div>

            {chatPhoneError ? (
              <p className="telegram-chat-phone-form__error">{chatPhoneError}</p>
            ) : chatPhoneInput ? (
              <p className="telegram-chat-phone-form__hint">
                {`Будет сохранён как ${chatPhonePreviewLabel}.`}
              </p>
            ) : null}
          </div>
        </div>
      </ModalConfirm>

      <ModalConfirm
        isOpen={isCourseSelectionModalOpen}
        onClose={closeCourseSelectionModal}
        closeAriaLabel="Закрыть окно выбора курса"
        dialogAriaLabel="Выбор курса для текущего чата"
        containerClassName="telegram-confirm--mobile-fullscreen"
        dialogClassName="telegram-course-selector-dialog"
        title="Выбрать курс"
        showHeaderCloseButton
        actions={(
          <>
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeCourseSelectionModal} disabled={isCourseSelectionLoading}>
              Отмена
            </button>
            <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={() => void handleApplySelectedCourse()} disabled={isCourseSelectionLoading || filteredCourseCatalogOptions.length === 0 || !selectedCourseId || !isSelectedCourseVisible}>
              {isCourseSelectionLoading ? 'Загружаем...' : isWidgetSection ? 'Выбрать' : 'Применить'}
            </button>
          </>
        )}
      >
        <div className="telegram-confirm__content telegram-course-selector-dialog__content">
          <p>Выберите курс для текущего чата.</p>

          <div className="telegram-course-selector-dialog__toolbar">
            <label className="telegram-search telegram-course-selector-dialog__search">
              <SearchIcon width="18px" height="18px" color="currentColor" opacity="1" />
              <input
                type="search"
                value={courseSearchQuery}
                onChange={handleCourseSearchChange}
                placeholder="Быстрый поиск курса"
                disabled={isCourseSelectionLoading || courseCatalogOptions.length === 0}
                aria-label="Быстрый поиск курса"
              />
            </label>

            {!isCourseSelectionLoading && courseCatalogOptions.length > 0 ? (
              <span className="telegram-course-selector-dialog__search-summary">
                {normalizedCourseSearchQuery
                  ? `Найдено: ${filteredCourseCatalogOptions.length}`
                  : `Всего курсов: ${courseCatalogOptions.length}`}
              </span>
            ) : null}
          </div>

          {isCourseSelectionLoading ? (
            <p className="telegram-course-selector-dialog__empty">Загружаем список курсов...</p>
          ) : filteredCourseCatalogOptions.length > 0 ? (
            <fieldset className="telegram-quick-reply-list" aria-label="Список доступных курсов">
              {filteredCourseCatalogOptions.map((course) => (
                <label className="telegram-quick-reply-option" key={course.courseId}>
                  <input
                    type="radio"
                    name="chat-course-selection"
                    value={course.courseId}
                    checked={selectedCourseId === String(course.courseId)}
                    onChange={handleCourseSelectionChange}
                    disabled={isCourseSelectionLoading}
                  />

                  <span className="telegram-quick-reply-option__card">
                    <span className="telegram-quick-reply-option__indicator" aria-hidden="true" />
                    <span className="telegram-course-selector-option__body">
                      <span className="telegram-quick-reply-option__title">{course.courseName}</span>
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
          ) : (
            <p className="telegram-course-selector-dialog__empty">
              {normalizedCourseSearchQuery ? 'По вашему запросу курсы не найдены.' : 'Доступных курсов не найдено.'}
            </p>
          )}

          {courseSelectionError ? <p className="telegram-chat-phone-form__error">{courseSelectionError}</p> : null}
        </div>
      </ModalConfirm>

      <ModalConfirm
        isOpen={isCourseProgramModalOpen}
        onClose={closeCourseProgramModal}
        closeAriaLabel="Закрыть окно выбора часов"
        dialogAriaLabel="Выбор часов для текущего чата"
        containerClassName="telegram-confirm--mobile-fullscreen"
        dialogClassName="telegram-course-selector-dialog"
        title="Выбрать часы"
        showHeaderCloseButton
        actions={(
          <>
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeCourseProgramModal} disabled={isCourseProgramLoading || isApplyingChatCourseProgram}>
              Отмена
            </button>
            <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={() => void handleApplySelectedCourseProgram()} disabled={isCourseProgramLoading || isApplyingChatCourseProgram || courseProgramOptions.length === 0 || !selectedCourseProgramId}>
              {isApplyingChatCourseProgram ? 'Применяем...' : 'Применить'}
            </button>
          </>
        )}
      >
        <div className="telegram-confirm__content telegram-course-selector-dialog__content">
          <p>{courseProgramCourseName ? `Выберите часы для курса «${courseProgramCourseName}».` : 'Выберите часы для текущего чата.'}</p>

          {isCourseProgramLoading ? (
            <p className="telegram-course-selector-dialog__empty">Загружаем варианты часов...</p>
          ) : courseProgramOptions.length > 0 ? (
            <fieldset className="telegram-quick-reply-list" aria-label="Список доступных вариантов часов">
              {courseProgramOptions.map((courseProgram) => (
                <label className="telegram-quick-reply-option" key={courseProgram.courseProgramId}>
                  <input
                    type="radio"
                    name="chat-course-program-selection"
                    value={courseProgram.courseProgramId}
                    checked={selectedCourseProgramId === String(courseProgram.courseProgramId)}
                    onChange={handleCourseProgramSelectionChange}
                    disabled={isCourseProgramLoading || isApplyingChatCourseProgram}
                  />

                  <span className="telegram-quick-reply-option__card">
                    <span className="telegram-quick-reply-option__indicator" aria-hidden="true" />
                    <span className="telegram-course-selector-option__body">
                      <span className="telegram-quick-reply-option__title">{formatCourseProgramOptionLabel(courseProgram.volumeHours, courseProgram.sum)}</span>
                      <span className="telegram-course-selector-option__meta">{courseProgram.programName}</span>
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
          ) : (
            <p className="telegram-course-selector-dialog__empty">Для выбранного курса нет активных вариантов часов.</p>
          )}

          {courseProgramSelectionError ? <p className="telegram-chat-phone-form__error">{courseProgramSelectionError}</p> : null}
        </div>
      </ModalConfirm>

      <ModalConfirm
        isOpen={isCourseAttachmentModalOpen}
        onClose={closeCourseAttachmentModal}
        closeAriaLabel="Закрыть окно выбора файлов"
        dialogAriaLabel="Выбор файлов курса из базы данных"
        containerClassName="telegram-confirm--mobile-fullscreen"
        dialogClassName="telegram-course-attachment-dialog"
        title="Выбрать файл из БД"
        icon={<AttachmentIcon />}
        showHeaderCloseButton
        actions={(
          <>
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeCourseAttachmentModal} disabled={isCourseAttachmentFilesLoading || isAttachingCourseFiles}>
              Отмена
            </button>
            <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={() => void handleAttachSelectedCourseFiles()} disabled={isCourseAttachmentFilesLoading || isAttachingCourseFiles || selectedCourseAttachmentCount === 0}>
              {isAttachingCourseFiles ? 'Прикрепляем...' : selectedCourseAttachmentCount > 0 ? `Прикрепить (${selectedCourseAttachmentCount})` : 'Прикрепить'}
            </button>
          </>
        )}
      >
        <div className="telegram-confirm__content telegram-course-attachment-dialog__content">
          {isWidgetSection ? (
            widgetAttachmentCourse ? <p>{`Файлы курса «${widgetAttachmentCourse.courseName}».`}</p> : null
          ) : composerCourseProgramId !== null ? <p>{composerCourseDisplayName ? `Файлы курса «${composerCourseDisplayName}».` : 'Файлы выбранного курса.'}</p> : null}

          {isCourseAttachmentFilesLoading ? (
            <p className="telegram-course-selector-dialog__empty">Загружаем файлы курса...</p>
          ) : courseAttachmentFiles.length > 0 ? (
            <div
              className="telegram-course-attachment-list"
              role="group"
              aria-label="Файлы курса из базы данных"
            >
              {courseAttachmentGroups.map((group, groupIndex) => renderCourseAttachmentGroup(group, groupIndex))}
            </div>
          ) : !courseAttachmentError ? (
            <p className="telegram-course-selector-dialog__empty">Для выбранного курса файлы не найдены.</p>
          ) : null}

          {courseAttachmentError ? <p className="telegram-chat-phone-form__error">{courseAttachmentError}</p> : null}
        </div>
      </ModalConfirm>

      <ModalConfirm
        isOpen={isQuickReplyModalOpen}
        onClose={closeQuickReplyModal}
        closeAriaLabel="Закрыть окно быстрых ответов"
        dialogAriaLabel="Список быстрых ответов"
        containerClassName="telegram-confirm--mobile-fullscreen"
        dialogClassName="telegram-quick-reply-dialog"
        title="Быстрые ответы"
        icon={<IconSmsTracking />}
        showHeaderCloseButton
        actions={(
          <>
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeQuickReplyModal} disabled={sendMessagePending}>
              Отмена
            </button>
            <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={handleQuickReplyInsert} disabled={sendMessagePending || !selectedQuickReplyText}>
              Выбрать
            </button>
          </>
        )}
      >
        <div className="telegram-confirm__content telegram-quick-reply-dialog__content">
          <p>Выберите один быстрый ответ. Текст шаблона можно посмотреть по иконке вопроса справа от названия.</p>

          {selectedQuickReply?.id === 'program-details-expanded' && composerCourseProgramId === null ? (
            <p className="telegram-chat-phone-form__error">{isWidgetSection ? 'У связанного чата посетителя не выбраны курс и программа часов.' : 'Для развёрнутого ответа сначала выберите курс и программу часов у клиента.'}</p>
          ) : null}

          {selectedQuickReply?.id === 'program-details-expanded' && isQuickReplyCourseProgramsLoading ? (
            <p className="telegram-course-selector-dialog__empty">Загружаем варианты программы...</p>
          ) : null}

          {selectedQuickReply?.id === 'program-details-expanded' && quickReplyCourseProgramsError ? (
            <p className="telegram-chat-phone-form__error">{quickReplyCourseProgramsError}</p>
          ) : null}

          <fieldset className="telegram-quick-reply-list" aria-label="Варианты быстрых ответов">
            {QUICK_REPLY_DEFINITIONS.map((quickReply) => {
              const quickReplyPreviewText = quickReplyChat ? quickReply.buildMessage(quickReplyChat, quickReplyBuildContext) : null;

              return (
                <label className="telegram-quick-reply-option" key={quickReply.id}>
                  <input
                    type="radio"
                    name="quick-reply"
                    value={quickReply.id}
                    checked={selectedQuickReplyId === quickReply.id}
                    onChange={handleQuickReplySelectionChange}
                    disabled={sendMessagePending}
                  />

                  <span className="telegram-quick-reply-option__card">
                    <span className="telegram-quick-reply-option__indicator" aria-hidden="true" />
                    <span className="telegram-quick-reply-option__header">
                      <span className="telegram-quick-reply-option__title">{quickReply.title}</span>

                      {quickReplyPreviewText ? (
                        <button
                          className="telegram-quick-reply-option__info"
                          type="button"
                          aria-label={`Показать текст ответа ${quickReply.title}`}
                          title="Показать текст ответа"
                        >
                          <IconQuestion width="18px" height="18px" color="currentColor" opacity="1" />
                          <span className="telegram-quick-reply-option__tooltip" role="tooltip">{quickReplyPreviewText}</span>
                        </button>
                      ) : null}
                    </span>
                  </span>
                </label>
              );
            })}
          </fieldset>
        </div>
      </ModalConfirm>

      <ModalConfirm
        isOpen={isReleaseUpdateModalOpen}
        onClose={remindAboutReleaseUpdateLater}
        closeAriaLabel="Напомнить об обновлении позже"
        dialogAriaLabel="Доступно обновление сервиса"
        title="Доступно обновление сервиса"
        icon={<DownloadIcon width="24px" height="24px" color="currentColor" opacity="1" />}
        actions={(
          <>
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={remindAboutReleaseUpdateLater}>
              Позже
            </button>
            <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={applyReleaseUpdateNow}>
              Обновить
            </button>
          </>
        )}
      >
        <div className="telegram-confirm__content telegram-release-update-dialog__content">
          <div className="telegram-release-update-dialog__block telegram-release-update-dialog__block--accent">
            <span className="telegram-release-update-dialog__eyebrow">Новый релиз</span>
            <p className="telegram-release-update-dialog__lead">На сервере уже доступна новая версия.</p>
            <p>Чтобы сервис перешёл на актуальный релиз, обновите страницу.</p>
          </div>

          <div className="telegram-release-update-dialog__block">
            <span className="telegram-release-update-dialog__eyebrow">Если нужно позже</span>
            <p>Нажмите «Позже», если сейчас неудобно обновляться.</p>
            <p>Напоминание повторится через 5 минут и будет появляться снова, пока страница не обновится.</p>
          </div>
        </div>
      </ModalConfirm>

      <ModalConfirm
        isOpen={isReminderModalOpen}
        onClose={closeReminderModal}
        closeAriaLabel="Закрыть окно уведомления"
        dialogAriaLabel={reminderModalTitle}
        containerClassName="telegram-confirm--mobile-fullscreen"
        dialogClassName="telegram-reminder-dialog"
        title={reminderModalTitle}
        icon={<BellIcon />}
        showHeaderCloseButton
        actions={(
          <>
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeReminderModal} disabled={createReminderPending}>
              Отмена
            </button>
            <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={() => void handleCreateReminder()} disabled={createReminderPending}>
              {reminderModalSubmitLabel}
            </button>
          </>
        )}
      >
        <div className="telegram-confirm__content telegram-reminder-dialog__content">
            <p>{reminderModalDescription}</p>

            <label className="telegram-reminder-form__label telegram-reminder-form__label--datetime">
              <span>Дата и время</span>

              <div className="telegram-reminder-form__datetime">
                <input
                  className="telegram-reminder-form__datetime-input"
                  ref={reminderDateInputRef}
                  type="datetime-local"
                  value={reminderFormState.remindAt}
                  min={reminderMinDateTimeValue}
                  onChange={handleReminderFieldChange('remindAt')}
                  disabled={createReminderPending}
                />

                <button
                  type="button"
                  className="telegram-reminder-form__picker-trigger"
                  onClick={handleReminderDatePickerClick}
                  disabled={createReminderPending}
                  aria-label="Выбрать дату и время"
                >
                  <IconCalendar width="20px" height="20px" color="currentColor" opacity="1" />
                </button>
              </div>
            </label>

            <label className="telegram-reminder-form__label telegram-reminder-form__label--comment">
              <span>Комментарий</span>
              <textarea
                value={reminderFormState.comment}
                onChange={handleReminderFieldChange('comment')}
                placeholder="Например: вернуться к клиенту после созвона и уточнить оплату"
                rows={4}
                disabled={createReminderPending}
              />
            </label>
        </div>
      </ModalConfirm>

      <ModalConfirm
        isOpen={isLogoutConfirmOpen}
        onClose={closeLogoutConfirm}
        closeAriaLabel="Закрыть окно подтверждения"
        dialogAriaLabel="Подтверждение выхода"
        title="Подтвердите выход"
        icon={<SparkIcon />}
        actions={(
          <>
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeLogoutConfirm} disabled={logoutPending}>
              Отмена
            </button>
            <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={handleLogout} disabled={logoutPending}>
              {logoutPending ? 'Выходим...' : 'Выход'}
            </button>
          </>
        )}
      >
        <div className="telegram-confirm__content">
            <p>Вы действительно хотите выйти из сервиса? Текущая сессия на этом устройстве будет завершена.</p>
        </div>
      </ModalConfirm>

      <ModalConfirm
        isOpen={isDeleteConfirmOpen}
        onClose={closeDeleteConfirm}
        closeAriaLabel="Закрыть окно подтверждения удаления"
        dialogAriaLabel="Подтверждение удаления сообщения"
        dialogClassName="telegram-confirm__dialog--delete"
        title="Удалить сообщение?"
        icon={<IconDelete width="26px" height="26px" color="currentColor" opacity="1" />}
        iconClassName="telegram-confirm__icon--delete"
        actions={(
          <>
            <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={closeDeleteConfirm} disabled={deletingMessageId !== null}>
              Отмена
            </button>
            <button className="telegram-confirm__button telegram-confirm__button--delete" type="button" onClick={() => void confirmDeleteMessage()} disabled={deletingMessageId !== null}>
              {deletingMessageId !== null ? 'Удаляем...' : 'Удалить'}
            </button>
          </>
        )}
      >
        <div className="telegram-confirm__content">
            <p>{deleteConfirmMessage?.platform === 'widget'
              ? 'Сообщение будет удалено из переписки оператора и истории виджета посетителя.'
              : `Сообщение будет удалено в сервисе и в ${deleteConfirmMessage?.platform === 'max' ? 'MAX' : 'Telegram'}, если платформа разрешит удаление.`}</p>
            {deleteConfirmMessage ? <p className="telegram-confirm__excerpt">{deleteConfirmPreviewText}</p> : null}
        </div>
      </ModalConfirm>

      <ModalPreview
        isOpen={isChatHistoryModalOpen}
        onClose={closeChatHistoryModal}
        closeAriaLabel="Закрыть окно истории чата"
        dialogAriaLabel={activeChat ? `История чата ${activeChat.title}` : 'История чата'}
        containerClassName="telegram-confirm--fullscreen"
        dialogClassName="telegram-chat-history-dialog"
      >
        <div className="telegram-chat-history-dialog__header">
          <div className="telegram-chat-history-dialog__meta">
            <strong>История чата</strong>
            <span title={activeChat?.title ?? 'Выбранный чат'}>{activeChat?.title ?? 'Выбранный чат'}</span>
          </div>

          <button
            type="button"
            className="telegram-file-preview-dialog__header-button telegram-chat-history-dialog__header-button"
            onClick={closeChatHistoryModal}
            aria-label="Закрыть окно истории чата"
            title="Закрыть"
          >
            <CloseIcon width="18px" height="18px" color="currentColor" opacity="1" />
          </button>
        </div>

        <div className="telegram-chat-history-dialog__body">
          {isChatHistoryLoading ? (
            <div className="telegram-chat-history-dialog__placeholder">
              <span className="telegram-chat-list__pulse" aria-hidden="true" />
              <strong>Загружаем историю чата</strong>
              <p>Логи текущего чата появятся здесь сразу после загрузки.</p>
            </div>
          ) : null}

          {!isChatHistoryLoading && activeChatHistoryEntries.length === 0 ? (
            <div className="telegram-chat-history-dialog__placeholder">
              <strong>История пока пуста</strong>
              <p>Для этого чата ещё нет записей журнала действий.</p>
            </div>
          ) : null}

          {!isChatHistoryLoading && activeChatHistoryEntries.length > 0 ? (
            <div className="telegram-chat-history-dialog__list">
              {activeChatHistoryEntries.map((entry) => (
                <article key={entry.logId} className="telegram-chat-history-dialog__item">
                  <div className="telegram-chat-history-dialog__item-header">
                    <strong>{entry.title}</strong>
                    <time dateTime={entry.occurredAt}>{formatChatHistoryTimestamp(entry.occurredAt)}</time>
                  </div>
                  <p>{entry.description}</p>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </ModalPreview>

      <ModalPreview
        isOpen={isFilePreviewOpen}
        onClose={closeFilePreview}
        closeAriaLabel="Закрыть окно предпросмотра файла"
        dialogAriaLabel={activeFilePreview ? `Предпросмотр файла ${getAttachmentFileName(activeFilePreview.file)}` : 'Предпросмотр файла'}
        dialogClassName="telegram-file-preview-dialog"
      >
        {activeFilePreview ? (
          <>
            <div className="telegram-file-preview-dialog__header">
                <div className="telegram-file-preview-dialog__meta">
                  <strong title={getAttachmentFileName(activeFilePreview.file)}>{getAttachmentFileName(activeFilePreview.file)}</strong>
                  {buildAttachmentMetaLabel(activeFilePreview.file) ? <span>{buildAttachmentMetaLabel(activeFilePreview.file)}</span> : null}
                </div>

                <div className="telegram-file-preview-dialog__header-actions">
                  <button
                    type="button"
                    className="telegram-file-preview-dialog__header-button"
                    onClick={() => void handleAttachmentDownload(activeFilePreview.file)}
                    aria-label="Скачать файл"
                    title="Скачать файл"
                  >
                    <DownloadIcon width="18px" height="18px" color="currentColor" opacity="1" />
                  </button>
                  <button
                    type="button"
                    className="telegram-file-preview-dialog__header-button"
                    onClick={closeFilePreview}
                    aria-label="Закрыть окно предпросмотра"
                    title="Закрыть"
                  >
                    <CloseIcon width="18px" height="18px" color="currentColor" opacity="1" />
                  </button>
                </div>
              </div>

            <div className={`telegram-file-preview-dialog__body${getFilePreviewMode(activeFilePreview.file) === 'document' ? ' telegram-file-preview-dialog__body--document' : ''}${activeFilePreview.isLoading || activeFilePreview.errorMessage ? ' telegram-file-preview-dialog__body--status' : ''}`}>
              {renderFilePreviewContent(activeFilePreview)}
            </div>
          </>
        ) : null}
      </ModalPreview>

      <ModalPreview
        isOpen={isAvatarPreviewOpen}
        onClose={closeAvatarPreview}
        closeAriaLabel="Закрыть окно фото клиента"
        dialogAriaLabel={activeAvatarPreview ? `Фото клиента ${activeAvatarPreview.title}` : 'Фото клиента'}
        dialogClassName="telegram-file-preview-dialog telegram-avatar-preview-dialog"
      >
        {activeAvatarPreview ? (
          <>
            <div className="telegram-file-preview-dialog__header">
              <div className="telegram-file-preview-dialog__meta">
                <strong title={activeAvatarPreview.title}>Фото клиента</strong>
                <span title={activeAvatarPreview.title}>{activeAvatarPreview.title}</span>
              </div>

              <div className="telegram-file-preview-dialog__header-actions">
                <button
                  type="button"
                  className="telegram-file-preview-dialog__header-button"
                  onClick={closeAvatarPreview}
                  aria-label="Закрыть окно фото клиента"
                  title="Закрыть"
                >
                  <CloseIcon width="18px" height="18px" color="currentColor" opacity="1" />
                </button>
              </div>
            </div>

            <div className="telegram-file-preview-dialog__body telegram-avatar-preview-dialog__body">
              <img
                className="telegram-file-preview-dialog__image telegram-avatar-preview-dialog__image"
                src={activeAvatarPreview.imageUrl}
                alt={activeAvatarPreview.title}
              />
            </div>
          </>
        ) : null}
      </ModalPreview>

      {notificationStack}
    </div>
  );
};

export default MainContainer;